import {
  parseReplay, parseBeatmap, loadSkin, loadSkinFromDir, extractBeatmapBackground,
  createReplaySession, buildSkin, type BeatmapAssets, type CoreSession,
  applyStacking, computeModDifficulty, Mod, md5,
  Player, AudioSync, TimeMapper, Renderer,
  type BeatmapData, type HitResult, type ReplayData, type SkinAssets, type VideoFrameSource,
} from './lib/index.js';
import { wirePPCounter, setPPCounterEnabled, isPPCounterEnabled } from './app/ppOverlay.js';
import { replayLabelSuffix } from './app/replayLabel.js';
import type { ExportFormat, ExportCallbacks, DiskWritable, MatchExportInputs, MatchExportCell } from './export/Exporter.js';
import {
  loadConfig as loadOAuthConfig,
  startLogin,
  handlePendingCallback,
  isLoggedIn,
  logout,
  getAccessToken,
  fetchMe,
  cachedUser,
} from './auth/osuAuth.js';
import { parseScoreRef, parseBeatmapRef, downloadReplay, lookupBeatmapByHash, downloadBeatmapOsu, fetchUserProfile, AuthError, OsuUnavailableError, type OsuUserProfile, type ScoreRuleset, type BeatmapRef } from './api/osuApi.js';
import {
  generateStdAutoReplay, generateTaikoAutoReplay, generateManiaAutoReplay,
  generateCatchAutoReplay, convertBeatmapToCatch, applyPositionOffsets, synthesizeAutoReplay,
} from './lib/index.js';
import { downloadBeatmapSet, lookupBeatmapsetIdByHash } from './api/mirrors.js';
import { fetchRoom, parseRoomRef, type MatchData, type MatchPlaylistItem, type MatchPlayer, type MatchScore, type TeamColor } from './api/mpRoom.js';

const osrInput      = document.getElementById('osr-input')       as HTMLInputElement;
const oszInput      = document.getElementById('osz-input')       as HTMLInputElement;
const oskInput      = document.getElementById('osk-input')       as HTMLInputElement;
const loadBtn       = document.getElementById('load-btn')        as HTMLButtonElement;
const statusEl      = document.getElementById('status')          as HTMLElement;
const canvas        = document.getElementById('replay-canvas')   as HTMLCanvasElement;
const canvas2       = document.getElementById('replay-canvas-2') as HTMLCanvasElement;
const canvasStack   = document.getElementById('canvas-stack')    as HTMLElement;
const partnerFrame  = document.getElementById('canvas-frame-2')  as HTMLElement;
const playerLabel1  = document.getElementById('player-label-1')  as HTMLElement;
const playerLabel2  = document.getElementById('player-label-2')  as HTMLElement;
const failOverlay   = document.getElementById('fail-overlay-2')  as HTMLElement;

// 8 canvas slots for 2v2/4v4; slot 0 is the primary `canvas` and also hosts the overview panel.
const NUM_SLOTS = 8;
const slotFrames:        HTMLElement[]       = [];
const slotCanvases:      HTMLCanvasElement[] = [];
const slotPlayerLabels:  HTMLElement[]       = [];
const slotFailOverlays:  HTMLElement[]       = [];
for (let i = 0; i < NUM_SLOTS; i++) {
  slotFrames.push(       document.getElementById(`canvas-frame-${i + 1}`)     as HTMLElement);
  slotCanvases.push(     document.getElementById(i === 0 ? 'replay-canvas' : `replay-canvas-${i + 1}`) as HTMLCanvasElement);
  slotPlayerLabels.push( document.getElementById(`player-label-${i + 1}`)     as HTMLElement);
  slotFailOverlays.push( document.getElementById(`fail-overlay-${i + 1}`)     as HTMLElement);
}
const controls      = document.getElementById('player-controls') as HTMLElement;
const shareAtTimeBtn = document.getElementById('share-at-time-btn') as HTMLButtonElement;
const fullscreenBtn = document.getElementById('fullscreen-btn')   as HTMLButtonElement;
const playPauseBtn  = document.getElementById('play-pause-btn')  as HTMLButtonElement;
const scrubBar      = document.getElementById('scrub-bar')       as HTMLInputElement;
const scrubBarWrap  = document.getElementById('scrub-bar-wrap')  as HTMLElement;
const scrubMarkers  = document.getElementById('scrub-markers')   as HTMLElement;
const clipRange      = document.getElementById('clip-range')        as HTMLElement;
const clipDimLeft    = document.getElementById('clip-dim-left')     as HTMLElement;
const clipDimRight   = document.getElementById('clip-dim-right')    as HTMLElement;
const clipHandleStart = document.getElementById('clip-handle-start') as HTMLElement;
const clipHandleEnd   = document.getElementById('clip-handle-end')   as HTMLElement;
const clipStartLabel = clipHandleStart.querySelector('.clip-handle-label') as HTMLElement;
const clipEndLabel   = clipHandleEnd.querySelector('.clip-handle-label')   as HTMLElement;
const timeDisplay   = document.getElementById('time-display')    as HTMLElement;
const renderOptions    = document.getElementById('render-options')     as HTMLElement;
const optJudgement     = document.getElementById('opt-judgement')      as HTMLInputElement;
const optFlyingHits    = document.getElementById('opt-flying-hits')    as HTMLInputElement;
const optFollowpoints  = document.getElementById('opt-followpoints')   as HTMLInputElement;
const optKeyOverlay    = document.getElementById('opt-key-overlay')    as HTMLInputElement;
const optURBar         = document.getElementById('opt-ur-bar')         as HTMLInputElement;
const optModIcons      = document.getElementById('opt-mod-icons')      as HTMLInputElement;
const optPP            = document.getElementById('opt-pp')             as HTMLInputElement;
const optModHidden     = document.getElementById('opt-mod-hidden')     as HTMLInputElement;
const optModFlashlight = document.getElementById('opt-mod-flashlight') as HTMLInputElement;
const optModFadeIn     = document.getElementById('opt-mod-fadein')     as HTMLInputElement;
const optModCover      = document.getElementById('opt-mod-cover')      as HTMLInputElement;
const optUpscroll      = document.getElementById('opt-upscroll')       as HTMLInputElement;
const optStoryboard    = document.getElementById('opt-storyboard')     as HTMLInputElement;
const optVideo         = document.getElementById('opt-video')          as HTMLInputElement;
const videoDlBtns      = Array.from(document.querySelectorAll<HTMLButtonElement>('.video-dl-btn'));
const optBeatmapHitsounds = document.getElementById('opt-beatmap-hitsounds') as HTMLInputElement;
const settingsMenu     = document.getElementById('settings-menu')      as HTMLElement;
const settingsBtn      = document.getElementById('settings-btn')       as HTMLButtonElement;
const settingsDropdown = document.getElementById('settings-dropdown')  as HTMLElement;
const optMusicVol      = document.getElementById('opt-music-vol')      as HTMLInputElement;
const optMusicVolValue = document.getElementById('opt-music-vol-value') as HTMLElement;
const optEffectsVol    = document.getElementById('opt-effects-vol')    as HTMLInputElement;
const optEffectsVolValue = document.getElementById('opt-effects-vol-value') as HTMLElement;
const optBgDim         = document.getElementById('opt-bg-dim')         as HTMLInputElement;
const optBgDimValue    = document.getElementById('opt-bg-dim-value')   as HTMLElement;
const optManiaScroll       = document.getElementById('opt-mania-scroll-speed')       as HTMLInputElement;
const optManiaScrollValue  = document.getElementById('opt-mania-scroll-speed-value') as HTMLElement;
const optSyncBtn       = document.getElementById('opt-sync-btn')       as HTMLButtonElement;
const optRate          = document.getElementById('opt-rate')           as HTMLInputElement;
const optRateValue     = document.getElementById('opt-rate-value')     as HTMLElement;
const optRateReset     = document.getElementById('opt-rate-reset')     as HTMLButtonElement;
const optOffsetDec     = document.getElementById('opt-offset-dec')     as HTMLButtonElement;
const optOffsetInc     = document.getElementById('opt-offset-inc')     as HTMLButtonElement;
const optOffsetReset   = document.getElementById('opt-offset-reset')   as HTMLButtonElement;
const optOffsetValue   = document.getElementById('opt-offset-value')   as HTMLElement;

const exportBtn        = document.getElementById('export-btn')         as HTMLButtonElement;
const exportDialog     = document.getElementById('export-dialog')      as HTMLDialogElement;
const exportClose      = document.getElementById('export-close')       as HTMLButtonElement;
const exportConfig     = document.getElementById('export-config')      as HTMLElement;
const exportPreset     = document.getElementById('export-preset')      as HTMLSelectElement;
const exportDetails    = document.getElementById('export-details')     as HTMLElement;
const exportCodecNotice = document.getElementById('export-codec-notice') as HTMLElement;
const exportFirefoxNote = document.getElementById('export-firefox-note') as HTMLElement;
const exportProgressWrap = document.getElementById('export-progress-wrap') as HTMLElement;
const exportProgressFill = document.getElementById('export-progress-fill') as HTMLElement;
const exportProgressPct  = document.getElementById('export-progress-pct')  as HTMLElement;
const exportProgressEta  = document.getElementById('export-progress-eta')  as HTMLElement;
const exportMessage    = document.getElementById('export-message')     as HTMLElement;
const exportStart      = document.getElementById('export-start')       as HTMLButtonElement;
const exportCancel     = document.getElementById('export-cancel')      as HTMLButtonElement;

const audioSwap        = document.getElementById('audio-swap')         as HTMLElement;
const audioSwapLeft    = document.getElementById('audio-swap-left')    as HTMLButtonElement;
const audioSwapRight   = document.getElementById('audio-swap-right')   as HTMLButtonElement;
const audioSwapBtn     = document.getElementById('audio-swap-btn')     as HTMLButtonElement;
const audioSwapLeftName  = document.getElementById('audio-swap-left-name')  as HTMLElement;
const audioSwapRightName = document.getElementById('audio-swap-right-name') as HTMLElement;

const loginBtn     = document.getElementById('login-btn')    as HTMLButtonElement;
const logoutBtn    = document.getElementById('logout-btn')   as HTMLButtonElement;
const authStateEl  = document.getElementById('auth-state')   as HTMLElement;
const scoreInput   = document.getElementById('score-input')  as HTMLInputElement;
const fetchBtn     = document.getElementById('fetch-btn')    as HTMLButtonElement;
const fetchStatusEl = document.getElementById('fetch-status') as HTMLElement;
const fetchOskInput = document.getElementById('fetch-osk-input') as HTMLInputElement;
const fetchDefaultSkinSelect  = document.getElementById('fetch-default-skin')  as HTMLSelectElement;
const uploadDefaultSkinSelect = document.getElementById('upload-default-skin') as HTMLSelectElement;

const autoInput            = document.getElementById('auto-input')        as HTMLInputElement;
const autoBtn              = document.getElementById('auto-btn')          as HTMLButtonElement;
const autoStatusEl         = document.getElementById('auto-status')       as HTMLElement;
const autoOskInput         = document.getElementById('auto-osk-input')    as HTMLInputElement;
const autoDefaultSkinSelect = document.getElementById('auto-default-skin') as HTMLSelectElement;
const autoModsBtn          = document.getElementById('auto-mods-btn')     as HTMLButtonElement;
const autoModsMenu         = document.getElementById('auto-mods-menu')    as HTMLElement;
const autoModsLabel        = document.getElementById('auto-mods-label')   as HTMLElement;

const replayTabsEl = document.getElementById('replay-tabs') as HTMLElement;

const roomInput              = document.getElementById('room-input')            as HTMLInputElement;
const matchFetchBtn          = document.getElementById('match-fetch-btn')       as HTMLButtonElement;
const matchStatusEl          = document.getElementById('match-status')          as HTMLElement;
const matchDefaultSkinSelect = document.getElementById('match-default-skin')    as HTMLSelectElement;
const matchOskInput          = document.getElementById('match-osk-input')       as HTMLInputElement;

const authStateMatchEl = document.getElementById('auth-state-match') as HTMLElement;
const loginBtnMatch    = document.getElementById('login-btn-match')  as HTMLButtonElement;
const logoutBtnMatch   = document.getElementById('logout-btn-match') as HTMLButtonElement;

const matchOverview        = document.getElementById('match-overview')         as HTMLElement;
const matchOverviewTitle   = document.getElementById('match-overview-title')   as HTMLElement;
const matchOverviewMeta    = document.getElementById('match-overview-meta')    as HTMLElement;
const matchOverviewPlayers = document.getElementById('match-overview-players') as HTMLElement;
const matchOverviewMaps    = document.getElementById('match-overview-maps')    as HTMLElement;

const matchStrip        = document.getElementById('match-strip')         as HTMLElement;
const matchStripRoom    = document.getElementById('match-strip-room')    as HTMLElement;
const matchStripCurrent = document.getElementById('match-strip-current') as HTMLElement;
const matchStripBack    = document.getElementById('match-strip-back')    as HTMLButtonElement;

const matchScoreStrip = document.getElementById('match-score-strip') as HTMLElement;
const matchScoreLeft  = document.getElementById('match-score-left')  as HTMLElement;
const matchScoreRight = document.getElementById('match-score-right') as HTMLElement;
const matchScoreDiffLeft  = document.getElementById('match-score-diff-left')  as HTMLElement;
const matchScoreDiffRight = document.getElementById('match-score-diff-right') as HTMLElement;

const progressEls = Array.from(
  document.querySelectorAll<HTMLElement>('.load-progress'),
);

const FALLBACK_SKIN = 'Default';
// YUGEN is the only bundled default with taiko sprites; layered in for taiko replays missing them.
const TAIKO_FALLBACK_SKIN = 'YUGEN';
// Same pattern for mania: swap to YUGEN if the selected skin lacks mania assets.
const MANIA_FALLBACK_SKIN = 'YUGEN';
// Same pattern for catch: swap to YUGEN if the selected skin lacks catch assets.
const CATCH_FALLBACK_SKIN = 'YUGEN';
// Same pattern for osu!std: swap to YUGEN if the selected skin lacks osu assets
// (e.g. the taiko-exclusive UNTITLED skin loaded against a standard replay).
const OSU_FALLBACK_SKIN = 'YUGEN';

function setStatus(msg: string, isError = false): void {
  const color = isError ? '#ff6b6b' : '#e0e0e0';
  statusEl.textContent      = msg;
  statusEl.style.color      = color;
  fetchStatusEl.textContent = msg;
  fetchStatusEl.style.color = color;
  autoStatusEl.textContent  = msg;
  autoStatusEl.style.color  = color;
}

// Belt-and-suspenders: Android "Request Desktop Site" flips the hover/pointer media query.
function isMatchUnsupported(): boolean {
  return window.matchMedia('(hover: none) and (pointer: coarse)').matches
    || (navigator.maxTouchPoints > 0 && window.innerWidth < 1280);
}

function setLoadProgress(active: boolean): void {
  for (const el of progressEls) el.classList.toggle('active', active);
}

function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Reusable parsed/decoded assets, shared by match partners and same-replay rebuilds
 * (skin swaps). The asset fields are the engine's BeatmapAssets (passed back to
 * createReplaySession as its `beatmapSet` input on rebuilds). Refcounted: every session
 * holding them bumps `refs` at build and drops it at destroy; the last reference closes
 * the background bitmap.
 */
interface SharedSessionAssets extends BeatmapAssets {
  refs: number;
}

interface ReplaySession {
  readonly kind: 'replay';
  readonly id: number;
  readonly dedupKey: string;
  readonly skinName: string;
  /** 0 = osu!std, 1 = taiko, 2 = catch, 3 = mania. Drives gamemode-specific UI toggles. */
  readonly mode: 0 | 1 | 2 | 3;
  readonly label: string;
  readonly username: string;
  readonly background: ImageBitmap | null;
  /** The engine session (owns Player/Renderer/AudioSync teardown via destroy()). */
  readonly core: CoreSession;
  readonly player: Player;
  readonly renderer: Renderer;
  readonly audioSync: AudioSync;
  readonly timeMapper: TimeMapper;
  readonly introOffsetMs: number;
  readonly speed: number;
  readonly scoreId: string | null;
  /** Mode prefix for legacy stable scores; namespaces the cache key so lazer/stable don't collide. */
  readonly scoreRuleset: ScoreRuleset | null;
  readonly beatmapsetId: number | null;
  readonly canvas: HTMLCanvasElement;
  readonly shared: SharedSessionAssets;
  /** False for match-pair primaries; parent MatchSession keeps the raw caches alive across overview swaps. */
  readonly ownsRawCache: boolean;
  /** True for the "osu!" Auto-generated session — drives the AUTO badge in the tab. */
  readonly isAuto: boolean;
  /** Auto-only share payload: the beatmapId + chosen mod acronyms, used to rebuild the #auto= link
   *  (replayData.mods can't reconstruct Cover, which has no stable bit). Null for non-Auto sessions. */
  readonly auto: { beatmapId: number; mods: string[] } | null;
  musicVol: number;
  effectsVol: number;
  /** "Beatmap Hitsounds" toggle: true ⇒ beatmap samples override skin; false ⇒ skin only. */
  beatmapHitsounds: boolean;
  /** Manual visual-only offset in presentation ms. */
  audioOffsetMs: number;
  wasPlaying: boolean;
}

/** Cached primary+partner pair; skin frozen at build time. */
interface BuiltPair {
  readonly kind: 'pair';
  primary: ReplaySession;
  /** Null iff partnerStubUsername is set (player had no replay). */
  partner: ReplaySession | null;
  partnerStubUsername: string | null;
  /** Persists across tab swaps so re-activation restores the last-chosen audio side. */
  audioSide: 'primary' | 'partner';
}

/** Slots are red-first then blue, in deterministic order across the match. */
interface TeamSlot {
  readonly team: TeamColor;
  readonly canvasIndex: number;
  /** 1-5 in the CSS grid (middle col is the team gap). */
  readonly gridColumn: number;
  readonly gridRow: number;
  readonly username: string;
  /** Mutually exclusive with stubUsername. */
  session: ReplaySession | null;
  stubUsername: string | null;
}

/** 2v2/4v4 analogue of BuiltPair. primarySlotIdx owns shared assets; audioSlotIdx is the audible slot. */
interface BuiltTeamMatch {
  readonly kind: 'team';
  /** Length 4 (2v2) or 8 (4v4); real player iff session !== null. */
  readonly slots: TeamSlot[];
  readonly primarySlotIdx: number;
  audioSlotIdx: number;
}

type BuiltMatchEntry = BuiltPair | BuiltTeamMatch;

interface MatchSession {
  readonly kind: 'match';
  readonly id: number;
  readonly dedupKey: string;
  readonly match: MatchData;
  readonly cover: ImageBitmap | null;
  label: string;
  /** Pairs/team matches keyed by playlistItemId, owned and destroyed by this MatchSession. */
  readonly builtPairs: Map<number, BuiltMatchEntry>;
  /** Missing entries fall back to MatchPlayer's bare username. */
  readonly profiles: Map<number, OsuUserProfile>;
}

type Session = ReplaySession | MatchSession;

const sessions: Session[] = [];
let activeSession: Session | null = null;
/** Transient second session on canvas2; reuses primary's assets and clock-locked to its AudioSync. */
let partnerSession: ReplaySession | null = null;
/** Static-paint canvas2 state when a match partner has no replay; mutually exclusive with partnerSession. */
let partnerStub: { username: string } | null = null;
let activeMatchPair: {
  matchSession: MatchSession;
  item: MatchPlaylistItem;
  primary: ReplaySession;
} | null = null;
let activeTeamMatch: BuiltTeamMatch | null = null;
let nextSessionId = 1;

let activePlayer:    Player    | null = null;
let activeRenderer:  Renderer  | null = null;
let activeAudioSync: AudioSync | null = null;

// User playback-rate slider (presentation control, not a mod). Not persisted — matches
// volumes/dim. Sessions built while the slider is off-1 pick this up at the AudioSync
// build site; exports ignore it (always render at 1.0×).
let playbackRate = 1;

// AudioContext must be created inside a user gesture; create lazily.
let sharedAudioCtx: AudioContext | null = null;

function ensureAudioCtx(): AudioContext {
  if (sharedAudioCtx === null) sharedAudioCtx = new AudioContext();
  return sharedAudioCtx;
}

let scrubbing = false;
// Export clip selection, in presentation ms (the same domain as the scrub bar). Reset to the
// full track [0, duration] on every session activation; the export dialog reads these.
let clipStartPresMs = 0;
let clipEndPresMs   = 0;
// True while an MP4 export is actively running. Locks out playback (the live renderer's rAF
// loop is stopped during export to free the main thread — see runExport).
let exporting = false;

// Session-scoped caches; hashToSetId persists in localStorage to skip /beatmaps/lookup.

const skinLibrary = new Map<string, SkinAssets>();

async function getSkin(name: string, audioCtx: AudioContext): Promise<SkinAssets> {
  const cached = skinLibrary.get(name);
  if (cached !== undefined) return cached;
  const skin = await loadSkinFromDir(`skins/${name}`, audioCtx);
  skinLibrary.set(name, skin);
  return skin;
}

function resolveUploadedSkinName(skin: SkinAssets, file: File): string {
  const fromIni = skin.config.name.trim();
  const fromFilename = file.name.replace(/\.osk$/i, '').trim();
  const desired = fromIni !== '' ? fromIni : (fromFilename !== '' ? fromFilename : 'Uploaded skin');
  if (!skinLibrary.has(desired)) return desired;
  let n = 2;
  while (skinLibrary.has(`${desired} (${n})`)) n++;
  return `${desired} (${n})`;
}

function registerUploadedSkin(name: string, skin: SkinAssets): void {
  skinLibrary.set(name, skin);
  for (const select of [fetchDefaultSkinSelect, uploadDefaultSkinSelect, matchDefaultSkinSelect, autoDefaultSkinSelect]) {
    const opt = document.createElement('option');
    opt.value = name;
    opt.textContent = name;
    select.appendChild(opt);
    refreshSkinDropdown(select);
  }
}

// Native <select> stays canonical (events, value, options); custom widget renders gamemode icons.

/** Drives per-option icons + the taiko/mania fallback skin swap. Sync with public/skins/<name>/. */
type Gamemode = 'osu' | 'taiko' | 'mania' | 'catch';
const DEFAULT_SKIN_GAMEMODES: Record<string, ReadonlyArray<Gamemode>> = {
  YUGEN:    ['osu', 'taiko', 'mania', 'catch'],
  Rafis:    ['osu'],
  bog:      ['osu'],
  Kamui:    ['osu'],
  UNTITLED: ['taiko'],
  shinchikuskin: ['taiko'],
  '4sbet1': ['taiko'],
  'R Skin V2.0': ['mania'],
  bojii:    ['mania'],
  'myuka arrows': ['mania'],
  Default:  ['osu', 'taiko', 'mania', 'catch'],
  'Skin for CTB': ['catch'],
};

const OSU_ICON_SVG =
  '<svg class="skin-dropdown-icon" viewBox="0 0 24 24" aria-hidden="true">'
  + '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/>'
  + '<circle cx="12" cy="12" r="3" fill="currentColor"/>'
  + '</svg>';

const TAIKO_ICON_SVG =
  '<svg class="skin-dropdown-icon" viewBox="0 0 512 512" aria-hidden="true">'
  + '<path fill="currentColor" d="M501.2 76.1c11.1-7.3 14.2-22.1 6.9-33.2s-22.1-14.2-33.2-6.9L370.2 104.5C335.8 98.7 297 96 256 96C114.6 96 0 128 0 208L0 368c0 31.3 27.4 58.8 72 78.7L72 344c0-13.3 10.7-24 24-24s24 10.7 24 24l0 119.4c33 8.9 71.1 14.5 112 16.1L232 376c0-13.3 10.7-24 24-24s24 10.7 24 24l0 103.5c40.9-1.6 79-7.2 112-16.1L392 344c0-13.3 10.7-24 24-24s24 10.7 24 24l0 102.7c44.6-19.9 72-47.4 72-78.7l0-160c0-41.1-30.2-69.5-78.8-87.4l67.9-44.5zM307.4 145.6l-64.6 42.3c-11.1 7.3-14.2 22.1-6.9 33.2s22.1 14.2 33.2 6.9l111.1-72.8c14.7 3.2 27.9 7 39.4 11.5C458.4 181.8 464 197.4 464 208c0 .8-2.7 17.2-46 35.9C379.1 260.7 322 272 256 272s-123.1-11.3-162-28.1C50.7 225.2 48 208.8 48 208c0-10.6 5.6-26.2 44.4-41.3C130.6 151.9 187.8 144 256 144c18 0 35.1 .5 51.4 1.6z"/>'
  + '</svg>';

// Mania icon — MDI "piano" glyph (Material Design Icons, Pictogrammers).
const MANIA_ICON_SVG =
  '<svg class="skin-dropdown-icon" viewBox="0 0 24 24" aria-hidden="true">'
  + '<path fill="currentColor" d="M20 2H4C2.9 2 2 2.9 2 4V20C2 21.11 2.9 22 4 22H20C21.11 22 22 21.11 22 20V4C22 2.9 21.11 2 20 2M14.74 14H15V20H9V14H9.31C9.86 14 10.3 13.56 10.3 13V4H13.75V13C13.75 13.56 14.19 14 14.74 14M4 4H6.8V13C6.8 13.56 7.24 14 7.79 14H8V20H4V4M20 20H16V14H16.26C16.81 14 17.25 13.56 17.25 13V4H20V20Z"/>'
  + '</svg>';

// Catch icon — Font Awesome "apple-whole" (solid) glyph.
const CATCH_ICON_SVG =
  '<svg class="skin-dropdown-icon" viewBox="0 0 448 512" aria-hidden="true">'
  + '<path fill="currentColor" d="M224 112c-8.8 0-16-7.2-16-16l0-16c0-44.2 35.8-80 80-80l16 0c8.8 0 16 7.2 16 16l0 16c0 44.2-35.8 80-80 80l-16 0zM0 288c0-76.3 35.7-160 112-160 27.3 0 59.7 10.3 82.7 19.3 18.8 7.3 39.9 7.3 58.7 0 22.9-8.9 55.4-19.3 82.7-19.3 76.3 0 112 83.7 112 160 0 128-80 224-160 224-16.5 0-38.1-6.6-51.5-11.3-8.1-2.8-16.9-2.8-25 0-13.4 4.7-35 11.3-51.5 11.3-80 0-160-96-160-224z"/>'
  + '</svg>';

function iconsHtmlForOption(opt: HTMLOptionElement): string {
  const modes = DEFAULT_SKIN_GAMEMODES[opt.value];
  if (modes === undefined) return ''; // uploaded skin — no icons
  const parts: string[] = [];
  for (const mode of modes) {
    if (mode === 'osu') parts.push(OSU_ICON_SVG);
    else if (mode === 'taiko') parts.push(TAIKO_ICON_SVG);
    else if (mode === 'mania') parts.push(MANIA_ICON_SVG);
    else if (mode === 'catch') parts.push(CATCH_ICON_SVG);
  }
  return parts.join('');
}

interface SkinDropdownState {
  trigger: HTMLButtonElement;
  menu: HTMLUListElement;
}
const skinDropdowns = new WeakMap<HTMLSelectElement, SkinDropdownState>();
let skinDropdownOpen: HTMLSelectElement | null = null;

function closeAllSkinDropdowns(): void {
  if (skinDropdownOpen === null) return;
  const state = skinDropdowns.get(skinDropdownOpen);
  if (state !== undefined) {
    state.menu.classList.add('hidden');
    state.trigger.setAttribute('aria-expanded', 'false');
  }
  skinDropdownOpen = null;
}

function refreshSkinDropdown(select: HTMLSelectElement): void {
  const state = skinDropdowns.get(select);
  if (state === undefined) return;
  const { trigger, menu } = state;

  const current = select.options[select.selectedIndex];
  const label  = trigger.querySelector('.skin-dropdown-label') as HTMLElement;
  const icons  = trigger.querySelector('.skin-dropdown-icons') as HTMLElement;
  label.textContent = current?.textContent ?? '';
  icons.innerHTML   = current !== undefined ? iconsHtmlForOption(current) : '';

  menu.replaceChildren();
  for (const opt of Array.from(select.options)) {
    const li = document.createElement('li');
    li.className = 'skin-dropdown-option';
    li.dataset.value = opt.value;
    if (opt.value === select.value) li.setAttribute('aria-selected', 'true');
    const name = document.createElement('span');
    name.className = 'skin-dropdown-name';
    name.textContent = opt.textContent ?? opt.value;
    const optIcons = document.createElement('span');
    optIcons.className = 'skin-dropdown-icons';
    optIcons.innerHTML = iconsHtmlForOption(opt);
    li.append(name, optIcons);
    li.addEventListener('click', () => {
      if (select.value !== opt.value) {
        select.value = opt.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      refreshSkinDropdown(select);
      closeAllSkinDropdowns();
    });
    menu.appendChild(li);
  }
}

function setupSkinDropdown(select: HTMLSelectElement): void {
  const wrap = select.parentElement;
  if (wrap === null) return;

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'skin-dropdown-trigger';
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.innerHTML =
    '<span class="skin-dropdown-label"></span>'
    + '<span class="skin-dropdown-icons"></span>'
    + '<span class="skin-dropdown-caret" aria-hidden="true">▾</span>';

  const menu = document.createElement('ul');
  menu.className = 'skin-dropdown-menu hidden';
  menu.setAttribute('role', 'listbox');

  trigger.addEventListener('click', (ev) => {
    ev.stopPropagation();
    const wasOpen = skinDropdownOpen === select;
    closeAllSkinDropdowns();
    if (!wasOpen) {
      menu.classList.remove('hidden');
      trigger.setAttribute('aria-expanded', 'true');
      skinDropdownOpen = select;
    }
  });

  wrap.append(trigger, menu);
  skinDropdowns.set(select, { trigger, menu });

  // External code mutates select.value; keep trigger in sync via change events.
  select.addEventListener('change', () => { refreshSkinDropdown(select); });

  refreshSkinDropdown(select);
}

setupSkinDropdown(fetchDefaultSkinSelect);
setupSkinDropdown(uploadDefaultSkinSelect);
setupSkinDropdown(matchDefaultSkinSelect);
setupSkinDropdown(autoDefaultSkinSelect);

document.addEventListener('click', (ev) => {
  if (skinDropdownOpen === null) return;
  const state = skinDropdowns.get(skinDropdownOpen);
  if (state === undefined) { closeAllSkinDropdowns(); return; }
  const t = ev.target;
  if (t instanceof Node && (state.trigger.contains(t) || state.menu.contains(t))) return;
  closeAllSkinDropdowns();
});

const HASH_LOOKUP_STORAGE_KEY = 'osu_hash_to_setid';
const hashToSetId: Map<string, number> = (() => {
  try {
    const raw = localStorage.getItem(HASH_LOOKUP_STORAGE_KEY);
    if (raw === null) return new Map();
    return new Map(Object.entries(JSON.parse(raw) as Record<string, number>));
  } catch {
    return new Map();
  }
})();
function persistHashLookup(): void {
  try {
    localStorage.setItem(HASH_LOOKUP_STORAGE_KEY, JSON.stringify(Object.fromEntries(hashToSetId)));
  } catch { /* quota exceeded or storage disabled — ignore */ }
}

// Storyboard toggle: a per-session render option like the other Display toggles, on by default.
// Every solo build parses its storyboard; `storyboardWanted` remembers the user's last choice so
// a newly loaded replay starts the same way. The checkbox mirrors the active session (unchecked
// and disabled when the map has no storyboard). Covers the storyboard's samples too (AudioSync).
let storyboardWanted = true;
optStoryboard.addEventListener('change', () => {
  storyboardWanted = optStoryboard.checked;
  forEachActiveReplaySession(s => {
    s.renderer.options.showStoryboard = optStoryboard.checked;
    s.audioSync.setStoryboardSamples(optStoryboard.checked);
  });
});
// Video display toggle: same pattern; disabled when the session has no playable video.
let videoWanted = true;
optVideo.addEventListener('change', () => {
  videoWanted = optVideo.checked;
  forEachActiveRenderer(r => { r.options.showVideo = optVideo.checked; });
});

// Video *download* opt-in: the switch beside each Load button. Off on every page load (a video
// multiplies the .osz size) and never persisted. It only decides the mirror URL of the next
// download; whatever an archive holds is played.
let videoDownloadWanted = false;
for (const btn of videoDlBtns) {
  btn.addEventListener('click', () => {
    videoDownloadWanted = !videoDownloadWanted;
    for (const b of videoDlBtns) b.setAttribute('aria-pressed', String(videoDownloadWanted));
  });
}
// Phones: no video downloads (data cost), like export and match view.
if (isMatchUnsupported()) for (const b of videoDlBtns) b.classList.add('hidden');

/** What the settings-panel Video toggle's tooltip should say for a session (empty when it plays). */
function videoStatusText(core: CoreSession): string {
  const st = core.videoStatus;
  switch (st.kind) {
    case 'none':        return 'No video in this beatmap';
    case 'not-loaded':  return 'Video not downloaded — turn on the video download switch next to Load Replay and load again';
    case 'unsupported': return `Video format not supported by browsers (${st.ext})`;
    case 'playable':    return core.video?.failed === true ? 'This browser cannot decode this video' : '';
  }
}

/** A with-video archive is a superset of a stripped one: `hasVideo: false` entries are replaced when video is wanted. */
const setIdToOsz     = new Map<number, { buffer: ArrayBuffer; hasVideo: boolean }>();
function cachedOsz(setId: number, video = false): ArrayBuffer | undefined {
  const hit = setIdToOsz.get(setId);
  return hit !== undefined && (hit.hasVideo || !video) ? hit.buffer : undefined;
}
/** The .osz for a solo load: cached, else downloaded per the video switch. Callers cache it after a clean build when `fromCache` is false. */
async function getOsz(setId: number): Promise<{ buffer: ArrayBuffer; fromCache: boolean; hasVideo: boolean }> {
  const hasVideo = videoDownloadWanted;
  const cached = cachedOsz(setId, hasVideo);
  if (cached !== undefined) return { buffer: cached, fromCache: true, hasVideo };
  return { buffer: (await downloadBeatmapSet(setId, undefined, { video: hasVideo })).buffer, fromCache: false, hasVideo };
}
const scoreIdToOsr   = new Map<string, ArrayBuffer>();
/** Legacy stable IDs get a `mode:` prefix; lazer IDs (ruleset null) stay bare, matching match-tab writes. */
function scoreCacheKey(scoreId: string, ruleset: ScoreRuleset | null): string {
  return ruleset !== null ? `${ruleset}:${scoreId}` : scoreId;
}

// Memoized parseReplay: LZMA-decompressing the frame stream is the dominant cost and the
// same buffer gets parsed 2-3× per load (hash probe, buildSession, skin-swap rebuilds).
// ReplayData is consumed read-only everywhere, so sharing the object is safe. Small LRU —
// decompressed frames are large.
const PARSED_REPLAY_CACHE_CAP = 16;
const parsedReplayCache = new Map<string, ReplayData>();

/** `key` lets callers that already MD5'd the buffer (dedupKey) skip a second multi-MB hash. */
async function parseReplayCached(osrBuffer: ArrayBuffer, key?: string): Promise<ReplayData> {
  key ??= md5(new Uint8Array(osrBuffer));
  const hit = parsedReplayCache.get(key);
  if (hit !== undefined) {
    parsedReplayCache.delete(key);
    parsedReplayCache.set(key, hit);
    return hit;
  }
  const parsed = await parseReplay(osrBuffer);
  parsedReplayCache.set(key, parsed);
  if (parsedReplayCache.size > PARSED_REPLAY_CACHE_CAP) {
    parsedReplayCache.delete(parsedReplayCache.keys().next().value!);
  }
  return parsed;
}

/** LRU cap to bound RAM (~10-50MB AudioBuffer per pair); Map insertion order encodes LRU. */
const MATCH_PAIR_CACHE_CAP = 5;

function formatTime(ms: number): string {
  const totalSec = Math.floor(Math.max(0, ms) / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

function updatePlayPauseBtn(playing: boolean): void {
  playPauseBtn.innerHTML = playing ? '&#10073;&#10073;' : '&#9654;';
  playPauseBtn.setAttribute('aria-label', playing ? 'Pause' : 'Play');
}

function tickUI(): void {
  const player = activePlayer;
  if (player !== null) {
    const timeMs   = player.currentTimeMs;
    const duration = player.durationMs;

    if (!scrubbing) {
      scrubBar.value = String(Math.round((timeMs / duration) * 1000));
    }

    timeDisplay.textContent = `${formatTime(timeMs)} / ${formatTime(duration)}`;
    updatePlayPauseBtn(player.isPlaying);
  }

  if (activeTeamMatch !== null) {
    let redSum = 0, blueSum = 0;
    for (const slot of activeTeamMatch.slots) {
      if (slot.session === null) continue;
      const s = slot.session.renderer.currentScore();
      if (slot.team === 'red') redSum  += s;
      else                     blueSum += s;
    }
    matchScoreLeft.textContent  = redSum.toLocaleString('en-US');
    matchScoreRight.textContent = blueSum.toLocaleString('en-US');
    const redLeads  = redSum  > blueSum;
    const blueLeads = blueSum > redSum;
    matchScoreLeft .classList.toggle('leading', redLeads);
    matchScoreRight.classList.toggle('leading', blueLeads);
    const diff = Math.abs(redSum - blueSum);
    const diffText = diff === 0 ? '' : `-${diff.toLocaleString('en-US')}`;
    matchScoreDiffLeft .textContent = blueLeads ? diffText : '';
    matchScoreDiffRight.textContent = redLeads  ? diffText : '';
  } else if (activeMatchPair !== null && partnerSession !== null) {
    const primaryScore = activeMatchPair.primary.renderer.currentScore();
    const partnerScore = partnerSession.renderer.currentScore();
    matchScoreLeft.textContent  = primaryScore.toLocaleString('en-US');
    matchScoreRight.textContent = partnerScore.toLocaleString('en-US');
    const primaryLeads = primaryScore > partnerScore;
    const partnerLeads = partnerScore > primaryScore;
    matchScoreLeft .classList.toggle('leading', primaryLeads);
    matchScoreRight.classList.toggle('leading', partnerLeads);
    const diff = Math.abs(primaryScore - partnerScore);
    const diffText = diff === 0 ? '' : `-${diff.toLocaleString('en-US')}`;
    matchScoreDiffLeft .textContent = partnerLeads ? diffText : '';
    matchScoreDiffRight.textContent = primaryLeads ? diffText : '';
  }

  requestAnimationFrame(tickUI);
}
requestAnimationFrame(tickUI);

// Route play/pause/seek through these helpers; never call player.play()/audioSync.playFrom() directly.

async function doPlay(): Promise<void> {
  const player = activePlayer;
  if (player === null) return;
  if (exporting) return; // live renderer is stopped during export; don't restart playback

  let presMs = player.currentTimeMs;
  if (presMs >= player.durationMs - 50) presMs = 0;

  const audio = activeAudioSync;
  if (audio !== null) {
    // Anchor Player's clock BEFORE play() so _lastClockMs uses the audio hardware timeline.
    player.setClockFn(audio.clockFn);
    await audio.playFrom(presMs);
  }

  player.seek(presMs);
  player.play();

  // Partners share the active session's AudioSync (muted); re-anchor and start each.
  if (partnerSession !== null) {
    partnerSession.player.seek(presMs);
    partnerSession.player.play();
  }
  if (activeTeamMatch !== null) {
    for (const slot of activeTeamMatch.slots) {
      const s = slot.session;
      if (s === null || s.player === player) continue;
      s.player.seek(presMs);
      s.player.play();
    }
  }
}

function doPause(): void {
  const player = activePlayer;
  if (player === null) return;
  player.pause();
  activeAudioSync?.pause();
  partnerSession?.player.pause();
  if (activeTeamMatch !== null) {
    for (const slot of activeTeamMatch.slots) {
      if (slot.session !== null && slot.session.player !== player) slot.session.player.pause();
    }
  }
}

/** commitAudio=false during drag avoids audio stop/start churn; audio commits on mouseup. */
async function doSeek(presMs: number, commitAudio = true): Promise<void> {
  const player = activePlayer;
  if (player === null) return;

  player.seek(presMs);
  partnerSession?.player.seek(presMs);
  if (activeTeamMatch !== null) {
    for (const slot of activeTeamMatch.slots) {
      if (slot.session !== null && slot.session.player !== player) slot.session.player.seek(presMs);
    }
  }

  if (commitAudio && activeAudioSync !== null) {
    await activeAudioSync.seekTo(presMs);
    // Re-anchor every Player against the post-seek clock.
    player.seek(presMs);
    partnerSession?.player.seek(presMs);
    if (activeTeamMatch !== null) {
      for (const slot of activeTeamMatch.slots) {
        if (slot.session !== null && slot.session.player !== player) slot.session.player.seek(presMs);
      }
    }
  }
}

playPauseBtn.addEventListener('click', () => {
  const player = activePlayer;
  if (player === null) return;
  if (player.isPlaying) doPause();
  else doPlay();
});

scrubBar.addEventListener('mousedown',  () => { scrubbing = true; });
scrubBar.addEventListener('touchstart', () => { scrubbing = true; }, { passive: true });

scrubBar.addEventListener('input', () => {
  const player = activePlayer;
  if (player === null) return;
  const ms = (Number(scrubBar.value) / 1000) * player.durationMs;
  doSeek(ms, false);
});

scrubBar.addEventListener('mouseup', () => {
  scrubbing = false;
  const player = activePlayer;
  if (player === null) return;
  const ms = (Number(scrubBar.value) / 1000) * player.durationMs;
  doSeek(ms, true);
});

scrubBar.addEventListener('touchend', () => {
  scrubbing = false;
  const player = activePlayer;
  if (player === null) return;
  const ms = (Number(scrubBar.value) / 1000) * player.durationMs;
  doSeek(ms, true);
});

// ── Export clip-range selector (Phase X3.5) ──────────────────────────────────
// Dual-handle overlay on the scrub bar that sets the export range. The engine has been
// range-based since X1, so this is pure front-end: the handles just feed startPresMs/endPresMs.
const MIN_CLIP_MS = 250;   // narrowest selectable clip
const CLIP_SNAP_FRAC = 0.012; // snap a handle to the track edge within this fraction (eases "full")

/** Active track length in presentation ms (== scrub-bar domain). */
function clipFullDurationMs(): number {
  return activePlayer?.durationMs ?? 0;
}
function clipDurationMs(): number {
  return clipEndPresMs - clipStartPresMs;
}
function clipIsTrimmed(): boolean {
  const dur = clipFullDurationMs();
  return clipStartPresMs > 1 || clipEndPresMs < dur - 1;
}

function layoutClipRange(): void {
  const dur = clipFullDurationMs();
  // Shows whenever a track is loaded — solo or a mounted match pair/team. On the match overview the
  // controls (and the scrub bar this lives in) are hidden, and dur is 0, so it stays hidden there.
  if (dur <= 0) {
    clipRange.classList.add('hidden');
    return;
  }
  clipRange.classList.remove('hidden');
  const startPct = (clipStartPresMs / dur) * 100;
  const endPct   = (clipEndPresMs   / dur) * 100;
  clipHandleStart.style.left = `${startPct}%`;
  clipHandleEnd.style.left   = `${endPct}%`;
  clipDimLeft.style.width    = `${startPct}%`;
  clipDimRight.style.width   = `${100 - endPct}%`;
  clipStartLabel.textContent = formatTime(clipStartPresMs);
  clipEndLabel.textContent   = formatTime(clipEndPresMs);
  clipRange.classList.toggle('trimmed', clipIsTrimmed());
}

/** Reset the selection to the full track. */
function resetClipRange(): void {
  clipStartPresMs = 0;
  clipEndPresMs   = clipFullDurationMs();
  layoutClipRange();
}

/** Clamp a handle move against the opposite handle + track edges, with edge snapping. */
function setClipBoundary(which: 'start' | 'end', ms: number): void {
  const dur = clipFullDurationMs();
  if (dur <= 0) return;
  if (ms < dur * CLIP_SNAP_FRAC)       ms = 0;
  else if (ms > dur * (1 - CLIP_SNAP_FRAC)) ms = dur;
  if (which === 'start') {
    clipStartPresMs = Math.max(0, Math.min(ms, clipEndPresMs - MIN_CLIP_MS));
  } else {
    clipEndPresMs = Math.min(dur, Math.max(ms, clipStartPresMs + MIN_CLIP_MS));
  }
  layoutClipRange();
  // Scrub the replay to the boundary being moved so the user sees exactly the in/out frame.
  // Video-only (like a scrub-bar drag); the playhead-following thumb tracks it via tickUI.
  void doSeek(which === 'start' ? clipStartPresMs : clipEndPresMs, false);
}

function clipMsFromClientX(clientX: number): number {
  const rect = scrubBarWrap.getBoundingClientRect();
  const frac = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
  return Math.max(0, Math.min(1, frac)) * clipFullDurationMs();
}

function wireClipHandle(handle: HTMLElement, which: 'start' | 'end'): void {
  handle.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    handle.setPointerCapture(e.pointerId);
    handle.classList.add('dragging');
    const onMove = (m: PointerEvent) => setClipBoundary(which, clipMsFromClientX(m.clientX));
    const onUp = () => {
      handle.classList.remove('dragging');
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      // Rest the playhead at the clip in-point and commit the audio seek (the drag was
      // video-only). After trimming the end you're parked at the start, ready to watch the clip.
      void doSeek(clipStartPresMs, true);
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  });
}
wireClipHandle(clipHandleStart, 'start');
wireClipHandle(clipHandleEnd, 'end');

/** Hits every on-screen renderer so match-mode toggles don't diverge between canvases. */
function forEachActiveRenderer(fn: (r: Renderer) => void): void {
  if (activeTeamMatch !== null) {
    for (const slot of activeTeamMatch.slots) {
      if (slot.session !== null) fn(slot.session.renderer);
    }
  } else if (activeMatchPair !== null) {
    fn(activeMatchPair.primary.renderer);
    if (partnerSession !== null) fn(partnerSession.renderer);
  } else if (activeRenderer !== null) {
    fn(activeRenderer);
  }
}

optJudgement.addEventListener('change', () => {
  forEachActiveRenderer(r => { r.options.showJudgement = optJudgement.checked; });
});

// Taiko-only: flying-hit animation (hit notes arcing off the playfield).
optFlyingHits.addEventListener('change', () => {
  forEachActiveRenderer(r => { r.options.taikoFlyingHits = optFlyingHits.checked; });
});

optFollowpoints.addEventListener('change', () => {
  forEachActiveRenderer(r => { r.options.showFollowpoints = optFollowpoints.checked; });
});

optKeyOverlay.addEventListener('change', () => {
  forEachActiveRenderer(r => { r.options.showKeyOverlay = optKeyOverlay.checked; });
});

optURBar.addEventListener('change', () => {
  forEachActiveRenderer(r => { r.options.showURBar = optURBar.checked; });
});

optModIcons.addEventListener('change', () => {
  forEachActiveRenderer(r => { r.options.showModIcons = optModIcons.checked; });
});

optPP.addEventListener('change', () => {
  // Enabling doubles as the retry path for a session whose rosu init failed (initRosu resets
  // its one-shot on rejection, so re-enabling re-attempts the fetch).
  forEachActiveRenderer(r => setPPCounterEnabled(r, optPP.checked));
});

optModHidden.addEventListener('change', () => {
  forEachActiveRenderer(r => { r.options.modHidden = optModHidden.checked; });
});

optModFlashlight.addEventListener('change', () => {
  forEachActiveRenderer(r => { r.options.modFlashlight = optModFlashlight.checked; });
});

optModFadeIn.addEventListener('change', () => {
  forEachActiveRenderer(r => { r.options.modFadeIn = optModFadeIn.checked; });
});

optModCover.addEventListener('change', () => {
  forEachActiveRenderer(r => { r.options.modCover = optModCover.checked; });
});

// Mania-only: vertical playfield flip (upscroll). Fans out to both panes in match mode.
optUpscroll.addEventListener('change', () => {
  forEachActiveRenderer(r => { r.options.maniaUpscroll = optUpscroll.checked; });
});

optBeatmapHitsounds.addEventListener('change', () => {
  const on = optBeatmapHitsounds.checked;
  forEachActiveReplaySession(s => { s.audioSync.setBeatmapHitsounds(on); s.beatmapHitsounds = on; });
});

// Settings dropdown: overlaps the canvas (no reflow); closes on outside click or Escape.
settingsBtn.addEventListener('click', () => {
  const open = settingsDropdown.classList.toggle('hidden');
  settingsMenu.classList.toggle('open', !open);
  settingsBtn.setAttribute('aria-expanded', String(!open));
});
function closeSettingsDropdown(): void {
  settingsDropdown.classList.add('hidden');
  settingsMenu.classList.remove('open');
  settingsBtn.setAttribute('aria-expanded', 'false');
}
document.addEventListener('pointerdown', (e) => {
  if (settingsDropdown.classList.contains('hidden')) return;
  if (e.target instanceof Node && settingsMenu.contains(e.target)) return;
  closeSettingsDropdown();
});
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeSettingsDropdown();
});

// Auto-tab mod selector: a checkbox panel (styled like the Settings dropdown) behind a
// trigger styled like the Skin dropdown. The map's [General] Mode decides which generator
// runs, so mods for other modes are simply ignored at generation time (A5); here we only
// open/close the panel, enforce mutual-exclusion within each conflict group, and surface
// the selection on the trigger. Conflict groups are keyed by checkbox id.
const AUTO_MOD_CONFLICTS: string[][] = [
  ['auto-mod-ez', 'auto-mod-hr'],                          // difficulty reduce vs increase
  ['auto-mod-ht', 'auto-mod-dt', 'auto-mod-nc'],           // rate mods
  ['auto-mod-hd', 'auto-mod-mania-fi', 'auto-mod-mania-co'], // mania visibility mods
];
const autoModChecks = Array.from(
  autoModsMenu.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'),
);

function closeAutoModsMenu(): void {
  autoModsMenu.classList.add('hidden');
  autoModsBtn.setAttribute('aria-expanded', 'false');
}
function updateAutoModsLabel(): void {
  const acronyms: string[] = [];
  for (const cb of autoModChecks) {
    if (!cb.checked) continue;
    acronyms.push(cb.dataset.mod ?? '');
  }
  autoModsLabel.textContent = acronyms.length > 0 ? acronyms.join(', ') : 'None';
}
// Reflect a supplied acronym set (from an #auto= share link) in the checkboxes. The acronyms come
// from a prior valid selection, so they never conflict — setting .checked directly (no change-event)
// is safe.
function applyAutoModsToSelector(acronyms: string[]): void {
  for (const cb of autoModChecks) cb.checked = acronyms.includes(cb.dataset.mod ?? '');
  updateAutoModsLabel();
}

autoModsBtn.addEventListener('click', () => {
  const open = autoModsMenu.classList.toggle('hidden');
  autoModsBtn.setAttribute('aria-expanded', String(!open));
});
for (const cb of autoModChecks) {
  cb.addEventListener('change', () => {
    if (cb.checked) {
      for (const group of AUTO_MOD_CONFLICTS) {
        if (!group.includes(cb.id)) continue;
        for (const otherId of group) {
          if (otherId === cb.id) continue;
          const other = document.getElementById(otherId) as HTMLInputElement | null;
          if (other !== null) other.checked = false;
        }
      }
    }
    updateAutoModsLabel();
  });
}
document.addEventListener('pointerdown', (e) => {
  if (autoModsMenu.classList.contains('hidden')) return;
  if (e.target instanceof Node && autoModsBtn.parentElement?.contains(e.target)) return;
  closeAutoModsMenu();
});
window.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeAutoModsMenu();
});

// Stable mod bits for the Auto-tab selector's acronyms. All of these flow through
// computeModDifficulty's stable-bitmask path, so the whole pipeline (judge/score/render/
// audio/stacking/convert) applies them. Cover (CO) has no stable bit and is purely visual,
// so it's handled separately as a render option (see modsForAcronyms / loadAutoSession).
const AUTO_MOD_BITS: Record<string, number> = {
  EZ: Mod.Easy, HR: Mod.HardRock, HD: Mod.Hidden, FL: Mod.Flashlight,
  HT: Mod.HalfTime, DT: Mod.DoubleTime, NC: Mod.Nightcore,
  FI: 1 << 20,   // mania FadeIn
  MR: 1 << 30,   // Mirror — std X reflect / mania column flip / catch EffectiveX reflect (by map mode)
};
/** The acronyms the Auto selector / share links / embed understand, for input validation. */
const KNOWN_AUTO_ACRONYMS = new Set(['EZ', 'HR', 'HD', 'FL', 'HT', 'DT', 'NC', 'FI', 'MR', 'CO']);

// The acronyms ticked in the Auto-tab selector (mode-independent). This is the DOM read;
// share-link and iframe paths supply their acronym list directly.
function selectedAutoAcronyms(): string[] {
  const out: string[] = [];
  for (const cb of autoModChecks) {
    if (!cb.checked) continue;
    const a = cb.dataset.mod ?? '';
    if (a !== '') out.push(a);
  }
  return out;
}

// Collapse acronyms into a stable bitmask for the loaded map's mode. Off-mode acronyms are ignored
// (the map's [General] Mode picks the generator): FadeIn/Cover are mania-only, Mirror has no taiko
// form, the rest apply to any mode. The selector can't know the mode when a box is ticked, so the
// one mode-specific conflict — lazer's osu!std Mirror is incompatible with Hard Rock (mania/catch allow
// both) — is resolved here: HR wins, Mirror is dropped. `cover` (CO has no stable bit) and `effective`
// (the acronyms that actually applied — what the #auto= share link stores) are reported separately.
function modsForAcronyms(acronyms: string[], mode: number): { mods: number; cover: boolean; effective: string[] } {
  let mods = 0;
  let cover = false;
  const effective: string[] = [];
  for (const acronym of acronyms) {
    if (!KNOWN_AUTO_ACRONYMS.has(acronym)) continue;
    if ((acronym === 'FI' || acronym === 'CO') && mode !== 3) continue;
    if (acronym === 'MR' && (mode === 1 || (mode === 0 && acronyms.includes('HR')))) continue;
    effective.push(acronym);
    if (acronym === 'CO') { cover = true; continue; }
    mods |= AUTO_MOD_BITS[acronym] ?? 0;
  }
  return { mods, cover, effective };
}

optMusicVol.addEventListener('input', () => {
  optMusicVolValue.textContent = `${optMusicVol.value}%`;
  const v = Number(optMusicVol.value) / 100;
  activeAudioSync?.setSongVolume(v);
  const replay = activeReplaySession();
  if (replay !== null) replay.musicVol = v;
});

optEffectsVol.addEventListener('input', () => {
  optEffectsVolValue.textContent = `${optEffectsVol.value}%`;
  const v = Number(optEffectsVol.value) / 100;
  activeAudioSync?.setEffectsVolume(v);
  const replay = activeReplaySession();
  if (replay !== null) replay.effectsVol = v;
});

function activeReplaySession(): ReplaySession | null {
  if (activeSession !== null && activeSession.kind === 'replay') return activeSession;
  if (activeMatchPair !== null) return activeMatchPair.primary;
  return null;
}

optBgDim.addEventListener('input', () => {
  optBgDimValue.textContent = `${optBgDim.value}%`;
  const dim = Number(optBgDim.value) / 100;
  forEachActiveRenderer(r => { r.options.backgroundDim = dim; });
});

optManiaScroll.addEventListener('input', () => {
  const speed = Math.max(1, Math.min(40, Number(optManiaScroll.value) | 0));
  optManiaScrollValue.textContent = String(speed);
  forEachActiveRenderer(r => { r.options.maniaScrollSpeed = speed; });
});

optSyncBtn.addEventListener('click', () => {
  if (optEffectsVol.value === optMusicVol.value) return;
  optEffectsVol.value = optMusicVol.value;
  optEffectsVol.dispatchEvent(new Event('input'));
});

// Committing a rate restarts the song source + hitsound schedule (via doSeek); doing that at
// input-event rate during a drag audibly stutters. The readout updates on every event; the
// commit runs on a ~100ms trailing edge (~10Hz, imperceptible). Applying to ALL active
// sessions keeps the muted match partner/team-slot AudioSyncs in agreement, so audio-swap
// and clock hand-offs inherit the rate. When paused, setUserRate just stores the field and
// the next doPlay picks it up — no seek needed.
let rateCommitTimer: ReturnType<typeof setTimeout> | null = null;

function commitPlaybackRate(): void {
  rateCommitTimer = null;
  const next = Number(optRate.value) / 100;
  if (next === playbackRate) return;
  playbackRate = next;
  forEachActiveReplaySession(s => s.audioSync.setUserRate(playbackRate));
  forEachActiveRenderer(r => { r.options.userRate = playbackRate; });
  if (activePlayer !== null && activePlayer.isPlaying) void doSeek(activePlayer.currentTimeMs);
}

optRate.addEventListener('input', () => {
  optRateValue.textContent = `${(Number(optRate.value) / 100).toFixed(2)}x`;
  if (rateCommitTimer === null) rateCommitTimer = setTimeout(commitPlaybackRate, 100);
});

optRateReset.addEventListener('click', () => {
  if (optRate.value === '100') return;
  optRate.value = '100';
  optRate.dispatchEvent(new Event('input'));
});

const OFFSET_STEP_MS = 5;
const OFFSET_MAX_MS  = 500;

function formatOffset(ms: number): string {
  if (ms === 0) return '+0 ms';
  const sign = ms > 0 ? '+' : '−';
  return `${sign}${Math.abs(ms)} ms`;
}

function updateOffsetReadout(ms: number): void {
  optOffsetValue.textContent = formatOffset(ms);
}

function forEachActiveReplaySession(fn: (s: ReplaySession) => void): void {
  if (activeTeamMatch !== null) {
    for (const slot of activeTeamMatch.slots) {
      if (slot.session !== null) fn(slot.session);
    }
  } else if (activeMatchPair !== null) {
    fn(activeMatchPair.primary);
    if (partnerSession !== null) fn(partnerSession);
  } else if (activeSession !== null && activeSession.kind === 'replay') {
    fn(activeSession);
  }
}

function nudgeOffset(deltaMs: number): void {
  const replay = activeReplaySession();
  if (replay === null) return;
  const next = Math.max(-OFFSET_MAX_MS,
                Math.min(OFFSET_MAX_MS, replay.audioOffsetMs + deltaMs));
  if (next === replay.audioOffsetMs) return;
  forEachActiveReplaySession(s => {
    s.audioOffsetMs = next;
    s.renderer.options.audioOffsetMs = next;
  });
  updateOffsetReadout(next);
}

function resetOffset(): void {
  const replay = activeReplaySession();
  if (replay === null || replay.audioOffsetMs === 0) return;
  forEachActiveReplaySession(s => {
    s.audioOffsetMs = 0;
    s.renderer.options.audioOffsetMs = 0;
  });
  updateOffsetReadout(0);
}

optOffsetDec.addEventListener('click',   () => nudgeOffset(-OFFSET_STEP_MS));
optOffsetInc.addEventListener('click',   () => nudgeOffset(+OFFSET_STEP_MS));
optOffsetReset.addEventListener('click', () => resetOffset());

interface ScrubMarkerSource {
  results: readonly HitResult[];
  introOffsetMs: number;
  presentationDurationMs: number;
  speed: number;
  side: 'primary' | 'partner';
}

function renderScrubMarkers(sources: readonly ScrubMarkerSource[]): void {
  scrubMarkers.replaceChildren();

  for (const src of sources) {
    if (src.presentationDurationMs <= 0) continue;
    for (const r of src.results) {
      // Combo-neutral results (catch tiny-droplet/banana, taiko ticks) never mark a
      // miss/sliderbreak. No-op for taiko/mania (their comboIgnore results are all judgement>0).
      if (r.comboIgnore) continue;
      const isMiss        = r.isSliderSub !== true && r.judgement === 0;
      const isSliderbreak = !isMiss && r.comboBreak;
      if (!isMiss && !isSliderbreak) continue;

      const presMs = (r.time - src.introOffsetMs) / src.speed;
      if (presMs < 0 || presMs > src.presentationDurationMs) continue;

      const marker = document.createElement('span');
      marker.className = `scrub-marker ${isMiss ? 'miss' : 'sliderbreak'} ${src.side}`;
      marker.style.left = `${(presMs / src.presentationDurationMs) * 100}%`;
      scrubMarkers.appendChild(marker);
    }
  }
}

async function consumeSkinUpload(
  fileInput: HTMLInputElement,
  originatingSelect: HTMLSelectElement,
): Promise<string> {
  const file = fileInput.files?.[0];
  if (!file) return originatingSelect.value;

  const oskBuffer = await readFileAsArrayBuffer(file);
  const skin = await loadSkin(oskBuffer, ensureAudioCtx());
  const name = resolveUploadedSkinName(skin, file);
  registerUploadedSkin(name, skin);

  originatingSelect.value = name;
  oskInput.value = '';
  oskInput.dispatchEvent(new Event('change', { bubbles: true }));
  fetchOskInput.value = '';
  fetchOskInput.dispatchEvent(new Event('change', { bubbles: true }));
  return name;
}

/** Inert ReplaySession (no globals/rendering); activate via activateSession. */
interface BuildSessionOpts {
  canvas: HTMLCanvasElement;
  /** Required unless `replayData` is provided (Auto synthesizes its own — no .osr). */
  osrBuffer?: ArrayBuffer;
  /** Required unless `shared` is provided. */
  oszBuffer?: ArrayBuffer;
  /** Reuse parsed/decoded assets instead of re-parsing the .osz. */
  shared?: SharedSessionAssets;
  /** Pre-parsed .osr, when the caller already parsed it (skips a cache-key MD5). */
  replayData?: ReplayData;
  skinName: string;
  dedupKey: string;
  scoreId: string | null;
  scoreRuleset?: ScoreRuleset | null;
  beatmapsetId: number | null;
  /** False for match-pair primaries — raw caches are owned by the parent MatchSession. */
  ownsRawCache?: boolean;
  /** Parse + decode the beatmap's storyboard (default true). Match builds pass false (spec D-MATCH). */
  storyboard?: boolean;
  /** Auto-generated ("osu!" AUTO) replay — drives the AUTO badge. */
  isAuto?: boolean;
  /** Auto-only: beatmapId + chosen mod acronyms, stored on the session for #auto= share-link rebuild. */
  auto?: { beatmapId: number; mods: string[] };
  /** Supplies the canonical .osu when the mirror .osz is stale (Auto's login-free recovery). */
  fetchOsuOverride?: () => Promise<Uint8Array>;
}

async function buildSession(opts: BuildSessionOpts): Promise<ReplaySession> {
  const { canvas: targetCanvas, osrBuffer, oszBuffer, shared: sharedIn,
          skinName, dedupKey, scoreId, scoreRuleset = null, beatmapsetId,
          ownsRawCache = true, isAuto = false, auto = null } = opts;

  const audioCtx = ensureAudioCtx();
  let replayData = opts.replayData;
  if (replayData === undefined) {
    if (osrBuffer === undefined) throw new Error('buildSession: osrBuffer or replayData is required');
    replayData = await parseReplayCached(osrBuffer);
  }

  // Same dedupKey ⇒ same .osr ⇒ same map and mods, so a live session's decoded assets
  // (beatmap incl. stacking, song, background, hitsounds) are directly reusable.
  // This is the skin-swap fast path: only the skin merge + re-judge run from scratch.
  const live = (sessions.find(s => s.kind === 'replay' && s.dedupKey === dedupKey) as ReplaySession | undefined)?.shared;
  // Except when this load brought the video a live session's assets lack: then the fresh archive wins.
  const reused = sharedIn ?? (live !== undefined && live.video === null && oszBuffer !== undefined && videoDownloadWanted ? undefined : live);

  let beatmapSetInput: ArrayBuffer | BeatmapAssets;
  if (reused !== undefined) {
    reused.refs++;
    beatmapSetInput = reused;
  } else {
    if (oszBuffer === undefined) {
      throw new Error('buildSession: oszBuffer is required when shared is not provided');
    }
    beatmapSetInput = oszBuffer;
  }

  // A logged-in user can recover from a stale mirror .osz by pulling the canonical .osu
  // straight from osu! — applies to fetch-originated loads and manual loads alike (incl. the
  // .osr-only path, whose .osz comes from the mirror). Logged-out manual uploads keep the
  // throw-on-mismatch behaviour and fall back to the user supplying a matching .osz.
  // Auto passes its already-fetched canonical .osu directly (opts.fetchOsuOverride), so it
  // stays login-free even when the mirror .osz is stale.
  const fetchOsuOverride = opts.fetchOsuOverride ?? (isLoggedIn()
    ? async (): Promise<Uint8Array> => {
        const token = await getAccessToken();
        const { beatmapId } = await lookupBeatmapByHash(replayData.beatmapHash, token);
        return new Uint8Array(await downloadBeatmapOsu(beatmapId, token));
      }
    : undefined);

  // osu!std replays must never fall through to a mode-incompatible skin; swap to YUGEN if selected skin lacks osu.
  let effectiveSkinName = skinName;
  if (replayData.mode === 0 && effectiveSkinName !== OSU_FALLBACK_SKIN) {
    const declared = DEFAULT_SKIN_GAMEMODES[effectiveSkinName];
    let supportsOsu: boolean;
    if (declared !== undefined) {
      supportsOsu = declared.includes('osu');
    } else {
      const uploaded = await getSkin(effectiveSkinName, audioCtx);
      supportsOsu = uploaded.images.has('hitcircle.png')
                 || uploaded.images.has('hitcircle@2x.png');
    }
    if (!supportsOsu) effectiveSkinName = OSU_FALLBACK_SKIN;
  }
  // Taiko replays must never fall through to dev-time primitives; swap to YUGEN if selected skin lacks taiko.
  if (replayData.mode === 1 && effectiveSkinName !== TAIKO_FALLBACK_SKIN) {
    const declared = DEFAULT_SKIN_GAMEMODES[effectiveSkinName];
    let supportsTaiko: boolean;
    if (declared !== undefined) {
      supportsTaiko = declared.includes('taiko');
    } else {
      const uploaded = await getSkin(effectiveSkinName, audioCtx);
      supportsTaiko = uploaded.images.has('taikohitcircle.png')
                   || uploaded.images.has('taikohitcircle@2x.png');
    }
    if (!supportsTaiko) effectiveSkinName = TAIKO_FALLBACK_SKIN;
  }
  // Same guard for mania: ensure the chosen skin ships mania sprites, else use the fallback.
  if (replayData.mode === 3 && effectiveSkinName !== MANIA_FALLBACK_SKIN) {
    const declared = DEFAULT_SKIN_GAMEMODES[effectiveSkinName];
    let supportsMania: boolean;
    if (declared !== undefined) {
      supportsMania = declared.includes('mania');
    } else {
      const uploaded = await getSkin(effectiveSkinName, audioCtx);
      // Matches lazer's `ManiaLegacySkinTransformer.IsLegacySkin`: a skin counts
      // as mania-aware if the default-skin key/note sprite exists at the root
      // OR the skin declares `[Mania]` sections (which carry NoteImage/KeyImage
      // lookups). The latter catches skins like Hiroshi that ship their assets
      // under subfolder paths (`Arrownote/note1.png`) referenced via skin.ini.
      supportsMania = uploaded.images.has('mania-note1.png')
                   || uploaded.images.has('mania-note1@2x.png')
                   || uploaded.images.has('mania-key1.png')
                   || uploaded.images.has('mania-key1@2x.png')
                   || uploaded.config.maniaSections.length > 0;
    }
    if (!supportsMania) effectiveSkinName = MANIA_FALLBACK_SKIN;
  }
  // Same guard for catch: ensure the chosen skin ships catch sprites, else use the fallback.
  // (Phase 0 draws no fruit yet, so this only establishes the swap path; the bundled catch
  // baseline + fruit rendering land in Phases 4/8 — see docs/catch.md.)
  if (replayData.mode === 2 && effectiveSkinName !== CATCH_FALLBACK_SKIN) {
    const declared = DEFAULT_SKIN_GAMEMODES[effectiveSkinName];
    let supportsCatch: boolean;
    if (declared !== undefined) {
      supportsCatch = declared.includes('catch');
    } else {
      const uploaded = await getSkin(effectiveSkinName, audioCtx);
      // A legacy skin counts as catch-aware if it ships a fruit sprite or a catcher (either the
      // old `fruit-ryuuta` or the new `fruit-catcher-idle`) — see docs/reference/catch_skinning.md.
      supportsCatch = uploaded.images.has('fruit-apple.png')
                   || uploaded.images.has('fruit-apple@2x.png')
                   || uploaded.images.has('fruit-ryuuta.png')
                   || uploaded.images.has('fruit-catcher-idle.png');
    }
    if (!supportsCatch) effectiveSkinName = CATCH_FALLBACK_SKIN;
  }

  // Default skin is the baseline (mirrors osu!stable); selected skin layers on top.
  // buildSkin handles the taiko pippidon single-owner rule + the spinnerImages channel.
  const baseSkin     = await getSkin(FALLBACK_SKIN, audioCtx);
  const selectedSkin = effectiveSkinName !== FALLBACK_SKIN
    ? await getSkin(effectiveSkinName, audioCtx)
    : undefined;
  const skin = buildSkin(baseSkin, selectedSkin, { mode: replayData.mode });

  const beatmapHitsounds = optBeatmapHitsounds.checked;
  const core = await createReplaySession({
    canvas: targetCanvas,
    audioContext: audioCtx,
    replay: replayData,
    beatmapSet: beatmapSetInput,
    skin,
    beatmapHitsounds,
    fetchOsuOverride,
    userRate: playbackRate,
    pageZoom: effectiveZoom(),
    storyboard: opts.storyboard ?? true,
    video: opts.storyboard ?? true,
  });
  core.renderer.options.showStoryboard = storyboardWanted;
  core.audioSync.setStoryboardSamples(storyboardWanted);
  core.renderer.options.showVideo = videoWanted;

  const shared: SharedSessionAssets = reused ?? { refs: 1, ...core.assets };

  // pp counter (docs/pp.md): app-side overlay on the engine renderer. Kicks rosu's async WASM
  // init off the load critical path and injects the counter via options.hudOverlay when it
  // resolves (the RAF loop paints it next frame). The curve is memoized per ReplayData, so
  // rebuilds are cache hits. Best-effort — on failure there's no curve, and the pp toggle
  // retries the init.
  wirePPCounter(core.renderer, core.beatmap, replayData, core.modDiff);
  // Off by default (legacy: rosu predates osu!'s 2026 pp rework); the toggle re-enables it.
  setPPCounterEnabled(core.renderer, optPP.checked);

  const musicVol   = Number(optMusicVol.value)   / 100;
  const effectsVol = Number(optEffectsVol.value) / 100;
  core.audioSync.setSongVolume(musicVol);
  core.audioSync.setEffectsVolume(effectsVol);

  const label = `${replayData.username} on ${core.beatmap.title} [${core.beatmap.version}]${replayLabelSuffix(core.modDiff.mods, replayData)}`;

  return {
    kind: 'replay' as const,
    id: nextSessionId++,
    dedupKey,
    skinName: effectiveSkinName,
    mode: core.mode,
    label,
    username: replayData.username,
    background: core.background,
    core,
    player: core.player,
    renderer: core.renderer,
    audioSync: core.audioSync,
    timeMapper: core.timeMapper,
    introOffsetMs: core.introOffsetMs,
    speed: core.speed,
    scoreId,
    scoreRuleset,
    beatmapsetId,
    canvas: targetCanvas,
    shared,
    ownsRawCache,
    isAuto,
    auto,
    musicVol,
    effectsVol,
    beatmapHitsounds,
    audioOffsetMs: 0,
    wasPlaying: true,
  };
}

function activateSession(next: Session, opts: { skipOverview?: boolean } = {}): void {
  if (activeSession === next) return;
  if (next.kind === 'match') activateMatchSession(next, opts);
  else                       activateReplaySession(next);
}

function activateReplaySession(next: ReplaySession): void {
  // Tab-switch deactivates (but doesn't destroy) the cached match pair for instant re-activation.
  if (partnerSession !== null || activeMatchPair !== null) deactivateMatchPair();
  exitMatchView();

  const prev = activeSession;
  if (prev !== null && prev.kind === 'replay') {
    prev.wasPlaying = prev.player.isPlaying;
    prev.renderer.stop();
    prev.audioSync.pause();
    prev.player.pause();
  }

  activeSession   = next;
  activePlayer    = next.player;
  activeRenderer  = next.renderer;
  activeAudioSync = next.audioSync;

  optJudgement.checked    = next.renderer.options.showJudgement;
  optFollowpoints.checked = next.renderer.options.showFollowpoints;
  optKeyOverlay.checked   = next.renderer.options.showKeyOverlay;
  optURBar.checked        = next.renderer.options.showURBar;
  optModIcons.checked     = next.renderer.options.showModIcons;
  optPP.checked           = isPPCounterEnabled(next.renderer);
  optModHidden.checked     = next.renderer.options.modHidden;
  optModFlashlight.checked = next.renderer.options.modFlashlight;
  optModFadeIn.checked     = next.renderer.options.modFadeIn;
  optModCover.checked      = next.renderer.options.modCover;
  optUpscroll.checked      = next.renderer.options.maniaUpscroll;
  optFlyingHits.checked = next.renderer.options.taikoFlyingHits;
  optStoryboard.checked   = next.renderer.hasStoryboard && next.renderer.options.showStoryboard;
  optStoryboard.disabled  = !next.renderer.hasStoryboard;
  optVideo.checked        = next.renderer.hasVideo && next.renderer.options.showVideo;
  optVideo.disabled       = !next.renderer.hasVideo;
  optVideo.parentElement!.title = videoStatusText(next.core);
  optBgDim.value          = String(Math.round(next.renderer.options.backgroundDim * 100));
  optBgDimValue.textContent = `${optBgDim.value}%`;
  optManiaScroll.value      = String(next.renderer.options.maniaScrollSpeed);
  optManiaScrollValue.textContent = String(next.renderer.options.maniaScrollSpeed);

  optMusicVol.value          = String(Math.round(next.musicVol * 100));
  optMusicVolValue.textContent = `${optMusicVol.value}%`;
  optEffectsVol.value        = String(Math.round(next.effectsVol * 100));
  optEffectsVolValue.textContent = `${optEffectsVol.value}%`;
  optBeatmapHitsounds.checked = next.beatmapHitsounds;
  // Rate is global (slider state), not per-session: conform the incoming session's AudioSync —
  // it may hold a stale rate if it was built before a slider change committed elsewhere, or
  // was the outgoing solo session when enterMatchView reset the rate to 1.
  next.audioSync.setUserRate(playbackRate);
  next.renderer.options.userRate = playbackRate;

  next.renderer.options.audioOffsetMs = next.audioOffsetMs;
  updateOffsetReadout(next.audioOffsetMs);

  renderScrubMarkers([{
    results: next.renderer.hitResults,
    introOffsetMs: next.introOffsetMs,
    presentationDurationMs: next.timeMapper.presentationDurationMs,
    speed: next.speed,
    side: 'primary',
  }]);
  resetClipRange();

  canvas.classList.remove('hidden');
  controls.classList.remove('hidden');
  renderOptions.classList.remove('hidden');
  renderOptions.classList.toggle('taiko-mode', next.mode === 1);
  renderOptions.classList.toggle('mania-mode', next.mode === 3);
  renderOptions.classList.toggle('catch-mode', next.mode === 2);
  matchOverview.classList.add('hidden');
  playerLabel1.textContent = '';
  playerLabel1.classList.add('hidden');

  scoreInput.value = next.scoreId ?? '';

  fetchDefaultSkinSelect.value  = next.skinName;
  uploadDefaultSkinSelect.value = next.skinName;
  refreshSkinDropdown(fetchDefaultSkinSelect);
  refreshSkinDropdown(uploadDefaultSkinSelect);

  setStatus(next.label);
  renderTabs();
  refreshShareAtTimeBtn();

  next.renderer.start();
  if (next.wasPlaying) {
    updatePlayPauseBtn(true);
    doPlay();
  } else {
    updatePlayPauseBtn(false);
  }
}

/** Paints overview onto canvas1; no renderer/player/audio until user clicks a map. */
function activateMatchSession(next: MatchSession, opts: { skipOverview?: boolean } = {}): void {
  if (partnerSession !== null || activeMatchPair !== null) deactivateMatchPair();
  exitMatchView();

  const prev = activeSession;
  if (prev !== null && prev.kind === 'replay') {
    prev.wasPlaying = prev.player.isPlaying;
    prev.renderer.stop();
    prev.audioSync.pause();
    prev.player.pause();
  }

  activeSession   = next;
  activePlayer    = null;
  activeRenderer  = null;
  activeAudioSync = null;

  // skipOverview: caller is about to mount the dual canvas; painting the overview first flashes.
  if (opts.skipOverview) {
    canvas.classList.add('hidden');
    controls.classList.add('hidden');
    renderOptions.classList.add('hidden');
    matchOverview.classList.add('hidden');
  } else {
    showMatchOverview(next);
  }
  setStatus(next.label);
  setMatchStatus(next.label);
  renderTabs();
  refreshShareAtTimeBtn();
}

function destroySession(session: Session): void {
  if (session.kind === 'replay') {
    destroyReplaySession(session);
  } else {
    destroyMatchSession(session);
  }
}

function destroyReplaySession(session: ReplaySession): void {
  session.core.destroy();
  // Last reference closes the background; partners and skin-swap rebuilds share the bitmap.
  session.shared.refs--;
  if (session.shared.refs === 0) {
    session.background?.close();
    for (const img of session.shared.storyboardImages.values()) img.bitmap.close();
  }

  // Match-pair primaries set ownsRawCache=false so "Return to overview" leaves caches populated.
  if (session.ownsRawCache) {
    if (session.scoreId !== null) scoreIdToOsr.delete(scoreCacheKey(session.scoreId, session.scoreRuleset));
    if (session.beatmapsetId !== null) setIdToOsz.delete(session.beatmapsetId);
  }
}

function destroyMatchSession(session: MatchSession): void {
  session.cover?.close();
  // MatchSession owns built entries + the raw buffer caches for its maps.
  for (const entry of session.builtPairs.values()) destroyBuiltEntry(entry);
  session.builtPairs.clear();
  for (const item of session.match.playlistItems) {
    setIdToOsz.delete(item.beatmapsetId);
    for (const s of item.scores) scoreIdToOsr.delete(s.scoreId);
  }
}

/** Shared by destroyMatchSession + LRU eviction. */
function destroyBuiltEntry(entry: BuiltMatchEntry): void {
  if (entry.kind === 'pair') {
    destroyReplaySession(entry.primary);
    if (entry.partner !== null) destroyReplaySession(entry.partner);
  } else {
    for (const slot of entry.slots) {
      if (slot.session !== null) destroyReplaySession(slot.session);
    }
  }
}

function touchBuiltPair(session: MatchSession, playlistItemId: number, entry: BuiltMatchEntry): void {
  session.builtPairs.delete(playlistItemId);
  session.builtPairs.set(playlistItemId, entry);
}

/** Skips the currently-active entry so we never destroy what's on screen. */
function trimBuiltPairs(session: MatchSession): void {
  const activeItemId = activeMatchPair?.matchSession === session
    ? activeMatchPair.item.playlistItemId
    : null;
  while (session.builtPairs.size > MATCH_PAIR_CACHE_CAP) {
    let evictedKey: number | null = null;
    for (const key of session.builtPairs.keys()) {
      if (key !== activeItemId) { evictedKey = key; break; }
    }
    if (evictedKey === null) break; // only the active entry remains
    const victim = session.builtPairs.get(evictedKey)!;
    destroyBuiltEntry(victim);
    session.builtPairs.delete(evictedKey);
  }
}

/** Audio muted, clock-locked to primary's AudioSync; call attachPartner to mount on canvas2. */
async function buildPartnerSession(primary: ReplaySession, partnerOsrBuffer: ArrayBuffer): Promise<ReplaySession> {
  const partner = await buildSession({
    canvas:       canvas2,
    storyboard:   false,
    osrBuffer:    partnerOsrBuffer,
    shared:       primary.shared,
    skinName:     primary.skinName,
    dedupKey:     `partner:${md5(new Uint8Array(partnerOsrBuffer))}`,
    scoreId:      null,
    beatmapsetId: null,
  });
  partner.audioSync.setSongVolume(0);
  partner.audioSync.setEffectsVolume(0);
  partner.player.setClockFn(primary.audioSync.clockFn);
  return partner;
}

function attachPartner(primary: ReplaySession, partner: ReplaySession): void {
  partnerSession = partner;
  canvasStack.classList.add('dual');
  partnerFrame.classList.remove('hidden');
  canvas2.classList.remove('hidden');
  failOverlay.classList.add('hidden');
  playerLabel2.textContent = partner.username;
  playerLabel2.classList.remove('hidden');

  // Inherit primary's render options; fresh partner Renderers would otherwise diverge with defaults.
  // Mod toggles (modHidden/modFlashlight/...) deliberately NOT copied: each renderer seeds them
  // from its own replay's actual mods, and the two players' mods can legitimately differ.
  partner.renderer.options.showJudgement    = primary.renderer.options.showJudgement;
  partner.renderer.options.showFollowpoints = primary.renderer.options.showFollowpoints;
  partner.renderer.options.showKeyOverlay   = primary.renderer.options.showKeyOverlay;
  partner.renderer.options.showURBar        = primary.renderer.options.showURBar;
  partner.renderer.options.showModIcons     = primary.renderer.options.showModIcons;
  setPPCounterEnabled(partner.renderer, isPPCounterEnabled(primary.renderer));
  partner.renderer.options.backgroundDim    = primary.renderer.options.backgroundDim;
  partner.renderer.options.maniaScrollSpeed = primary.renderer.options.maniaScrollSpeed;
  partner.renderer.options.maniaUpscroll    = primary.renderer.options.maniaUpscroll;
  partner.renderer.options.taikoFlyingHits = primary.renderer.options.taikoFlyingHits;
  partner.audioOffsetMs                     = primary.audioOffsetMs;
  partner.renderer.options.audioOffsetMs    = primary.audioOffsetMs;

  matchScoreLeft.textContent  = '0';
  matchScoreRight.textContent = '0';
  matchScoreLeft.classList.remove('leading');
  matchScoreRight.classList.remove('leading');
  matchScoreDiffLeft.textContent  = '';
  matchScoreDiffRight.textContent = '';
  matchScoreStrip.classList.remove('hidden');

  partner.renderer.start();
  partner.player.seek(primary.player.currentTimeMs);
  if (primary.player.isPlaying) partner.player.play();
}

/** Static-paint canvas2 for fail-stub partners. */
function paintStubBackground(target: HTMLCanvasElement, background: ImageBitmap | null, dim: number): void {
  const ctx = target.getContext('2d');
  if (ctx === null) return;
  ctx.clearRect(0, 0, target.width, target.height);
  if (background !== null) {
    // Aspect-cover: matches Renderer.ts.
    const scale = Math.max(target.width / background.width, target.height / background.height);
    const w = background.width * scale;
    const h = background.height * scale;
    ctx.drawImage(background, (target.width - w) / 2, (target.height - h) / 2, w, h);
  } else {
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, target.width, target.height);
  }
  const d = Math.max(0, Math.min(1, dim));
  ctx.fillStyle = `rgba(0, 0, 0, ${d})`;
  ctx.fillRect(0, 0, target.width, target.height);
}

function attachFailStub(primary: ReplaySession, username: string): void {
  partnerStub = { username };
  const dim = Number(optBgDim.value) / 100;
  paintStubBackground(canvas2, primary.shared.background, dim);

  canvasStack.classList.add('dual');
  partnerFrame.classList.remove('hidden');
  canvas2.classList.remove('hidden');
  failOverlay.classList.remove('hidden');
  playerLabel2.textContent = username;
  playerLabel2.classList.remove('hidden');
}

/** Pauses + clears refs but keeps builtPairs intact for instant re-activation. */
function deactivateMatchPair(): void {
  if (partnerSession === null && partnerStub === null && activeMatchPair === null && activeTeamMatch === null) return;

  if (partnerSession !== null) {
    partnerSession.player.pause();
    partnerSession.audioSync.pause();
    partnerSession.renderer.stop();
    partnerSession = null;
  }
  partnerStub = null;

  if (activeTeamMatch !== null) {
    for (const slot of activeTeamMatch.slots) {
      if (slot.session !== null) {
        slot.session.player.pause();
        slot.session.audioSync.pause();
        slot.session.renderer.stop();
      }
      // Slot 0 = canvas-frame-1; never fully hidden (hosts overview panel).
      const frame = slotFrames[slot.canvasIndex]!;
      const labelEl = slotPlayerLabels[slot.canvasIndex]!;
      const failEl  = slotFailOverlays[slot.canvasIndex]!;
      frame.style.gridColumn = '';
      frame.style.gridRow    = '';
      labelEl.classList.remove('team-red', 'team-blue');
      if (slot.canvasIndex > 0) {
        frame.classList.add('hidden');
        slotCanvases[slot.canvasIndex]!.classList.add('hidden');
      }
      labelEl.textContent = '';
      labelEl.classList.add('hidden');
      failEl.classList.add('hidden');
    }
    canvasStack.classList.remove('team-2v2', 'team-4v4');
    activeTeamMatch = null;
  }

  if (activeMatchPair !== null) {
    const primary = activeMatchPair.primary;
    primary.player.pause();
    primary.audioSync.pause();
    primary.renderer.stop();
    activeMatchPair = null;
    activePlayer    = null;
    activeRenderer  = null;
    activeAudioSync = null;
  }

  canvasStack.classList.remove('dual');
  partnerFrame.classList.add('hidden');
  canvas2.classList.add('hidden');
  failOverlay.classList.add('hidden');
  playerLabel2.textContent = '';
  playerLabel2.classList.add('hidden');

  matchScoreStrip.classList.add('hidden');
  matchScoreLeft.classList.remove('leading');
  matchScoreRight.classList.remove('leading');
  matchScoreDiffLeft.textContent  = '';
  matchScoreDiffRight.textContent = '';

  hideAudioSwap();
  renderTabs();
  refreshShareAtTimeBtn();
}

/** 2v2: red col 1, blue col 2. 4v4: red cols 1-2, blue cols 3-4 (each a 2×2). */
function teamSlotLayout(idxInTeam: number, team: TeamColor, teamSize: number): { col: number; row: number } {
  if (teamSize === 2) {
    return { col: team === 'red' ? 1 : 2, row: idxInTeam + 1 };
  }
  const baseCol = team === 'red' ? 1 : 3;
  return {
    col: baseCol + (idxInTeam % 2),
    row: Math.floor(idxInTeam / 2) + 1,
  };
}

/** Counts scorers (not details.teams which is overinclusive); requires team_versus + symmetric 2 or 4. */
function shouldUseTeamMatch(match: MatchData, item: MatchPlaylistItem): boolean {
  if (match.roomType !== 'team_versus') return false;
  let red = 0, blue = 0;
  for (const s of item.scores) {
    const t = item.teams.get(s.userId);
    if (t === 'red')  red++;
    else if (t === 'blue') blue++;
  }
  return red === blue && (red === 2 || red === 4);
}

async function buildTeamMatch(
  matchSession: MatchSession,
  item: MatchPlaylistItem,
  skinName: string,
): Promise<BuiltTeamMatch> {
  if (item.teams.size === 0) throw new Error('buildTeamMatch: item has no team assignments');
  if (!isLoggedIn()) throw new Error('log in on the Match tab to download match replays');

  // Pin order to match-level player order so the same player stays on the same canvas across maps.
  const playerOrder = new Map<number, number>();
  matchSession.match.players.forEach((p, i) => playerOrder.set(p.userId, i));
  const cmpByPlayerOrder = (a: MatchScore, b: MatchScore) =>
    (playerOrder.get(a.userId) ?? 999) - (playerOrder.get(b.userId) ?? 999);

  const redScores:  MatchScore[] = [];
  const blueScores: MatchScore[] = [];
  for (const s of item.scores) {
    const t = item.teams.get(s.userId);
    if (t === 'red')  redScores.push(s);
    else if (t === 'blue') blueScores.push(s);
  }
  redScores.sort(cmpByPlayerOrder);
  blueScores.sort(cmpByPlayerOrder);

  if (redScores.length === 0 || blueScores.length === 0) {
    throw new Error('team match has no players on one team');
  }
  if (redScores.length !== blueScores.length) {
    throw new Error(`asymmetric team sizes (${redScores.length}v${blueScores.length}) not supported`);
  }
  const teamSize = redScores.length;
  if (teamSize !== 2 && teamSize !== 4) {
    throw new Error(`team size ${teamSize} not supported (2v2 or 4v4 only)`);
  }

  // Canvas slot index == position in this array.
  interface OrderedEntry { team: TeamColor; score: MatchScore }
  const orderedScores: OrderedEntry[] = [
    ...redScores.map(s => ({ team: 'red' as TeamColor,  score: s })),
    ...blueScores.map(s => ({ team: 'blue' as TeamColor, score: s })),
  ];

  // Primary = first with a replay; owns the shared decode.
  const primarySlotIdx = orderedScores.findIndex(o => o.score.hasReplay);
  if (primarySlotIdx < 0) throw new Error('no player has a replay for this map');

  // Defer token fetch until at least one buffer is missing.
  const oszCached     = cachedOsz(item.beatmapsetId);
  const replayCaches  = orderedScores.map(o => o.score.hasReplay ? scoreIdToOsr.get(o.score.scoreId) ?? null : null);
  const anyReplayMiss = orderedScores.some((o, i) => o.score.hasReplay && replayCaches[i] === null);
  const needsToken    = oszCached === undefined || anyReplayMiss;
  const token         = needsToken ? await getAccessToken() : '';

  const oszPromise: Promise<ArrayBuffer> = oszCached !== undefined
    ? Promise.resolve(oszCached)
    : downloadBeatmapSet(item.beatmapsetId).then(r => r.buffer);
  const replayPromises: Array<Promise<ArrayBuffer | null>> = orderedScores.map((o, i) => {
    if (!o.score.hasReplay) return Promise.resolve<ArrayBuffer | null>(null);
    const cached = replayCaches[i] ?? null;
    if (cached !== null) return Promise.resolve<ArrayBuffer | null>(cached);
    return downloadReplay(o.score.scoreId, token);
  });
  const [osz, ...replays] = await Promise.all([oszPromise, ...replayPromises]) as [ArrayBuffer, ...Array<ArrayBuffer | null>];

  if (oszCached === undefined) setIdToOsz.set(item.beatmapsetId, { buffer: osz, hasVideo: false });
  for (let i = 0; i < orderedScores.length; i++) {
    const buf = replays[i] ?? null;
    if (buf !== null && (replayCaches[i] ?? null) === null) {
      scoreIdToOsr.set(orderedScores[i]!.score.scoreId, buf);
    }
  }

  const dedupKeyBase = `match-team:${matchSession.id}:${item.playlistItemId}:${skinName}`;
  const primaryOsr   = replays[primarySlotIdx]!;
  const primaryEntry = orderedScores[primarySlotIdx]!;
  const primarySession = await buildSession({
    canvas:       slotCanvases[primarySlotIdx]!,
    storyboard:   false,
    osrBuffer:    primaryOsr,
    oszBuffer:    osz,
    skinName,
    dedupKey:     `${dedupKeyBase}:primary`,
    scoreId:      primaryEntry.score.scoreId,
    beatmapsetId: item.beatmapsetId,
    ownsRawCache: false,
  });

  // Other real slots reuse primary.shared, muted, clock-locked. Non-replays become fail-stubs.
  const slots: TeamSlot[] = [];
  for (let idx = 0; idx < orderedScores.length; idx++) {
    const entry = orderedScores[idx]!;
    const team  = entry.team;
    const idxInTeam = team === 'red' ? idx : idx - teamSize;
    const { col, row } = teamSlotLayout(idxInTeam, team, teamSize);

    let session: ReplaySession | null = null;
    let stubUsername: string | null = null;

    if (idx === primarySlotIdx) {
      session = primarySession;
    } else if (entry.score.hasReplay) {
      const osr = replays[idx]!;
      session = await buildSession({
        canvas:       slotCanvases[idx]!,
        storyboard:   false,
        osrBuffer:    osr,
        shared:       primarySession.shared,
        skinName,
        dedupKey:     `${dedupKeyBase}:slot${idx}`,
        scoreId:      null,
        beatmapsetId: null,
      });
      session.audioSync.setSongVolume(0);
      session.audioSync.setEffectsVolume(0);
      session.player.setClockFn(primarySession.audioSync.clockFn);
    } else {
      stubUsername = entry.score.username;
    }

    slots.push({
      team,
      canvasIndex: idx,
      gridColumn: col,
      gridRow:    row,
      username:   entry.score.username,
      session, stubUsername,
    });
  }

  return { kind: 'team' as const, slots, primarySlotIdx, audioSlotIdx: primarySlotIdx };
}

/** Real slots: grid cell + renderer start + clock-seek. Stubs: background paint + overlay. */
function mountTeamSlot(slot: TeamSlot, primary: ReplaySession): void {
  const frame  = slotFrames[slot.canvasIndex]!;
  const cnv    = slotCanvases[slot.canvasIndex]!;
  const label  = slotPlayerLabels[slot.canvasIndex]!;
  const failEl = slotFailOverlays[slot.canvasIndex]!;

  frame.style.gridColumn = String(slot.gridColumn);
  frame.style.gridRow    = String(slot.gridRow);
  frame.classList.remove('hidden');
  cnv.classList.remove('hidden');

  label.textContent = slot.username;
  label.classList.remove('hidden');
  label.classList.toggle('team-red',  slot.team === 'red');
  label.classList.toggle('team-blue', slot.team === 'blue');

  if (slot.session !== null) {
    failEl.classList.add('hidden');
    const session = slot.session;
    // Inherit primary render options + offset; identify against shared clock.
    session.renderer.options.showJudgement    = primary.renderer.options.showJudgement;
    session.renderer.options.showFollowpoints = primary.renderer.options.showFollowpoints;
    session.renderer.options.showKeyOverlay   = primary.renderer.options.showKeyOverlay;
    session.renderer.options.showURBar        = primary.renderer.options.showURBar;
    session.renderer.options.showModIcons     = primary.renderer.options.showModIcons;
    setPPCounterEnabled(session.renderer, isPPCounterEnabled(primary.renderer));
    session.renderer.options.backgroundDim    = primary.renderer.options.backgroundDim;
    session.renderer.options.maniaScrollSpeed = primary.renderer.options.maniaScrollSpeed;
    session.renderer.options.maniaUpscroll    = primary.renderer.options.maniaUpscroll;
    session.renderer.options.taikoFlyingHits = primary.renderer.options.taikoFlyingHits;
    session.audioOffsetMs                     = primary.audioOffsetMs;
    session.renderer.options.audioOffsetMs    = primary.audioOffsetMs;

    if (session !== primary) {
      session.renderer.start();
      session.player.seek(primary.player.currentTimeMs);
      if (primary.player.isPlaying) session.player.play();
    }

  } else if (slot.stubUsername !== null) {
    const dim = Number(optBgDim.value) / 100;
    paintStubBackground(cnv, primary.shared.background, dim);
    failEl.classList.remove('hidden');
  }
}

/** Mounts only; no parsing or decoding. */
function activateBuiltTeamMatch(
  matchSession: MatchSession,
  item: MatchPlaylistItem,
  entry: BuiltTeamMatch,
  opts: { autoplay?: boolean } = {},
): void {
  const autoplay = opts.autoplay ?? true;
  const primarySlot = entry.slots[entry.primarySlotIdx]!;
  const primary = primarySlot.session!;
  activeMatchPair  = { matchSession, item, primary };
  activeTeamMatch  = entry;
  activePlayer     = primary.player;
  activeRenderer   = primary.renderer;

  // Every slot's Player clock-locks to the audible side's AudioSync for shared presentation clock.
  const audioSlot = entry.slots[entry.audioSlotIdx]!;
  const audible = audioSlot.session ?? primary;
  activeAudioSync = audible.audioSync;

  const musicVol   = Number(optMusicVol.value)   / 100;
  const effectsVol = Number(optEffectsVol.value) / 100;
  for (const slot of entry.slots) {
    if (slot.session === null) continue;
    const isAudible = slot === audioSlot;
    slot.session.audioSync.setSongVolume   (isAudible ? musicVol   : 0);
    slot.session.audioSync.setEffectsVolume(isAudible ? effectsVol : 0);
    slot.session.player.setClockFn(audible.audioSync.clockFn);
  }

  optJudgement.checked    = primary.renderer.options.showJudgement;
  optFollowpoints.checked = primary.renderer.options.showFollowpoints;
  optKeyOverlay.checked   = primary.renderer.options.showKeyOverlay;
  optURBar.checked        = primary.renderer.options.showURBar;
  optModIcons.checked     = primary.renderer.options.showModIcons;
  optPP.checked           = isPPCounterEnabled(primary.renderer);
  optModHidden.checked     = primary.renderer.options.modHidden;
  optModFlashlight.checked = primary.renderer.options.modFlashlight;
  optModFadeIn.checked     = primary.renderer.options.modFadeIn;
  optModCover.checked      = primary.renderer.options.modCover;
  optUpscroll.checked      = primary.renderer.options.maniaUpscroll;
  optFlyingHits.checked = primary.renderer.options.taikoFlyingHits;
  optBgDim.value          = String(Math.round(primary.renderer.options.backgroundDim * 100));
  optBgDimValue.textContent = `${optBgDim.value}%`;
  optManiaScroll.value      = String(primary.renderer.options.maniaScrollSpeed);
  optManiaScrollValue.textContent = String(primary.renderer.options.maniaScrollSpeed);
  optMusicVol.value          = String(Math.round(primary.musicVol * 100));
  optMusicVolValue.textContent = `${optMusicVol.value}%`;
  optEffectsVol.value        = String(Math.round(primary.effectsVol * 100));
  optEffectsVolValue.textContent = `${optEffectsVol.value}%`;
  optBeatmapHitsounds.checked = primary.beatmapHitsounds;
  primary.renderer.options.audioOffsetMs = primary.audioOffsetMs;
  updateOffsetReadout(primary.audioOffsetMs);

  // `side` pins color: red slots use the 'primary' (red) class, blue use 'partner' (blue).
  const markerSources: ScrubMarkerSource[] = [];
  for (const slot of entry.slots) {
    if (slot.session === null) continue;
    markerSources.push({
      results: slot.session.renderer.hitResults,
      introOffsetMs: slot.session.introOffsetMs,
      presentationDurationMs: slot.session.timeMapper.presentationDurationMs,
      speed: slot.session.speed,
      side: slot.team === 'red' ? 'primary' : 'partner',
    });
  }
  renderScrubMarkers(markerSources);
  resetClipRange();

  const teamSize = entry.slots.length / 2;
  canvasStack.classList.remove('dual');
  canvasStack.classList.toggle('team-2v2', teamSize === 2);
  canvasStack.classList.toggle('team-4v4', teamSize === 4);
  matchOverview.classList.add('hidden');
  controls.classList.remove('hidden');
  renderOptions.classList.remove('hidden');
  renderOptions.classList.toggle('taiko-mode', primary.mode === 1);
  renderOptions.classList.toggle('mania-mode', primary.mode === 3);
  renderOptions.classList.toggle('catch-mode', primary.mode === 2);

  // Start primary first so others have a stable clock to seek against.
  primary.renderer.start();
  for (const slot of entry.slots) mountTeamSlot(slot, primary);

  matchScoreLeft.textContent  = '0';
  matchScoreRight.textContent = '0';
  matchScoreLeft.classList.remove('leading');
  matchScoreRight.classList.remove('leading');
  matchScoreDiffLeft.textContent  = '';
  matchScoreDiffRight.textContent = '';
  matchScoreStrip.classList.remove('hidden');

  showTeamAudioSwap(entry);

  setStatus(matchSession.label);
  setMatchStatus(matchSession.label);

  enterMatchView(matchSession.match, item);

  if (autoplay) {
    updatePlayPauseBtn(true);
    void doPlay();
  } else {
    updatePlayPauseBtn(false);
  }

  renderTabs();
  refreshShareAtTimeBtn();
}

// audioSide is persisted on BuiltPair so re-activation restores the same audio source.

function showAudioSwap(primary: ReplaySession, partner: ReplaySession, side: 'primary' | 'partner'): void {
  audioSwap.classList.remove('hidden');
  audioSwap.classList.remove('team-mode', 'team-2v2', 'team-4v4');
  audioSwapLeft.classList.remove('hidden');
  audioSwapRight.classList.remove('hidden');
  audioSwapBtn.classList.remove('hidden');
  for (const b of teamAudioSwapBtns) b.remove();
  teamAudioSwapBtns = [];
  audioSwapLeftName.textContent  = primary.username;
  audioSwapRightName.textContent = partner.username;
  updateAudioSwapHighlight(side);
}

function hideAudioSwap(): void {
  audioSwap.classList.add('hidden');
  audioSwap.classList.remove('team-mode', 'team-2v2', 'team-4v4');
  for (const b of teamAudioSwapBtns) b.remove();
  teamAudioSwapBtns = [];
  audioSwapLeft.classList.remove('hidden');
  audioSwapRight.classList.remove('hidden');
  audioSwapBtn.classList.remove('hidden');
}

function updateAudioSwapHighlight(side: 'primary' | 'partner'): void {
  audioSwapLeft.classList.toggle('active',  side === 'primary');
  audioSwapRight.classList.toggle('active', side === 'partner');
}

/** Grid mirrors canvas layout; buttons tracked in teamAudioSwapBtns for clean removal. */
let teamAudioSwapBtns: HTMLButtonElement[] = [];
function showTeamAudioSwap(entry: BuiltTeamMatch): void {
  audioSwap.classList.remove('hidden');
  audioSwap.classList.add('team-mode');
  audioSwap.classList.remove('team-2v2', 'team-4v4');
  const teamSize = entry.slots.length / 2;
  audioSwap.classList.add(teamSize === 2 ? 'team-2v2' : 'team-4v4');
  // `⇆` swap-direction button has no meaning with 4+ slots.
  audioSwapLeft.classList.add('hidden');
  audioSwapRight.classList.add('hidden');
  audioSwapBtn.classList.add('hidden');
  for (const b of teamAudioSwapBtns) b.remove();
  teamAudioSwapBtns = [];

  for (let i = 0; i < entry.slots.length; i++) {
    const slot = entry.slots[i]!;
    if (slot.session === null) continue;
    const idxInTeam = slot.team === 'red' ? i : i - teamSize;
    const layout = teamSlotLayout(idxInTeam, slot.team, teamSize);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `audio-swap-note team-${slot.team}`;
    btn.title = `Play ${slot.username}'s audio`;
    btn.dataset.slotIdx = String(i);
    btn.style.gridColumn = String(layout.col);
    btn.style.gridRow    = String(layout.row);
    if (i === entry.audioSlotIdx) btn.classList.add('active');
    const icon = document.createElement('span');
    icon.className = 'audio-swap-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = '&#9835;';
    const name = document.createElement('span');
    name.className = 'audio-swap-name';
    name.textContent = slot.username;
    btn.append(icon, name);
    btn.addEventListener('click', () => { void setTeamAudioSlot(i); });
    teamAudioSwapBtns.push(btn);
    audioSwap.appendChild(btn);
  }
}

/** Flip audio to a specific slot in the active team match. Mirrors
 *  `setMatchAudioSide` for 1v1: pause, flip gains, rewire every Player's
 *  clockFn to the new AudioSync, resume if it was playing. */
async function setTeamAudioSlot(newSlotIdx: number): Promise<void> {
  const entry = activeTeamMatch;
  if (entry === null) return;
  if (newSlotIdx === entry.audioSlotIdx) return;
  const newSlot = entry.slots[newSlotIdx];
  if (newSlot === undefined || newSlot.session === null) return;
  const oldSlot = entry.slots[entry.audioSlotIdx]!;
  if (oldSlot.session === null) return;

  const oldSync = oldSlot.session.audioSync;
  const newSync = newSlot.session.audioSync;

  const wasPlaying = activePlayer?.isPlaying ?? false;
  const presMs    = activePlayer?.currentTimeMs ?? 0;

  oldSync.pause();
  for (const slot of entry.slots) {
    if (slot.session !== null) slot.session.player.pause();
  }

  activeAudioSync = newSync;
  const musicVol   = Number(optMusicVol.value)   / 100;
  const effectsVol = Number(optEffectsVol.value) / 100;
  for (const slot of entry.slots) {
    if (slot.session === null) continue;
    const isAudible = slot === newSlot;
    slot.session.audioSync.setSongVolume   (isAudible ? musicVol   : 0);
    slot.session.audioSync.setEffectsVolume(isAudible ? effectsVol : 0);
    slot.session.player.setClockFn(newSync.clockFn);
  }

  entry.audioSlotIdx = newSlotIdx;

  for (const btn of teamAudioSwapBtns) {
    const idxAttr = btn.dataset.slotIdx;
    if (idxAttr === undefined) continue;
    btn.classList.toggle('active', Number(idxAttr) === newSlotIdx);
  }

  if (wasPlaying) {
    await newSync.playFrom(presMs);
    for (const slot of entry.slots) {
      if (slot.session === null) continue;
      slot.session.player.seek(presMs);
      slot.session.player.play();
    }
  } else {
    await newSync.seekTo(presMs);
    for (const slot of entry.slots) {
      if (slot.session === null) continue;
      slot.session.player.seek(presMs);
    }
  }
}

function currentBuiltMatchEntry(): BuiltMatchEntry | null {
  if (activeMatchPair === null) return null;
  return activeMatchPair.matchSession.builtPairs.get(activeMatchPair.item.playlistItemId) ?? null;
}

/** Narrowed version — returns null when the active entry is a team match. */
function currentBuiltPair(): BuiltPair | null {
  const entry = currentBuiltMatchEntry();
  return entry !== null && entry.kind === 'pair' ? entry : null;
}

async function setMatchAudioSide(side: 'primary' | 'partner'): Promise<void> {
  if (activeMatchPair === null || partnerSession === null) return;
  const pair = currentBuiltPair();
  if (pair === null || pair.partner === null) return;
  if (pair.audioSide === side) return;

  const primary = pair.primary;
  const partner = pair.partner;
  const newSync = side === 'primary' ? primary.audioSync : partner.audioSync;
  const oldSync = side === 'primary' ? partner.audioSync : primary.audioSync;

  const wasPlaying = activePlayer?.isPlaying ?? false;
  const presMs = activePlayer?.currentTimeMs ?? 0;

  oldSync.pause();
  primary.player.pause();
  partner.player.pause();

  // Use live slider value (not replay.musicVol) so the new side honors current UI state.
  activeAudioSync = newSync;
  const musicVol   = Number(optMusicVol.value)   / 100;
  const effectsVol = Number(optEffectsVol.value) / 100;
  oldSync.setSongVolume(0);
  oldSync.setEffectsVolume(0);
  newSync.setSongVolume(musicVol);
  newSync.setEffectsVolume(effectsVol);

  primary.player.setClockFn(newSync.clockFn);
  partner.player.setClockFn(newSync.clockFn);

  pair.audioSide = side;
  updateAudioSwapHighlight(side);

  if (wasPlaying) {
    await newSync.playFrom(presMs);
    primary.player.seek(presMs);
    partner.player.seek(presMs);
    primary.player.play();
    partner.player.play();
  } else {
    // seekTo to keep newSync's pres-time snapshot fresh so clockFn returns the right value next tick.
    await newSync.seekTo(presMs);
    primary.player.seek(presMs);
    partner.player.seek(presMs);
  }
}

function toggleMatchAudioSide(): void {
  const pair = currentBuiltPair();
  if (pair === null) return;
  setMatchAudioSide(pair.audioSide === 'primary' ? 'partner' : 'primary');
}

audioSwapBtn.addEventListener('click',   toggleMatchAudioSide);
audioSwapLeft.addEventListener('click',  () => setMatchAudioSide('primary'));
audioSwapRight.addEventListener('click', () => setMatchAudioSide('partner'));

function enterMatchView(match: MatchData, item: MatchPlaylistItem): void {
  document.body.classList.add('match-view');
  // Rate is a solo-only control (hidden in match view via CSS): match playback is always 1×.
  // Runs after the pair/team sessions are wired into the active refs, so this also corrects
  // sessions built moments ago with a non-1 build-site userRate; the global reset makes any
  // later in-match builds (skin swaps, cached-pair reloads) start at 1. Resetting the slider
  // value also defuses a pending commitPlaybackRate throttle tick (it reads value 100 → no-op).
  if (playbackRate !== 1) {
    playbackRate = 1;
    optRate.value = '100';
    optRateValue.textContent = '1.00x';
    forEachActiveReplaySession(s => s.audioSync.setUserRate(1));
  }
  matchStrip.classList.remove('hidden');
  matchStripRoom.textContent = match.name;
  matchStripCurrent.textContent = `${item.artist} – ${item.title} [${item.version}]`;
}

function exitMatchView(): void {
  document.body.classList.remove('match-view');
  matchStrip.classList.add('hidden');
}

matchStripBack.addEventListener('click', () => {
  const s = activeSession;
  deactivateMatchPair();
  exitMatchView();
  if (s !== null && s.kind === 'match') showMatchOverview(s);
});

function renderTabs(): void {
  replayTabsEl.replaceChildren();

  for (const session of sessions) {
    const tab = document.createElement('div');
    tab.className = 'replay-tab' + (session === activeSession ? ' active' : '');
    tab.tabIndex = 0;
    tab.addEventListener('click', (ev) => {
      if ((ev.target as HTMLElement).classList.contains('replay-tab-remove')) return;
      activateSession(session);
    });
    tab.addEventListener('keydown', (ev) => {
      const tabs = Array.from(replayTabsEl.children) as HTMLElement[];
      const idx = tabs.indexOf(tab);
      switch (ev.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          ev.preventDefault();
          tabs[Math.min(idx + 1, tabs.length - 1)]?.focus();
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          ev.preventDefault();
          tabs[Math.max(idx - 1, 0)]?.focus();
          break;
        case 'Home':
          ev.preventDefault();
          tabs[0]?.focus();
          break;
        case 'End':
          ev.preventDefault();
          tabs[tabs.length - 1]?.focus();
          break;
        case 'Enter':
        case ' ':
          ev.preventDefault();
          activateSession(session);
          break;
        case 'Delete':
        case 'Backspace':
          ev.preventDefault();
          removeSession(session);
          break;
      }
    });

    const label = document.createElement('span');
    label.className = 'replay-tab-label';
    label.textContent = session.label;

    const shareUrl = shareUrlForSession(session);
    tab.appendChild(label);
    if (shareUrl !== null) {
      const shareBtn = document.createElement('button');
      shareBtn.className = 'replay-tab-share';
      shareBtn.setAttribute('aria-label', 'Copy share link');
      shareBtn.title = 'Copy share link';
      shareBtn.innerHTML = SHARE_LINK_ICON_SVG;
      shareBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        void copyShareLink(shareBtn, shareUrl);
      });
      tab.appendChild(shareBtn);
    }

    const removeBtn = document.createElement('button');
    removeBtn.className = 'replay-tab-remove';
    removeBtn.setAttribute('aria-label', 'Remove replay');
    removeBtn.textContent = '×';
    removeBtn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      removeSession(session);
    });

    tab.appendChild(removeBtn);
    replayTabsEl.appendChild(tab);
  }
}

/** Non-default skins are dropped to avoid silently-missing-skin errors. */
function shareUrlForSession(session: Session): string | null {
  const base = window.location.origin + '/';
  if (session.kind === 'replay') {
    const skinParam = isDefaultSkin(session.skinName) ? `&skin=${encodeURIComponent(session.skinName)}` : '';
    if (session.auto !== null) {
      const modsParam = session.auto.mods.length > 0 ? `&mods=${session.auto.mods.join('')}` : '';
      return `${base}#auto=${session.auto.beatmapId}${modsParam}${skinParam}`;
    }
    if (session.scoreId == null) return null;   // manual uploads have no sharable URL
    const rulesetPrefix = session.scoreRuleset !== null ? `${session.scoreRuleset}/` : '';
    return `${base}#score=${rulesetPrefix}${session.scoreId}${skinParam}`;
  }
  const roomId = session.match.roomId;
  const activePair = activeMatchPair?.matchSession === session ? activeMatchPair : null;
  if (activePair === null) return `${base}#match=${roomId}`;
  const skinName  = activePair.primary.skinName;
  const skinParam = isDefaultSkin(skinName) ? `&skin=${encodeURIComponent(skinName)}` : '';
  return `${base}#match=${roomId}&map=${activePair.item.playlistItemId}${skinParam}`;
}

function builtEntrySkinName(entry: BuiltMatchEntry | undefined): string | null {
  if (entry === undefined) return null;
  if (entry.kind === 'pair') return entry.primary.skinName;
  return entry.slots[entry.primarySlotIdx]?.session?.skinName ?? null;
}

function shareUrlAtCurrentTime(): string | null {
  const player = activePlayer;
  if (player === null) return null;
  const tMs = Math.max(0, Math.round(player.currentTimeMs));

  if (activeMatchPair !== null) {
    const { matchSession, item, primary } = activeMatchPair;
    const skinParam = isDefaultSkin(primary.skinName) ? `&skin=${encodeURIComponent(primary.skinName)}` : '';
    return `${window.location.origin}/#match=${matchSession.match.roomId}&map=${item.playlistItemId}&t=${tMs}${skinParam}`;
  }

  const s = activeSession;
  if (s === null || s.kind !== 'replay') return null;
  const skinParam = isDefaultSkin(s.skinName) ? `&skin=${encodeURIComponent(s.skinName)}` : '';
  if (s.auto !== null) {
    const modsParam = s.auto.mods.length > 0 ? `&mods=${s.auto.mods.join('')}` : '';
    return `${window.location.origin}/#auto=${s.auto.beatmapId}${modsParam}&t=${tMs}${skinParam}`;
  }
  if (s.scoreId == null) return null;
  const rulesetPrefix = s.scoreRuleset !== null ? `${s.scoreRuleset}/` : '';
  return `${window.location.origin}/#score=${rulesetPrefix}${s.scoreId}&t=${tMs}${skinParam}`;
}

function refreshShareAtTimeBtn(): void {
  const visible = shareUrlAtCurrentTime() !== null;
  shareAtTimeBtn.classList.toggle('hidden', !visible);
}

shareAtTimeBtn.addEventListener('click', (ev) => {
  ev.stopPropagation();
  const url = shareUrlAtCurrentTime();
  if (url !== null) void copyShareLink(shareAtTimeBtn, url);
});

// Octicons "link" glyph (16x16); uses fill="currentColor" via CSS so it tracks the button's
// text color/hover state, unlike the emoji it replaces.
const SHARE_LINK_ICON_SVG = '<svg class="share-link-icon" viewBox="0 0 16 16" aria-hidden="true"><path d="M4.715 6.542 3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.002 1.002 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z"></path><path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 1 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 1 0-4.243-4.243L6.586 4.672z"></path></svg>';

async function copyShareLink(btn: HTMLButtonElement, url: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
    const prev = btn.innerHTML;
    btn.innerHTML = '✓';
    setTimeout(() => { btn.innerHTML = prev; }, 1200);
  } catch {
    window.prompt('Copy share link:', url);
  }
}

function removeSession(session: Session): void {
  const idx = sessions.indexOf(session);
  if (idx < 0) return;

  const wasActive = activeSession === session;
  // Active session removal: deactivate the pair here to handle the no-neighbor path; teardown happens in destroyMatchSession.
  if (wasActive && (partnerSession !== null || activeMatchPair !== null)) deactivateMatchPair();

  sessions.splice(idx, 1);

  if (wasActive) {
    activeSession   = null;
    activePlayer    = null;
    activeRenderer  = null;
    activeAudioSync = null;
  }

  destroySession(session);

  if (wasActive) {
    const neighbor = sessions[idx - 1] ?? sessions[idx] ?? null;
    if (neighbor !== null) {
      activateSession(neighbor);
    } else {
      canvas.classList.add('hidden');
      controls.classList.add('hidden');
      renderOptions.classList.add('hidden');
      matchOverview.classList.add('hidden');
      playerLabel1.textContent = '';
      playerLabel1.classList.add('hidden');
      scrubMarkers.replaceChildren();
      exitMatchView();
      updatePlayPauseBtn(false);
      setStatus('');
      refreshShareAtTimeBtn();
    }
  }

  renderTabs();
}

async function loadAndAddSession(
  osrBuffer: ArrayBuffer,
  oszBuffer: ArrayBuffer | undefined,
  skinName: string,
): Promise<void> {
  const dedupKey = md5(new Uint8Array(osrBuffer));
  const existing = sessions.find(s => s.kind === 'replay' && s.dedupKey === dedupKey && s.skinName === skinName);
  if (existing !== undefined) {
    activateSession(existing);
    return;
  }

  const replayData = await parseReplayCached(osrBuffer, dedupKey);

  // Only a .osr supplied → resolve the beatmapset from the public mirror md5 index (no login)
  // and download the .osz. A supplied .osz skips this and stays beatmapsetId-less as before.
  let beatmapsetId: number | null = null;
  let oszFromCache = false;
  let oszHasVideo = false;
  if (oszBuffer === undefined) {
    const { beatmapHash } = replayData;
    let setId = hashToSetId.get(beatmapHash);
    if (setId === undefined) {
      setId = await lookupBeatmapsetIdByHash(beatmapHash);
      hashToSetId.set(beatmapHash, setId);
      persistHashLookup();
    }
    beatmapsetId = setId;
    const osz = await getOsz(setId);
    oszBuffer = osz.buffer;
    oszFromCache = osz.fromCache;
    oszHasVideo = osz.hasVideo;
  }

  const session = await buildSession({
    canvas,
    osrBuffer,
    oszBuffer,
    replayData,
    skinName,
    dedupKey,
    scoreId: null,
    beatmapsetId,
  });

  // Cache the downloaded .osz only after a clean build (mirrors the online path's discipline).
  if (beatmapsetId !== null && !oszFromCache) setIdToOsz.set(beatmapsetId, { buffer: oszBuffer, hasVideo: oszHasVideo });

  sessions.push(session);
  activateSession(session);
}

loadBtn.addEventListener('click', async () => {
  try {
    const osrFile = osrInput.files?.[0];
    const oszFile = oszInput.files?.[0];

    if (!osrFile) { setStatus('Please select a .osr replay file.', true); return; }

    loadBtn.disabled = true;
    setStatus('');
    setLoadProgress(true);

    const osrBuffer = await readFileAsArrayBuffer(osrFile);
    const oszBuffer = oszFile ? await readFileAsArrayBuffer(oszFile) : undefined;

    const skinName = await consumeSkinUpload(oskInput, uploadDefaultSkinSelect);

    await loadAndAddSession(osrBuffer, oszBuffer, skinName);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    setStatus(`Error: ${msg}`, true);
    console.error(err);
  } finally {
    setLoadProgress(false);
    loadBtn.disabled = false;
  }
});

// Generates a perfect ("osu!" AUTO) replay for a beatmap that has no .osr and plays it
// through the normal pipeline — the existing judge re-derives the SS (docs/autoplay.md A1).
// All four rulesets + mods. Caller supplies a parsed beatmap ref + the chosen mod acronyms
// (the Auto-tab button reads the DOM; the #auto= deep-link and the iframe loadAuto path supply
// them directly), so the core is fully DOM-decoupled.
async function loadAutoSession(
  ref: BeatmapRef,
  acronyms: string[],
  skinName: string,
  opts: { autoplay?: boolean } = {},
): Promise<void> {
  const autoplay = opts.autoplay ?? true;

  // Canonical .osu straight from osu! (login-free); its md5 is the live checksum, which
  // hash-matches the right diff inside the mirror .osz (and recovers a stale one below).
  const osuBytes    = new Uint8Array(await downloadBeatmapOsu(ref.beatmapId));
  const beatmapHash = md5(osuBytes);
  const beatmap     = parseBeatmap(new TextDecoder('utf-8').decode(osuBytes));

  // Acronyms filtered to this map's mode (off-mode ones ignored). The bitmask is part of the
  // dedup key so each mod combo gets its own session/tab; `effective` is what the share link stores.
  const { mods, cover, effective } = modsForAcronyms(acronyms, beatmap.mode);

  const dedupKey = `auto:${beatmapHash}:${mods}${cover ? ':co' : ''}`;
  const existing = sessions.find(s => s.kind === 'replay' && s.dedupKey === dedupKey && s.skinName === skinName);
  if (existing !== undefined) { activateSession(existing); return; }

  // computeModDifficulty reads only mods + map difficulty (not frames), so a frameless stub
  // yields the modded preempt/radius/windows. Stacking must be applied before generating so the
  // cursor lands on stacked centres (matching the judge's effectiveX) → offset-0 hits → SS.
  const modDiff = computeModDifficulty(beatmap, synthesizeAutoReplay(beatmap, beatmapHash, [], mods));
  // mania + catch ignore stacking (matches buildSession's skip for modes 3 and 2).
  if (beatmap.mode !== 3 && beatmap.mode !== 2) applyStacking(beatmap, modDiff);
  let autoFrames;
  if (beatmap.mode === 3) {
    autoFrames = generateManiaAutoReplay(beatmap, modDiff);
  } else if (beatmap.mode === 1) {
    autoFrames = generateTaikoAutoReplay(beatmap, modDiff);
  } else if (beatmap.mode === 2) {
    // Catch judges positionally, so the generator needs the converted palpable objects with
    // their byte-exact EffectiveX + hyperdash flags (buildSession's catch ruleset recomputes the
    // same values from the hash-matched .osz copy → the catcher lands where the judge expects).
    const objects = convertBeatmapToCatch(beatmap, modDiff);
    applyPositionOffsets(objects, beatmap, modDiff);
    autoFrames = generateCatchAutoReplay(objects, modDiff);
  } else {
    autoFrames = generateStdAutoReplay(beatmap, modDiff);
  }
  const replayData = synthesizeAutoReplay(beatmap, beatmapHash, autoFrames, mods);

  // beatmapsets/{setId} fragment is authoritative; bare beatmaps/{id} links resolve via the md5 index.
  let setId = ref.setId ?? hashToSetId.get(beatmapHash) ?? null;
  if (setId === null) setId = await lookupBeatmapsetIdByHash(beatmapHash);
  hashToSetId.set(beatmapHash, setId);
  persistHashLookup();

  const osz = await getOsz(setId);
  const oszBuffer = osz.buffer;

  const session = await buildSession({
    canvas,
    oszBuffer,
    replayData,
    skinName,
    dedupKey,
    scoreId: null,
    beatmapsetId: setId,
    isAuto: true,
    auto: { beatmapId: ref.beatmapId, mods: effective },
    fetchOsuOverride: async () => osuBytes,
  });

  if (!osz.fromCache) setIdToOsz.set(setId, { buffer: oszBuffer, hasVideo: osz.hasVideo });

  // Cover (CO) has no stable mod bit, so it can't ride the bitmask → modDiff path. It's a
  // purely-visual mania mod, so apply it as a render option (matching the user-flippable
  // toggle); modDiff carries the lazer defaults (coverage 0.5, AlongScroll) the renderer reads.
  if (cover && beatmap.mode === 3) session.renderer.options.modCover = true;

  // Boot-time deep-links pass autoplay:false to avoid suspended-ctx zombie playback (see loadScoreById).
  if (!autoplay) session.wasPlaying = false;
  sessions.push(session);
  activateSession(session);
}

autoBtn.addEventListener('click', async () => {
  try {
    autoBtn.disabled = true;
    setStatus('');
    setLoadProgress(true);

    const ref = parseBeatmapRef(autoInput.value);
    if (ref === null) {
      setStatus('Unrecognized beatmap link — use a beatmapset URL like .../beatmapsets/4651#osu/24722.', true);
      return;
    }
    const skinName = await consumeSkinUpload(autoOskInput, autoDefaultSkinSelect);
    await loadAutoSession(ref, selectedAutoAcronyms(), skinName);
  } catch (err) {
    setStatus(loadErrorMessage(err), true);
    console.error(err);
  } finally {
    setLoadProgress(false);
    autoBtn.disabled = false;
  }
});

function setMatchStatus(msg: string, isError = false): void {
  matchStatusEl.textContent = msg;
  matchStatusEl.style.color = isError ? '#ff6b6b' : '#e0e0e0';
}

/** Derives from top-2 players (by mapWins) so 1v1 reads as "A vs B"; falls back to room name. */
function computeMatchLabel(match: MatchData): string {
  const [p0, p1] = match.players;
  if (p0 === undefined || p1 === undefined) return match.name;
  return `${p0.username} vs ${p1.username} [${p0.mapWins} - ${p1.mapWins}]`;
}

async function fetchCoverBitmap(url: string | null): Promise<ImageBitmap | null> {
  if (url === null) return null;
  try {
    const resp = await fetch(url);
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await createImageBitmap(blob);
  } catch {
    return null;
  }
}

/** Caches .osz into setIdToOsz for later Load reuse; falls back to API cover thumbnail. */
async function fetchMatchBackground(item: MatchPlaylistItem): Promise<ImageBitmap | null> {
  try {
    let osz = cachedOsz(item.beatmapsetId);
    if (osz === undefined) {
      const res = await downloadBeatmapSet(item.beatmapsetId);
      osz = res.buffer;
      setIdToOsz.set(item.beatmapsetId, { buffer: osz, hasVideo: false });
    }
    const bg = await extractBeatmapBackground(osz, item.beatmapMd5);
    if (bg !== null) return bg;
  } catch (err) {
    console.warn('fetchMatchBackground: falling back to cover thumbnail', err);
  }
  return fetchCoverBitmap(item.coverUrl);
}

/** Missing entries (failed fetch) degrade to bare-username cards. */
async function fetchMatchProfiles(match: MatchData): Promise<Map<number, OsuUserProfile>> {
  const profiles = new Map<number, OsuUserProfile>();
  if (match.players.length === 0) return profiles;
  let token: string;
  try {
    token = await getAccessToken();
  } catch (err) {
    console.warn('fetchMatchProfiles: no access token, skipping profile fetch', err);
    return profiles;
  }
  const results = await Promise.allSettled(
    match.players.map(p => fetchUserProfile(p.userId, token)),
  );
  for (const [i, r] of results.entries()) {
    if (r.status === 'fulfilled') {
      profiles.set(r.value.userId, r.value);
    } else {
      const p = match.players[i];
      console.warn(`fetchMatchProfiles: failed for ${p?.username ?? 'user'}`, r.reason);
    }
  }
  return profiles;
}

/** Drives row button label: Watch (cached, instant) vs Load (build from scratch). */
function isPairBuilt(session: MatchSession, item: MatchPlaylistItem): boolean {
  return session.builtPairs.has(item.playlistItemId);
}

function renderMatchMapRows(container: HTMLElement, session: MatchSession): void {
  container.replaceChildren();
  // Pin to match-level player order so the same user stays on the same side across rows.
  const leftUserId  = session.match.players[0]?.userId;
  const rightUserId = session.match.players[1]?.userId;
  for (const [idx, item] of session.match.playlistItems.entries()) {
    const row = document.createElement('div');
    row.className = 'match-overview-map';
    if (activeMatchPair !== null
        && activeMatchPair.matchSession === session
        && activeMatchPair.item.playlistItemId === item.playlistItemId) {
      row.classList.add('current');
    }

    const title = document.createElement('div');
    title.className = 'match-overview-map-title';
    title.textContent = `${idx + 1}. ${item.artist} – ${item.title} [${item.version}]`;
    row.appendChild(title);

    const isTeam = session.match.roomType === 'team_versus' && item.teams.size > 0;
    const scores = document.createElement('span');
    scores.className = 'match-overview-map-scores';

    if (isTeam) {
      row.classList.add('team');
      const redScores  = item.scores.filter(s => item.teams.get(s.userId) === 'red');
      const blueScores = item.scores.filter(s => item.teams.get(s.userId) === 'blue');
      const sumOf = (ss: MatchScore[]) => ss.reduce((n, s) => n + (s.totalScore ?? 0), 0);
      const redSum  = sumOf(redScores);
      const blueSum = sumOf(blueScores);
      const redLeads  = redSum > blueSum;
      const blueLeads = blueSum > redSum;

      const teamSums = document.createElement('span');
      teamSums.className = 'match-overview-map-team-sums';
      const redSpan = document.createElement('span');
      redSpan.className = 'match-overview-map-team-sum team-red' + (redLeads  ? ' leading' : '');
      redSpan.textContent = `RED ${redSum.toLocaleString()}`;
      const vs = document.createElement('span');
      vs.className = 'match-overview-map-team-vs';
      vs.textContent = ' vs ';
      const blueSpan = document.createElement('span');
      blueSpan.className = 'match-overview-map-team-sum team-blue' + (blueLeads ? ' leading' : '');
      blueSpan.textContent = `BLUE ${blueSum.toLocaleString()}`;
      teamSums.append(redSpan, vs, blueSpan);
      scores.appendChild(teamSums);

      const players = document.createElement('div');
      players.className = 'match-overview-map-players';
      const appendSide = (ss: MatchScore[], team: TeamColor) => {
        const col = document.createElement('div');
        col.className = `match-overview-map-team-col team-${team}`;
        for (const s of ss) {
          const seg = document.createElement('span');
          seg.className = `match-overview-map-score team-${team}`;
          const modStr = s.mods.length > 0 ? ` +${s.mods.join('')}` : '';
          const scoreStr = s.totalScore !== null ? ` ${s.totalScore.toLocaleString()}` : '';
          seg.textContent = `${s.username} ${(s.accuracy * 100).toFixed(1)}% ${s.rank}${modStr}${scoreStr}`;
          col.appendChild(seg);
        }
        return col;
      };
      players.append(appendSide(redScores, 'red'), appendSide(blueScores, 'blue'));
      scores.appendChild(players);
    } else {
      const byLeft  = leftUserId  !== undefined ? item.scores.find(s => s.userId === leftUserId)  : undefined;
      const byRight = rightUserId !== undefined ? item.scores.find(s => s.userId === rightUserId) : undefined;
      const ordered = byLeft !== undefined && byRight !== undefined
        ? [byLeft, byRight]
        : item.scores;

      // Ties leave both sides un-bolded so 0/0 maps stay stable.
      let topScore: number | null = null;
      let topCount = 0;
      for (const s of ordered) {
        if (s.totalScore === null) continue;
        if (topScore === null || s.totalScore > topScore) { topScore = s.totalScore; topCount = 1; }
        else if (s.totalScore === topScore) topCount += 1;
      }
      const winnerScoreId = topScore !== null && topCount === 1
        ? ordered.find(s => s.totalScore === topScore)!.scoreId
        : null;

      for (const [i, s] of ordered.entries()) {
        if (i > 0) scores.appendChild(document.createTextNode('  •  '));
        const seg = document.createElement('span');
        seg.className = 'match-overview-map-score';
        if (s.scoreId === winnerScoreId) seg.classList.add('leading');
        const modStr = s.mods.length > 0 ? ` +${s.mods.join('')}` : '';
        const scoreStr = s.totalScore !== null ? ` ${s.totalScore.toLocaleString()}` : '';
        seg.textContent = `${s.username} ${(s.accuracy * 100).toFixed(1)}% ${s.rank}${modStr}${scoreStr}`;
        scores.appendChild(seg);
      }
    }
    row.appendChild(scores);

    const btn = document.createElement('button');
    btn.className = 'match-overview-map-load';
    const anyReplay = item.scores.length >= 2 && item.scores.some(s => s.hasReplay);
    const wasBuilt = isPairBuilt(session, item);
    btn.textContent = wasBuilt ? 'Watch' : 'Load';
    // One replay suffices (missing side gets a fail-stub).
    btn.disabled = !anyReplay;
    btn.addEventListener('click', async () => {
      try {
        btn.disabled = true;
        // Cached pairs activate synchronously; bar would flicker on/off.
        const showProgress = !isPairBuilt(session, item);
        if (showProgress) setLoadProgress(true);
        await loadMatchPair(session, item);
      } catch (err) {
        setMatchStatus(loadErrorMessage(err), true);
        console.error(err);
      } finally {
        setLoadProgress(false);
        btn.disabled = false;
      }
    });
    row.appendChild(btn);

    const shareBtn = document.createElement('button');
    shareBtn.className = 'match-overview-map-share';
    shareBtn.setAttribute('aria-label', 'Copy share link');
    shareBtn.title = 'Copy share link';
    shareBtn.innerHTML = SHARE_LINK_ICON_SVG;
    shareBtn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      // Prefer built skin (what's on screen); else dropdown value. Non-defaults dropped.
      const builtSkin = builtEntrySkinName(session.builtPairs.get(item.playlistItemId));
      const skinName  = builtSkin ?? matchDefaultSkinSelect.value;
      const skinParam = isDefaultSkin(skinName) ? `&skin=${encodeURIComponent(skinName)}` : '';
      const url = `${window.location.origin}/#match=${session.match.roomId}&map=${item.playlistItemId}${skinParam}`;
      void copyShareLink(shareBtn, url);
    });
    row.appendChild(shareBtn);

    container.appendChild(row);
  }
}

function renderMatchOverview(session: MatchSession): void {
  const match = session.match;
  matchOverviewTitle.textContent = match.name;
  matchOverviewMeta.textContent = [
    `${match.playlistItems.length} map${match.playlistItems.length === 1 ? '' : 's'}`,
    `${match.players.length} player${match.players.length === 1 ? '' : 's'}`,
  ].join(' • ');

  const totalMaps = match.playlistItems.length;
  matchOverviewPlayers.replaceChildren();
  matchOverviewPlayers.classList.toggle('team', match.roomType === 'team_versus');

  if (match.roomType === 'team_versus') {
    // Layout: [red column] [team summary] [blue column].
    const redPlayers  = match.players.filter(p => p.team === 'red');
    const bluePlayers = match.players.filter(p => p.team === 'blue');

    const redCol = document.createElement('div');
    redCol.className = 'match-overview-team-col match-overview-team-col-red';
    for (const p of redPlayers) {
      redCol.appendChild(buildPlayerCard(p, session.profiles.get(p.userId), totalMaps));
    }

    const blueCol = document.createElement('div');
    blueCol.className = 'match-overview-team-col match-overview-team-col-blue';
    for (const p of bluePlayers) {
      blueCol.appendChild(buildPlayerCard(p, session.profiles.get(p.userId), totalMaps));
    }

    matchOverviewPlayers.appendChild(redCol);
    matchOverviewPlayers.appendChild(buildTeamMatchSummary(redPlayers, bluePlayers));
    matchOverviewPlayers.appendChild(blueCol);
  } else if (match.players.length === 2) {
    const [p1, p2] = match.players as [MatchPlayer, MatchPlayer];
    matchOverviewPlayers.appendChild(buildPlayerCard(p1, session.profiles.get(p1.userId), totalMaps));
    matchOverviewPlayers.appendChild(buildMatchSummary(p1, p2));
    matchOverviewPlayers.appendChild(buildPlayerCard(p2, session.profiles.get(p2.userId), totalMaps));
  } else {
    for (const p of match.players) {
      matchOverviewPlayers.appendChild(buildPlayerCard(p, session.profiles.get(p.userId), totalMaps));
    }
  }

  renderMatchMapRows(matchOverviewMaps, session);
}

/** Whole winning team gets +1 mapWins, so any member's value is the team count. */
function buildTeamMatchSummary(red: MatchPlayer[], blue: MatchPlayer[]): HTMLElement {
  const redMapsWon  = red[0]?.mapWins  ?? 0;
  const blueMapsWon = blue[0]?.mapWins ?? 0;
  const redTotal    = red.reduce( (n, p) => n + p.totalScore, 0);
  const blueTotal   = blue.reduce((n, p) => n + p.totalScore, 0);
  const redAcc      = red.length  > 0 ? red.reduce( (n, p) => n + p.avgAccuracy, 0) / red.length  : 0;
  const blueAcc     = blue.length > 0 ? blue.reduce((n, p) => n + p.avgAccuracy, 0) / blue.length : 0;

  const el = document.createElement('div');
  el.className = 'match-overview-summary match-overview-summary-team';

  const scoreRow = document.createElement('div');
  scoreRow.className = 'match-overview-summary-score';
  const leftWins = document.createElement('span');
  leftWins.className = 'match-overview-summary-wins';
  leftWins.textContent = String(redMapsWon);
  if (redMapsWon > blueMapsWon) leftWins.classList.add('leading');
  const dash = document.createElement('span');
  dash.className = 'match-overview-summary-dash';
  dash.textContent = '–';
  const rightWins = document.createElement('span');
  rightWins.className = 'match-overview-summary-wins';
  rightWins.textContent = String(blueMapsWon);
  if (blueMapsWon > redMapsWon) rightWins.classList.add('leading');
  scoreRow.append(leftWins, dash, rightWins);
  el.appendChild(scoreRow);

  el.appendChild(buildSummaryStatRow(
    'Total score',
    redTotal.toLocaleString(), blueTotal.toLocaleString(),
    redTotal, blueTotal,
  ));
  el.appendChild(buildSummaryStatRow(
    'Mean accuracy',
    `${(redAcc * 100).toFixed(2)}%`, `${(blueAcc * 100).toFixed(2)}%`,
    redAcc, blueAcc,
  ));
  return el;
}

/** Ties leave both un-bolded so 0/0 states stay stable. */
function buildMatchSummary(p1: MatchPlayer, p2: MatchPlayer): HTMLElement {
  const el = document.createElement('div');
  el.className = 'match-overview-summary';

  const scoreRow = document.createElement('div');
  scoreRow.className = 'match-overview-summary-score';
  const leftWins = document.createElement('span');
  leftWins.className = 'match-overview-summary-wins';
  leftWins.textContent = String(p1.mapWins);
  if (p1.mapWins > p2.mapWins) leftWins.classList.add('leading');
  const dash = document.createElement('span');
  dash.className = 'match-overview-summary-dash';
  dash.textContent = '–';
  const rightWins = document.createElement('span');
  rightWins.className = 'match-overview-summary-wins';
  rightWins.textContent = String(p2.mapWins);
  if (p2.mapWins > p1.mapWins) rightWins.classList.add('leading');
  scoreRow.append(leftWins, dash, rightWins);
  el.appendChild(scoreRow);

  el.appendChild(buildSummaryStatRow(
    'Total score',
    p1.totalScore.toLocaleString(),
    p2.totalScore.toLocaleString(),
    p1.totalScore, p2.totalScore,
  ));
  el.appendChild(buildSummaryStatRow(
    'Accuracy',
    `${(p1.avgAccuracy * 100).toFixed(2)}%`,
    `${(p2.avgAccuracy * 100).toFixed(2)}%`,
    p1.avgAccuracy, p2.avgAccuracy,
  ));

  return el;
}

function buildSummaryStatRow(label: string, leftText: string, rightText: string, leftVal: number, rightVal: number): HTMLElement {
  const row = document.createElement('div');
  row.className = 'match-overview-summary-stat';

  const left = document.createElement('span');
  left.className = 'match-overview-summary-stat-val left';
  left.textContent = leftText;
  if (leftVal > rightVal) left.classList.add('leading');

  const lab = document.createElement('span');
  lab.className = 'match-overview-summary-stat-label';
  lab.textContent = label;

  const right = document.createElement('span');
  right.className = 'match-overview-summary-stat-val right';
  right.textContent = rightText;
  if (rightVal > leftVal) right.classList.add('leading');

  row.append(left, lab, right);
  return row;
}

/** profile=undefined degrades to a bare card with username + in-match W-L. */
function buildPlayerCard(
  p: { userId: number; username: string; mapWins: number; avgAccuracy: number },
  profile: OsuUserProfile | undefined,
  totalMaps: number,
): HTMLElement {
  const card = document.createElement('div');
  card.className = 'match-overview-player';

  if (profile !== undefined && profile.avatarUrl !== null) {
    const avatar = document.createElement('img');
    avatar.className = 'match-overview-player-avatar';
    avatar.src = profile.avatarUrl;
    avatar.alt = '';
    avatar.referrerPolicy = 'no-referrer';
    card.appendChild(avatar);
  } else {
    const avatar = document.createElement('div');
    avatar.className = 'match-overview-player-avatar match-overview-player-avatar-placeholder';
    card.appendChild(avatar);
  }

  const body = document.createElement('div');
  body.className = 'match-overview-player-body';

  const topRow = document.createElement('div');
  topRow.className = 'match-overview-player-topline';
  if (profile?.countryCode) {
    const flag = document.createElement('img');
    flag.className = 'match-overview-player-flag';
    flag.src = `https://flagcdn.com/w40/${profile.countryCode.toLowerCase()}.png`;
    flag.alt = profile.countryCode;
    flag.title = profile.countryName ?? profile.countryCode;
    topRow.appendChild(flag);
  }
  const name = document.createElement('span');
  name.className = 'match-overview-player-name';
  name.textContent = profile?.username ?? p.username;
  topRow.appendChild(name);
  body.appendChild(topRow);

  // QP rank + lifetime W-L drove the room; falls back to global std rank + in-match W-L.
  const stats = document.createElement('div');
  stats.className = 'match-overview-player-stats';
  const mm = profile?.matchmaking ?? null;
  let rankPart: string;
  if (mm?.rank != null) {
    // `*` flags a provisional rank (rating still calibrating).
    rankPart = `QP #${mm.rank.toLocaleString()}${mm.isProvisional ? '*' : ''}`;
    if (mm.isProvisional) stats.title = 'Provisional quick play rank — rating still calibrating';
    else if (mm.poolName)  stats.title = `${mm.poolName} (quick play)`;
  } else if (profile?.globalRank != null) {
    rankPart = `#${profile.globalRank.toLocaleString()}`;
  } else {
    rankPart = 'unranked';
  }
  const recordPart =
    mm?.wins != null && mm?.losses != null
      ? `${mm.wins.toLocaleString()}W - ${mm.losses.toLocaleString()}L`
      : `${p.mapWins}-${Math.max(0, totalMaps - p.mapWins)} this match`;
  stats.textContent = `${rankPart} • ${recordPart}`;
  body.appendChild(stats);

  if (profile?.globalRank != null || profile?.countryRank != null) {
    const ranks = document.createElement('div');
    ranks.className = 'match-overview-player-ranks';
    const parts: string[] = [];
    if (profile?.globalRank != null) parts.push(`#${profile.globalRank.toLocaleString()} global`);
    if (profile?.countryRank != null) {
      const cc = profile.countryCode ?? '';
      parts.push(`#${profile.countryRank.toLocaleString()}${cc ? ` ${cc}` : ' country'}`);
    }
    ranks.textContent = parts.join(' • ');
    body.appendChild(ranks);
  }

  card.appendChild(body);
  return card;
}

/** Does NOT touch activeSession. */
function showMatchOverview(session: MatchSession): void {
  canvas.classList.remove('hidden');
  controls.classList.add('hidden');
  renderOptions.classList.add('hidden');
  playerLabel1.classList.add('hidden');
  scrubMarkers.replaceChildren();

  paintStubBackground(canvas, session.cover, 0.55);
  renderMatchOverview(session);
  matchOverview.classList.remove('hidden');
  updatePlayPauseBtn(false);
}

/** Caller caches the result on matchSession.builtPairs; skinName is per-click. */
async function buildPair(matchSession: MatchSession, item: MatchPlaylistItem, skinName: string): Promise<BuiltPair> {
  if (item.scores[0] === undefined || item.scores[1] === undefined) {
    throw new Error('match item has fewer than 2 scores');
  }
  if (!item.scores.some(s => s.hasReplay)) {
    throw new Error('neither player has a replay for this map');
  }
  if (!isLoggedIn()) {
    throw new Error('log in on the Match tab to download match replays');
  }

  // Anchor to match-level player order so the same player stays on the same canvas across maps.
  const leftUserId  = matchSession.match.players[0]?.userId;
  const rightUserId = matchSession.match.players[1]?.userId;
  const byLeft  = leftUserId  !== undefined ? item.scores.find(s => s.userId === leftUserId)  : undefined;
  const byRight = rightUserId !== undefined ? item.scores.find(s => s.userId === rightUserId) : undefined;
  const [scoreA, scoreB] = byLeft !== undefined && byRight !== undefined
    ? [byLeft, byRight]
    : [item.scores[0], item.scores[1]];

  // Primary must have a replay; if the left player has none, the right takes over (one-map exception).
  const primaryScore = scoreA.hasReplay ? scoreA : scoreB;
  const partnerScore = scoreA.hasReplay ? scoreB : scoreA;
  const partnerHasReplay = partnerScore.hasReplay;

  const primaryOsrCached = scoreIdToOsr.get(primaryScore.scoreId);
  const partnerOsrCached = partnerHasReplay ? scoreIdToOsr.get(partnerScore.scoreId) : undefined;
  const oszCached        = cachedOsz(item.beatmapsetId);

  const needsToken = primaryOsrCached === undefined
    || (partnerHasReplay && partnerOsrCached === undefined);
  const token = needsToken ? await getAccessToken() : '';

  const [primaryOsr, partnerOsr, osz] = await Promise.all([
    primaryOsrCached ?? downloadReplay(primaryScore.scoreId, token),
    partnerHasReplay
      ? (partnerOsrCached ?? downloadReplay(partnerScore.scoreId, token))
      : Promise.resolve<ArrayBuffer | null>(null),
    oszCached ?? downloadBeatmapSet(item.beatmapsetId).then(r => r.buffer),
  ]);

  if (primaryOsrCached === undefined) scoreIdToOsr.set(primaryScore.scoreId, primaryOsr);
  if (oszCached === undefined)        setIdToOsz.set(item.beatmapsetId, { buffer: osz, hasVideo: false });
  if (partnerHasReplay && partnerOsr !== null && partnerOsrCached === undefined) {
    scoreIdToOsr.set(partnerScore.scoreId, partnerOsr);
  }

  const dedupKey = `match-pair:${matchSession.id}:${item.playlistItemId}:${skinName}`;
  const primary = await buildSession({
    canvas,
    storyboard:   false,
    osrBuffer:    primaryOsr,
    oszBuffer:    osz,
    skinName,
    dedupKey,
    scoreId:      primaryScore.scoreId,
    beatmapsetId: item.beatmapsetId,
    // MatchSession owns the raw caches; teardown here must leave them populated.
    ownsRawCache: false,
  });

  let partner: ReplaySession | null = null;
  let partnerStubUsername: string | null = null;
  if (partnerHasReplay && partnerOsr !== null) {
    partner = await buildPartnerSession(primary, partnerOsr);
  } else {
    partnerStubUsername = partnerScore.username;
  }
  return { kind: 'pair' as const, primary, partner, partnerStubUsername, audioSide: 'primary' };
}

/** Snap-mount; call after deactivateMatchPair. */
function activateBuiltPair(matchSession: MatchSession, item: MatchPlaylistItem, pair: BuiltPair, opts: { autoplay?: boolean } = {}): void {
  const autoplay = opts.autoplay ?? true;
  const { primary } = pair;
  activeMatchPair = { matchSession, item, primary };
  activePlayer    = primary.player;
  activeRenderer  = primary.renderer;

  // activePlayer stays primary; only audio source + clockFn flip. Must run before play().
  const audioSync = pair.partner !== null && pair.audioSide === 'partner'
    ? pair.partner.audioSync
    : primary.audioSync;
  activeAudioSync = audioSync;
  if (pair.partner !== null) {
    const musicVol   = Number(optMusicVol.value)   / 100;
    const effectsVol = Number(optEffectsVol.value) / 100;
    primary.audioSync.setSongVolume(pair.audioSide === 'primary' ? musicVol   : 0);
    primary.audioSync.setEffectsVolume(pair.audioSide === 'primary' ? effectsVol : 0);
    pair.partner.audioSync.setSongVolume(pair.audioSide === 'partner' ? musicVol   : 0);
    pair.partner.audioSync.setEffectsVolume(pair.audioSide === 'partner' ? effectsVol : 0);
    primary.player.setClockFn(audioSync.clockFn);
    pair.partner.player.setClockFn(audioSync.clockFn);
  }

  optJudgement.checked    = primary.renderer.options.showJudgement;
  optFollowpoints.checked = primary.renderer.options.showFollowpoints;
  optKeyOverlay.checked   = primary.renderer.options.showKeyOverlay;
  optURBar.checked        = primary.renderer.options.showURBar;
  optModIcons.checked     = primary.renderer.options.showModIcons;
  optPP.checked           = isPPCounterEnabled(primary.renderer);
  optModHidden.checked     = primary.renderer.options.modHidden;
  optModFlashlight.checked = primary.renderer.options.modFlashlight;
  optModFadeIn.checked     = primary.renderer.options.modFadeIn;
  optModCover.checked      = primary.renderer.options.modCover;
  optUpscroll.checked      = primary.renderer.options.maniaUpscroll;
  optFlyingHits.checked = primary.renderer.options.taikoFlyingHits;
  optBgDim.value          = String(Math.round(primary.renderer.options.backgroundDim * 100));
  optBgDimValue.textContent = `${optBgDim.value}%`;
  optManiaScroll.value      = String(primary.renderer.options.maniaScrollSpeed);
  optManiaScrollValue.textContent = String(primary.renderer.options.maniaScrollSpeed);
  optMusicVol.value          = String(Math.round(primary.musicVol * 100));
  optMusicVolValue.textContent = `${optMusicVol.value}%`;
  optEffectsVol.value        = String(Math.round(primary.effectsVol * 100));
  optEffectsVolValue.textContent = `${optEffectsVol.value}%`;
  optBeatmapHitsounds.checked = primary.beatmapHitsounds;
  primary.renderer.options.audioOffsetMs = primary.audioOffsetMs;
  updateOffsetReadout(primary.audioOffsetMs);
  const markerSources: ScrubMarkerSource[] = [{
    results: primary.renderer.hitResults,
    introOffsetMs: primary.introOffsetMs,
    presentationDurationMs: primary.timeMapper.presentationDurationMs,
    speed: primary.speed,
    side: 'primary',
  }];
  if (pair.partner !== null) {
    markerSources.push({
      results: pair.partner.renderer.hitResults,
      introOffsetMs: pair.partner.introOffsetMs,
      presentationDurationMs: pair.partner.timeMapper.presentationDurationMs,
      speed: pair.partner.speed,
      side: 'partner',
    });
  }
  renderScrubMarkers(markerSources);
  resetClipRange();

  matchOverview.classList.add('hidden');
  canvas.classList.remove('hidden');
  controls.classList.remove('hidden');
  renderOptions.classList.remove('hidden');
  renderOptions.classList.toggle('taiko-mode', primary.mode === 1);
  renderOptions.classList.toggle('mania-mode', primary.mode === 3);
  renderOptions.classList.toggle('catch-mode', primary.mode === 2);
  playerLabel1.textContent = primary.username;
  playerLabel1.classList.remove('hidden');
  // Use matchSession.label (not primary.label) so Online/Manual slots don't leak single-replay format.
  setStatus(matchSession.label);
  setMatchStatus(matchSession.label);

  primary.renderer.start();

  if (pair.partner !== null) {
    attachPartner(primary, pair.partner);
    showAudioSwap(primary, pair.partner, pair.audioSide);
  } else if (pair.partnerStubUsername !== null) {
    attachFailStub(primary, pair.partnerStubUsername);
  }

  enterMatchView(matchSession.match, item);

  if (autoplay) {
    updatePlayPauseBtn(true);
    void doPlay();
  } else {
    updatePlayPauseBtn(false);
  }

  renderTabs();
  refreshShareAtTimeBtn();
}

async function loadMatchPair(matchSession: MatchSession, item: MatchPlaylistItem, opts: { autoplay?: boolean } = {}): Promise<void> {
  let entry = matchSession.builtPairs.get(item.playlistItemId);
  if (entry === undefined) {
    const skinName = matchDefaultSkinSelect.value;
    entry = shouldUseTeamMatch(matchSession.match, item)
      ? await buildTeamMatch(matchSession, item, skinName)
      : await buildPair(matchSession, item, skinName);
    matchSession.builtPairs.set(item.playlistItemId, entry);
    trimBuiltPairs(matchSession);
  } else {
    touchBuiltPair(matchSession, item.playlistItemId, entry);
  }
  deactivateMatchPair();
  if (entry.kind === 'team') {
    activateBuiltTeamMatch(matchSession, item, entry, opts);
  } else {
    activateBuiltPair(matchSession, item, entry, opts);
  }
}

/** autoplay:false on boot-time deep-links (no fresh user gesture). */
async function loadMatch(roomIdOrRef: string, autoMapId?: number, opts: { autoplay?: boolean } = {}): Promise<void> {
  const roomId = parseRoomRef(roomIdOrRef);
  if (roomId === null) {
    setMatchStatus('Invalid room URL or ID.', true);
    return;
  }
  if (!isLoggedIn()) {
    setMatchStatus('Log in to fetch match data.', true);
    return;
  }

  let matchSession: MatchSession | null = null;
  try {
    matchFetchBtn.disabled = true;
    setMatchStatus('');
    // Stay on through the autoMapId pair-load so the bar covers the replay download.
    setLoadProgress(true);

    const match = await fetchRoom(roomId);
    await consumeSkinUpload(matchOskInput, matchDefaultSkinSelect);

    // Dedup by roomId only; skin is per-map (read at Load click) so it doesn't differentiate.
    const dedupKey = `match:${match.roomId}`;
    const existing = sessions.find(s => s.dedupKey === dedupKey);
    if (existing !== undefined && existing.kind === 'match') {
      activateSession(existing, { skipOverview: autoMapId !== undefined });
      if (autoMapId === undefined) {
        setMatchStatus(`Match ${match.roomId} already loaded — activated existing tab.`);
      } else {
        setMatchStatus(existing.label);
      }
      matchSession = existing;
    } else {
      const firstItem = match.playlistItems[0];
      const coverPromise = firstItem !== undefined ? fetchMatchBackground(firstItem) : Promise.resolve(null);
      const profilesPromise = fetchMatchProfiles(match);
      const [cover, profiles] = await Promise.all([coverPromise, profilesPromise]);

      const session: MatchSession = {
        kind: 'match' as const,
        id:   nextSessionId++,
        dedupKey,
        match,
        cover,
        label: computeMatchLabel(match),
        builtPairs: new Map(),
        profiles,
      };
      sessions.push(session);
      activateSession(session, { skipOverview: autoMapId !== undefined });
      setMatchStatus(session.label);
      matchSession = session;
    }
    if (matchSession !== null && autoMapId !== undefined) {
      const item = matchSession.match.playlistItems.find(p => p.playlistItemId === autoMapId);
      if (item === undefined) {
        setMatchStatus(`Map ${autoMapId} is not part of this match.`, true);
        // Overview was skipped on activation; show it now.
        showMatchOverview(matchSession);
      } else {
        try {
          await loadMatchPair(matchSession, item, opts);
        } catch (err) {
          setMatchStatus(loadErrorMessage(err), true);
          console.error(err);
          showMatchOverview(matchSession);
        }
      }
    }
  } catch (err) {
    setMatchStatus(loadErrorMessage(err), true);
    console.error(err);
  } finally {
    setLoadProgress(false);
    matchFetchBtn.disabled = !isLoggedIn();
  }
}

matchFetchBtn.addEventListener('click', () => {
  if (isMatchUnsupported()) {
    setMatchStatus('Match replay viewing isn’t supported on mobile yet — open on desktop.', true);
    return;
  }
  void loadMatch(roomInput.value);
});

// ─── OAuth + fetch-by-score flow ─────────────────────────────────────────────

function setAuthText(text: string): void {
  authStateEl.textContent       = text;
  authStateMatchEl.textContent  = text;
}

function setLoggedOutUI(text: string): void {
  setAuthText(text);
  loginBtn.classList.remove('hidden');
  logoutBtn.classList.add('hidden');
  loginBtnMatch.classList.remove('hidden');
  logoutBtnMatch.classList.add('hidden');
  fetchBtn.disabled = true;
  matchFetchBtn.disabled = true;
}

function setLoggedInUI(text: string): void {
  setAuthText(text);
  loginBtn.classList.add('hidden');
  logoutBtn.classList.remove('hidden');
  loginBtnMatch.classList.add('hidden');
  logoutBtnMatch.classList.remove('hidden');
  fetchBtn.disabled = false;
  matchFetchBtn.disabled = false;
}

async function refreshAuthUI(): Promise<void> {
  if (!isLoggedIn()) {
    setLoggedOutUI('Not logged in');
    return;
  }
  // Identity cached under the current access token → no /me call this load. The cache
  // invalidates on token rotation (~daily), so revocation still surfaces within a day;
  // mid-session it surfaces as AuthError from any API helper (see loadErrorMessage).
  const cached = cachedUser();
  if (cached !== null) {
    setLoggedInUI(`Logged in as ${cached.username}`);
    return;
  }
  // Trust stored token; fetchMe only adjusts the label or downgrades on genuine auth failure.
  setLoggedInUI('Logged in');
  try {
    const me = await fetchMe();
    setAuthText(`Logged in as ${me.username}`);
  } catch (err) {
    if (err instanceof AuthError) {
      logout();
      setLoggedOutUI('Session expired — please log in again');
    } else if (err instanceof OsuUnavailableError) {
      // osu! is reachable but blocking us (e.g. the Cloudflare DDoS block on /api/v2).
      // Token is still valid — stay logged in and tell the user it's an osu!-side issue.
      setStatus(err.message, true);
      console.warn('fetchMe: osu! unavailable (keeping logged-in state):', err);
    } else {
      // Transient failure (network, 5xx, proxy cold start). Stay logged in; token is still good.
      console.warn('fetchMe failed (keeping logged-in state):', err);
    }
  }
}

/** Maps a load-path failure to its user-facing message; AuthError (token revoked or
 *  refresh rejected mid-session) additionally downgrades the auth UI to logged-out. */
function loadErrorMessage(err: unknown): string {
  if (err instanceof AuthError) {
    logout();
    setLoggedOutUI('Session expired — please log in again');
    return 'Session expired — please log in again';
  }
  if (err instanceof OsuUnavailableError) return err.message;
  return `Error: ${err instanceof Error ? err.message : String(err)}`;
}

async function onLoginClick(): Promise<void> {
  try {
    await startLogin();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    setStatus(msg, true);
  }
}

function onLogoutClick(): void {
  logout();
  setStatus('');
  refreshAuthUI();
}

loginBtn.addEventListener('click',       onLoginClick);
loginBtnMatch.addEventListener('click',  onLoginClick);
logoutBtn.addEventListener('click',      onLogoutClick);
logoutBtnMatch.addEventListener('click', onLogoutClick);

/** autoplay:false for boot-time deep-links to avoid suspended-ctx zombie playback on first Play. */
async function loadScoreById(scoreIdOrRef: string, opts: { autoplay?: boolean } = {}): Promise<void> {
  const autoplay = opts.autoplay ?? true;
  const ref = parseScoreRef(scoreIdOrRef);
  if (ref === null) {
    setStatus('Unrecognized score URL or ID.', true);
    return;
  }
  const scoreId = ref.id;
  const scoreRuleset = ref.ruleset;
  const cacheKey = scoreCacheKey(scoreId, scoreRuleset);

  fetchBtn.disabled = true;
  setStatus('');
  setLoadProgress(true);
  try {
    // Defer cache writes until buildSession parses cleanly; malformed buffers would otherwise poison the cache.
    let osrBuffer = scoreIdToOsr.get(cacheKey);
    const osrFromCache = osrBuffer !== undefined;
    let token: string | null = null;
    if (osrBuffer === undefined) {
      token = await getAccessToken();
      osrBuffer = await downloadReplay(scoreId, token, { ruleset: scoreRuleset });
    }

    const skinName = await consumeSkinUpload(fetchOskInput, fetchDefaultSkinSelect);

    const dedupKey = md5(new Uint8Array(osrBuffer));
    const existing = sessions.find(s => s.kind === 'replay' && s.dedupKey === dedupKey && s.skinName === skinName);
    if (existing !== undefined) {
      activateSession(existing);
      return;
    }

    const replayData = await parseReplayCached(osrBuffer, dedupKey);

    // Cache as soon as API answers (mapping is correct even if .osz/buildSession fails later).
    let beatmapsetId = hashToSetId.get(replayData.beatmapHash);
    if (beatmapsetId === undefined) {
      if (token === null) token = await getAccessToken();
      const lookup = await lookupBeatmapByHash(replayData.beatmapHash, token);
      beatmapsetId = lookup.beatmapsetId;
      hashToSetId.set(replayData.beatmapHash, beatmapsetId);
      persistHashLookup();
    }

    const osz = await getOsz(beatmapsetId);
    const oszBuffer = osz.buffer;

    const session = await buildSession({
      canvas,
      osrBuffer,
      oszBuffer,
      replayData,
      skinName,
      dedupKey,
      scoreId,
      scoreRuleset,
      beatmapsetId,
    });

    if (!osrFromCache) scoreIdToOsr.set(cacheKey, osrBuffer);
    if (!osz.fromCache) setIdToOsz.set(beatmapsetId, { buffer: oszBuffer, hasVideo: osz.hasVideo });

    if (!autoplay) session.wasPlaying = false;

    sessions.push(session);
    activateSession(session);
  } catch (err) {
    setStatus(loadErrorMessage(err), true);
    console.error(err);
  } finally {
    setLoadProgress(false);
    fetchBtn.disabled = !isLoggedIn();
  }
}

fetchBtn.addEventListener('click', () => { void loadScoreById(scoreInput.value); });

const panelTabs    = document.querySelectorAll<HTMLButtonElement>('.panel-tab');
const tabContents  = document.querySelectorAll<HTMLElement>('.tab-content');

function activateTab(target: 'online' | 'manual' | 'match' | 'auto'): void {
  panelTabs.forEach((t)   => t.classList.toggle('active', t.dataset.target === target));
  tabContents.forEach((c) => c.classList.toggle('hidden', c.dataset.tab !== target));
}

panelTabs.forEach((tab) => {
  tab.addEventListener('click', () => {
    const target = tab.dataset.target;
    if (target !== 'online' && target !== 'manual' && target !== 'match' && target !== 'auto') return;
    if (target === 'match' && isMatchUnsupported()) {
      setStatus('Match replay viewing isn’t supported on mobile yet — open on desktop.', true);
      return;
    }
    activateTab(target);
  });
});

// Drop assigns dataTransfer.files directly: Chrome sniffs .osz as application/zip and the input's
// accept attr filters it; rebuilding via DataTransfer yields shifted bytes in some browsers.
function wireFileDrop(label: HTMLLabelElement): void {
  const input = label.querySelector<HTMLInputElement>('input[type="file"]');
  const text  = label.querySelector<HTMLSpanElement>('.file-drop-text');
  if (input === null || text === null) return;
  const placeholder = label.dataset.placeholder ?? 'Click or drop file';

  const updateLabel = (): void => {
    const file = input.files?.[0];
    if (file) {
      text.textContent = file.name;
      label.classList.add('has-file');
    } else {
      text.textContent = placeholder;
      label.classList.remove('has-file');
    }
  };

  input.addEventListener('change', updateLabel);

  label.addEventListener('dragover', (e) => {
    e.preventDefault();
    label.classList.add('dragover');
  });
  label.addEventListener('dragleave', () => label.classList.remove('dragover'));
  label.addEventListener('drop', (e) => {
    e.preventDefault();
    label.classList.remove('dragover');
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    input.files = files;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  updateLabel();
}

document.querySelectorAll<HTMLLabelElement>('.file-drop').forEach(wireFileDrop);

function cycleSession(dir: 1 | -1): void {
  if (activeSession === null || sessions.length < 2) return;
  const i = sessions.indexOf(activeSession);
  if (i < 0) return;
  const n = sessions.length;
  const next = sessions[(i + dir + n) % n];
  if (next && next !== activeSession) activateSession(next);
}

window.addEventListener('keydown', (e) => {
  const tag = (document.activeElement?.tagName ?? '').toUpperCase();
  if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
  // While the export dialog is open it owns the keyboard (its own ESC handler closes it);
  // don't let space/arrows/offset keys drive playback behind the modal.
  if (exportDialog.open) return;

  if (e.key === ' ' || e.code === 'Space') {
    if (activePlayer === null) return;
    e.preventDefault();
    if (activePlayer.isPlaying) doPause();
    else doPlay();
  } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
    if (sessions.length < 2) return;
    e.preventDefault();
    cycleSession(-1);
  } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
    if (sessions.length < 2) return;
    e.preventDefault();
    cycleSession(1);
  } else if (e.key === '-' || e.key === '_') {
    if (activeSession === null) return;
    e.preventDefault();
    nudgeOffset(-OFFSET_STEP_MS);
  } else if (e.key === '+' || e.key === '=') {
    if (activeSession === null) return;
    e.preventDefault();
    nudgeOffset(+OFFSET_STEP_MS);
  } else if ((e.key === 'E' || e.key === 'e') && e.shiftKey) {
    // Shift+E opens the export dialog (same as the toolbar Export button).
    e.preventDefault();
    void openExportDialog();
  }
});

// Strip characters illegal in Windows/macOS filenames, collapse whitespace, trim trailing
// dots/spaces (also illegal on Windows). Falls back to "replay" if nothing usable remains.
function sanitizeFilename(name: string): string {
  // eslint-disable-next-line no-control-regex
  const cleaned = name.replace(/[\\/:*?"<>|\x00-\x1F]/g, '').replace(/\s+/g, ' ').trim().replace(/[. ]+$/, '');
  return cleaned || 'replay';
}

// ── MP4 export (Phase X3 UI) ─────────────────────────────────────────────────
// The export path (mediabunny + SoundTouch + the Exporter) is dynamically imported, so it
// stays off the initial load; opening the dialog warms the chunk, the Export button runs it.
// The dialog is modal (showModal), so the page behind is inert during config; during the
// actual run we additionally pause + stop the live renderer's rAF loop to free the main thread.
type ExportModule = typeof import('./export/Exporter.js');
type PresetsModule = typeof import('./export/presets.js');
let exportMod: ExportModule | null = null;
let presetsMod: PresetsModule | null = null;
let exportWasPlaying = false;
let exportCancelRequested = false;
// The format negotiated for the current dialog/preset (set by refreshExportDetails). runExport
// reads it without re-detecting so it can decide streaming + open the save picker within the
// click's user-activation window (no await before showSaveFilePicker).
let lastExportFormat: ExportFormat | null = null;

// Above this estimated output size, stream to disk (File System Access) instead of buffering
// the whole file in RAM — the marathon memory guard. Set to 1 GB: typical exports stay on the
// frictionless in-memory <a download> path; only genuinely huge ones prompt the save picker.
// Lower this if users report OOM/failed exports below 1 GB.
const STREAM_THRESHOLD_BYTES = 1024 * 1024 * 1024;

// Minimal File System Access typings (not in lib.dom for this TS version). Chromium-only; the
// streaming path is skipped where this is absent (Firefox/Safari → in-memory fallback).
interface SaveFileHandle {
  createWritable(): Promise<DiskWritable>;
  getFile(): Promise<File>;
  readonly name: string;
}
type ShowSaveFilePicker = (opts: {
  suggestedName?: string;
  types?: Array<{ description?: string; accept: Record<string, string[]> }>;
}) => Promise<SaveFileHandle>;

async function loadExportModules(): Promise<{ exp: ExportModule; presets: PresetsModule }> {
  if (exportMod === null || presetsMod === null) {
    const [exp, presets] = await Promise.all([
      import('./export/Exporter.js'),
      import('./export/presets.js'),
    ]);
    exportMod = exp;
    presetsMod = presets;
  }
  return { exp: exportMod, presets: presetsMod };
}

// Gather the currently-mounted match canvases into export inputs, mirroring the on-screen layout
// (grid position, player names, the audible side). Returns null when not in a mounted match pair/
// team — i.e. solo replays and the match overview both fall back to the single-replay export path.
function buildMatchExportInputs(): MatchExportInputs | null {
  if (activeMatchPair === null) return null;
  const primary = activeMatchPair.primary;
  const dim = primary.renderer.options.backgroundDim;
  const cells: MatchExportCell[] = [];
  let cols: number;
  let rows: number;
  let audible: ReplaySession;

  if (activeTeamMatch !== null) {
    const entry = activeTeamMatch;
    const teamSize = entry.slots.length / 2;
    cols = teamSize === 2 ? 2 : 4;
    rows = 2;
    for (const slot of entry.slots) {
      cells.push({
        renderer: slot.session?.renderer ?? null,
        gridCol: slot.gridColumn,
        gridRow: slot.gridRow,
        label: slot.username,
        side: slot.team === 'red' ? 'red' : 'blue',
        stubBackground: slot.session === null ? primary.shared.background : null,
        stubDim: dim,
      });
    }
    audible = entry.slots[entry.audioSlotIdx]?.session ?? primary;
  } else {
    cols = 2;
    rows = 1;
    cells.push({ renderer: primary.renderer, gridCol: 1, gridRow: 1, label: primary.username, side: 'left' });
    if (partnerSession !== null) {
      cells.push({ renderer: partnerSession.renderer, gridCol: 2, gridRow: 1, label: partnerSession.username, side: 'right' });
    } else if (partnerStub !== null) {
      cells.push({ renderer: null, gridCol: 2, gridRow: 1, label: partnerStub.username, side: 'right', stubBackground: primary.shared.background, stubDim: dim });
    }
    const pair = currentBuiltPair();
    audible = (pair !== null && pair.audioSide === 'partner' && partnerSession !== null) ? partnerSession : primary;
  }

  // Strip needs a real score on both sides; a fail-stub side has none, so drop it then (matches the
  // on-screen dual-score strip, hidden for stub pairs).
  const leftReal  = cells.some(c => c.renderer !== null && (c.side === 'left'  || c.side === 'red'));
  const rightReal = cells.some(c => c.renderer !== null && (c.side === 'right' || c.side === 'blue'));

  return {
    cells, cols, rows,
    showStrip: leftReal && rightReal,
    primaryRenderer: primary.renderer,
    timeMapper: primary.timeMapper,
    presentationDurationMs: primary.timeMapper.presentationDurationMs,
    audio: audible.audioSync.getMixdownInputs(),
  };
}

// Whether the exporter can decode a session's beatmap video (mediabunny demux + WebCodecs), probed
// once per file at dialog time so the readout can say up front when a clip will lack it.
const exportVideoDecodable = new WeakMap<Uint8Array, Promise<boolean>>();
function canExportVideo(session: ReplaySession): Promise<boolean> {
  const video = session.core.assets.video;
  if (video === null || exportMod === null) return Promise.resolve(false);
  let p = exportVideoDecodable.get(video.bytes);
  if (p === undefined) {
    p = exportMod.canDecodeVideoForExport(video.bytes);
    exportVideoDecodable.set(video.bytes, p);
  }
  return p;
}

/** The export dialog's `Video:` line for a solo session with a video attached. */
function exportVideoText(session: ReplaySession, decodable: boolean): string {
  if (!session.renderer.options.showVideo) return 'off';
  return decodable ? 'on' : 'on — this browser cannot decode it in the exporter, exporting without it';
}

// Recompute the size estimate + codec notice for the selected preset; enables/disables Export.
async function refreshExportDetails(): Promise<void> {
  const session = activeReplaySession();
  if (session === null || presetsMod === null || exportMod === null) return;
  const preset = presetsMod.PRESETS[exportPreset.value as keyof PresetsModule['PRESETS']];
  if (preset === undefined) return;

  exportStart.disabled = true;
  exportCodecNotice.className = 'export-codec-notice';
  exportCodecNotice.textContent = 'Checking encoder support…';
  exportFirefoxNote.classList.add('hidden');

  // Match exports composite every canvas into one (larger) frame; probe + report that size, not a
  // single panel, so over-large grids (e.g. 4v4 at a high preset) correctly disable the preset.
  const matchInputs = buildMatchExportInputs();
  const outDims = matchInputs !== null
    ? exportMod.matchOutputDims(preset, matchInputs.cols, matchInputs.rows, matchInputs.showStrip)
    : { width: preset.width, height: preset.height };

  const [fmt, videoDecodable] = await Promise.all([
    exportMod.detectExportSupport(preset, outDims.width, outDims.height),
    matchInputs === null && session.renderer.hasVideo ? canExportVideo(session) : false,
  ]);
  if (exportPreset.value !== preset.id || !exportDialog.open) return; // preset changed / closed mid-detect
  lastExportFormat = fmt;

  const trimmed = clipIsTrimmed();
  const durationS = (trimmed ? clipDurationMs() : session.timeMapper.presentationDurationMs) / 1000;
  const audioBitrate = fmt !== null && fmt.audioCodec !== null ? preset.audioBitrate : 0;
  const sizeBytes = presetsMod.estimateExportBytes(preset, audioBitrate, durationS);
  exportStart.textContent = trimmed ? 'Export Clip' : 'Export';
  exportDetails.innerHTML =
    (trimmed
      ? `<div class="export-clip-line">Clip ${formatTime(clipStartPresMs)} – ${formatTime(clipEndPresMs)} · ${formatTime(clipDurationMs())}</div>`
      : '') +
    `<div><strong>${outDims.width}×${outDims.height}</strong> · ${preset.fps} fps · ` +
    `~<strong>${presetsMod.formatBytes(sizeBytes)}</strong>${preset.slow ? ' · slow' : ''}</div>` +
    // Exports draw what the live renderer draws; say so for maps that have a storyboard (match
    // builds never load one).
    (matchInputs === null && session.renderer.hasStoryboard
      ? `<div>Storyboard: ${session.renderer.options.showStoryboard ? 'on' : 'off'}</div>`
      : '') +
    // The video is decoded frame-exact by the exporter; say up front when it cannot be.
    (matchInputs === null && session.renderer.hasVideo
      ? `<div>Video: ${exportVideoText(session, videoDecodable)}</div>`
      : '');

  if (fmt === null) {
    exportCodecNotice.className = 'export-codec-notice error';
    exportCodecNotice.textContent = matchInputs !== null
      ? `This quality (${outDims.width}×${outDims.height}) is too large to encode here — pick a lower preset.`
      : 'No supported video encoder in this browser. Use desktop Chrome, Edge, Firefox, or Safari.';
    exportStart.disabled = true;
    return;
  }
  const vName = fmt.videoCodec === 'avc' ? 'H.264' : fmt.videoCodec.toUpperCase();
  const webmNote = fmt.container === 'webm' ? ' · saves as .webm' : '';
  if (fmt.audioCodec === null) {
    exportCodecNotice.className = 'export-codec-notice warn';
    exportCodecNotice.textContent = `Video: ${vName} · no audio encoder available — export will be silent${webmNote}.`;
  } else {
    exportCodecNotice.className = 'export-codec-notice';
    exportCodecNotice.textContent =
      `Video: ${vName} · Audio: ${fmt.audioCodec === 'aac' ? 'AAC' : 'Opus'}${webmNote}`;
  }
  // The WebM/VP9 path is the software-encoded Firefox route — warn it's slow.
  exportFirefoxNote.classList.toggle('hidden', fmt.container !== 'webm');
  exportStart.disabled = false;
}

async function openExportDialog(): Promise<void> {
  if (exportDialog.open) return;
  // Export is desktop-only (the WebCodecs encode is too heavy for phones); also belt-and-suspenders
  // for the CSS that hides the button — Shift+E / window.exportReplay() bypass the DOM.
  if (isMatchUnsupported()) return;
  // activeReplaySession() is the solo session or the mounted match primary; it's null on the match
  // overview (no pair loaded), so the dialog opens for solo replays and mounted match pairs/teams.
  const session = activeReplaySession();
  if (session === null) return;

  exportWasPlaying = session.player.isPlaying;
  if (exportWasPlaying) doPause();

  // Reset to config state.
  exportConfig.classList.remove('hidden');
  exportProgressWrap.classList.add('hidden');
  exportMessage.classList.add('hidden');
  exportMessage.textContent = '';
  exportCancel.classList.add('hidden');
  exportStart.classList.remove('hidden');
  exportStart.disabled = true;
  exportClose.disabled = false;
  exportDetails.textContent = '';
  exportCodecNotice.className = 'export-codec-notice';
  exportCodecNotice.textContent = 'Loading…';
  exportFirefoxNote.classList.add('hidden');

  exportDialog.showModal();

  const { presets } = await loadExportModules();
  if (exportPreset.options.length === 0) {
    for (const id of presets.PRESET_ORDER) {
      const p = presets.PRESETS[id];
      const opt = document.createElement('option');
      opt.value = p.id;
      opt.textContent = `${p.label} — ${p.width}×${p.height} ${p.fps}fps${p.slow ? ' (slow)' : ''}`;
      exportPreset.appendChild(opt);
    }
    exportPreset.value = presets.DEFAULT_PRESET.id;
  }
  await refreshExportDetails();
}

function closeExportDialog(): void {
  if (exporting) return; // mid-export: must Cancel first
  exportDialog.close();
  if (exportWasPlaying) {
    exportWasPlaying = false;
    void doPlay();
  }
}

// Decide the save path for an export run: above STREAM_THRESHOLD_BYTES on a browser with File
// System Access, open the save picker and stream straight to disk (marathon memory guard);
// otherwise return {} for the in-memory <a download> path.
// INVARIANT (load-bearing — see docs/decisions.md "Marathon exports stream to disk"):
// showSaveFilePicker consumes the click's transient user activation, so runExport must reach
// this call with NO await before it — an await inserted upstream silently breaks streaming on
// Chromium (the picker throws and every big export falls back to in-memory).
async function resolveSaveStream(
  fmt: ExportFormat | null,
  estBytes: number,
  baseName: string,
): Promise<{ saveStream?: DiskWritable; saveHandle?: SaveFileHandle }> {
  const picker = (window as unknown as { showSaveFilePicker?: ShowSaveFilePicker }).showSaveFilePicker;
  if (fmt === null || estBytes <= STREAM_THRESHOLD_BYTES || picker === undefined) return {};
  let saveHandle: SaveFileHandle;
  try {
    saveHandle = await picker({
      suggestedName: `${baseName}.${fmt.extension}`,
      types: [{
        description: 'Video',
        accept: { [fmt.container === 'webm' ? 'video/webm' : 'video/mp4']: [`.${fmt.extension}`] },
      }],
    });
  } catch (err) {
    // User dismissed the save dialog → cancel the export (flows through runExport's catch);
    // any other picker error (e.g. lost activation) → fall back to the in-memory path.
    if (err instanceof DOMException && err.name === 'AbortError') throw new exportMod!.ExportCancelledError();
    return {};
  }
  return { saveHandle, saveStream: await saveHandle.createWritable() };
}

// Progress/message wiring for an export run: mixdown prep → per-frame %/ETA → finalize lock.
function buildExportCallbacks(): ExportCallbacks {
  return {
    // Fired only for DT/HT exports: their SoundTouch stretch briefly freezes the main thread
    // before the frames begin (other mixdowns overlap the frame loop and never fire this).
    onPreparingAudio: () => {
      exportProgressPct.textContent = '0%';
      exportProgressEta.textContent = 'Preparing audio…';
    },
    onProgress: (p) => {
      const pct = Math.round(p.fraction * 100);
      exportProgressFill.style.width = `${pct}%`;
      exportProgressPct.textContent = `${pct}%`;
      exportProgressEta.textContent = p.etaMs > 0 ? `~${Math.ceil(p.etaMs / 1000)}s remaining` : 'estimating…';
    },
    // Frames done; the mux finalize (commit the file) is next. Can't cancel past this point.
    onFinalizing: () => {
      exportProgressFill.style.width = '100%';
      exportProgressPct.textContent = '100%';
      exportProgressEta.textContent = 'Finalizing…';
      exportCancel.disabled = true;
    },
    isCancelled: () => exportCancelRequested,
  };
}

async function runExport(): Promise<void> {
  if (exporting) return; // already running — ignore a second Export click
  const session = activeReplaySession();
  if (session === null || presetsMod === null || exportMod === null) return;
  const preset = presetsMod.PRESETS[exportPreset.value as keyof PresetsModule['PRESETS']];
  if (preset === undefined) return;

  exporting = true;
  exportCancelRequested = false;

  // Free the main thread: every live renderer redraws on its own rAF even while paused, which would
  // compete with the export draw loop. Stop them all (match view has one per canvas) for the
  // duration; restart in `finally`. The export runs on independent clones, so this is safe.
  doPause();
  forEachActiveRenderer(r => r.stop());

  exportConfig.classList.add('hidden');
  exportProgressWrap.classList.remove('hidden');
  exportMessage.classList.add('hidden');
  exportStart.classList.add('hidden');
  exportCancel.classList.remove('hidden');
  exportCancel.disabled = false;
  exportClose.disabled = true;
  exportProgressFill.style.width = '0%';
  exportProgressPct.textContent = '0%';
  exportProgressEta.textContent = 'starting…';

  try {
    // Marathon memory guard: for large exports, stream straight to disk via File System Access.
    // resolveSaveStream (and its save picker) must be reached with no await before it — see its
    // comment for the user-activation invariant.
    const fmt = lastExportFormat;
    const matchInputs = buildMatchExportInputs();
    // Clip-range trim works the same for solo and match (the clip handles live on the shared scrub bar).
    const trimmed = clipIsTrimmed();
    const durS = (trimmed ? clipDurationMs() : session.timeMapper.presentationDurationMs) / 1000;
    const estBytes = fmt !== null
      ? presetsMod.estimateExportBytes(preset, fmt.audioCodec !== null ? preset.audioBitrate : 0, durS)
      : 0;
    // A match clip spans many players, so name it after the map alone. Solo keeps the full
    // "{username} on {title} [{version}] +{mods} {acc}%" label.
    const mapLabel = `${session.core.beatmap.title} [${session.core.beatmap.version}]`;
    const baseName = matchInputs !== null
      ? sanitizeFilename(`${mapLabel} (match)`)
      : sanitizeFilename(session.label);
    const { saveStream, saveHandle } = await resolveSaveStream(fmt, estBytes, baseName);

    const exportCallbacks = buildExportCallbacks();

    // Player names + score strip are burned into match frames with the Exo web font; make sure it's
    // loaded so the OffscreenCanvas text doesn't fall back to a system font mid-export.
    if (matchInputs !== null) await document.fonts.ready;

    // The format was negotiated for this preset when the dialog enabled Export (lastExportFormat);
    // pass it through so the encode uses exactly what the readout promised (no re-probe).
    const range = trimmed ? { startPresMs: clipStartPresMs, endPresMs: clipEndPresMs } : undefined;
    const { blob, extension } = matchInputs !== null
      ? await exportMod.exportMatchSession(matchInputs, preset, range, exportCallbacks, saveStream, fmt ?? undefined)
      : await exportMod.exportSession(
          {
            renderer: session.renderer,
            timeMapper: session.timeMapper,
            presentationDurationMs: session.timeMapper.presentationDurationMs,
            audio: session.audioSync.getMixdownInputs(),
            video: session.core.assets.video,
          },
          preset,
          range,
          exportCallbacks,
          saveStream,
          fmt ?? undefined,
        );

    // e.g. "bog on Pre-Parade (TGV Size) [KG's Taiko Oni].mp4" (solo) or "Pre-Parade … (match).mp4".
    // The extension follows the negotiated container (.mp4 normally, .webm on Firefox/VP9). When
    // streamed, the user named the file in the save picker (saveHandle.name).
    const filename = saveHandle?.name ?? `${baseName}.${extension}`;
    let savedSize: number;
    if (blob !== null) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      savedSize = blob.size;
    } else {
      // Streamed to disk — already saved; read back the final size for the message (best-effort:
      // a getFile() hiccup must not flip a successful save into a failure message).
      try { savedSize = saveHandle ? (await saveHandle.getFile()).size : 0; }
      catch { savedSize = 0; }
    }

    exportProgressWrap.classList.add('hidden');
    exportMessage.className = 'export-message success';
    exportMessage.textContent = savedSize > 0
      ? `Saved ${filename} — ${presetsMod.formatBytes(savedSize)}`
      : `Saved ${filename}`;
    exportMessage.classList.remove('hidden');
  } catch (err) {
    exportProgressWrap.classList.add('hidden');
    const cancelled = err instanceof exportMod.ExportCancelledError;
    exportMessage.className = cancelled ? 'export-message' : 'export-message error';
    exportMessage.textContent = cancelled
      ? 'Export cancelled.'
      : `Export failed: ${err instanceof Error ? err.message : String(err)}`;
    exportMessage.classList.remove('hidden');
  } finally {
    exporting = false;
    forEachActiveRenderer(r => r.start()); // resume live rendering behind the (still-open) dialog
    exportConfig.classList.remove('hidden');
    exportStart.classList.remove('hidden');
    exportCancel.classList.add('hidden');
    exportClose.disabled = false;
    void refreshExportDetails();
  }
}

exportBtn.addEventListener('click', () => { void openExportDialog(); });
exportClose.addEventListener('click', closeExportDialog);
exportStart.addEventListener('click', () => { void runExport(); });
exportCancel.addEventListener('click', () => { exportCancelRequested = true; exportCancel.disabled = true; });
exportPreset.addEventListener('change', () => { void refreshExportDetails(); });
// ESC fires the dialog's `cancel` event; block it mid-export (use the Cancel button), else close cleanly.
exportDialog.addEventListener('cancel', (e) => {
  e.preventDefault();
  closeExportDialog();
});

(window as unknown as { exportReplay: () => void }).exportReplay = () => { void openExportDialog(); };

// Dev check that both export paths draw the same pixels as the live renderer (storyboard
// included): an in-thread clone (`cloneForExport`) and a worker-style rebuild
// (`buildForExport` over the bundle round-tripped through `structuredClone`, exactly what
// postMessage does) are rendered at the same map times as the live renderer, at its backing
// quality, and compared byte for byte. DevTools: `await __rvExportParity([5000, 20000])`
// (default: five times spread over the map). Logs one row per time; 0 differing bytes = parity.
(window as unknown as { __rvExportParity: (mapTimesMs?: number[]) => Promise<void> }).__rvExportParity =
  async (mapTimesMs) => {
    const session = activeReplaySession();
    if (session === null) { console.log('no active replay session'); return; }
    const live = session.renderer;
    const quality = session.canvas.width / 1280;
    const bundle = live.exportBundle();
    const cloned = structuredClone({
      ...bundle,
      skin: { ...bundle.skin, sounds: new Map() },
      options: { ...bundle.options, hudOverlay: undefined },
    });
    // With a video: each export renderer decodes it through its own WebCodecs source, exactly as
    // the export does; times must then be ascending (the sources only move forward).
    const { exp } = await loadExportModules();
    const videoFile = live.hasVideo && live.options.showVideo ? session.core.assets.video : null;
    const sources = videoFile === null ? [] : [
      await exp.createWebCodecsVideoSource(videoFile.bytes),
      await exp.createWebCodecsVideoSource(videoFile.bytes),
    ];
    const attach = (i: number): { path: string; source: VideoFrameSource } | null =>
      sources[i] == null ? null : { path: videoFile!.path, source: sources[i]! };
    if (cloned.storyboard !== null) cloned.storyboard.video = attach(1);
    const inThread = live.cloneForExport(new OffscreenCanvas(1, 1), quality, attach(0));
    const rebuilt = Renderer.buildForExport(new OffscreenCanvas(1, 1), quality, cloned);
    rebuilt.options.hudOverlay = live.options.hudOverlay;
    const pixels = async (r: Renderer, canvas: HTMLCanvasElement | OffscreenCanvas, t: number): Promise<Uint8ClampedArray> => {
      await r.prepareFrameAt(t);
      r.renderFrameAt(t);
      const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
      return ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    };
    const canvasOf = (r: Renderer): OffscreenCanvas => (r as unknown as { canvas: OffscreenCanvas }).canvas;
    const diff = (a: Uint8ClampedArray, b: Uint8ClampedArray): { bytes: number; max: number; mean: number } => {
      if (a.length !== b.length) return { bytes: NaN, max: NaN, mean: NaN }; // backing stores differ in size
      let bytes = 0, max = 0, sum = 0;
      for (let i = 0; i < a.length; i++) {
        const d = Math.abs(a[i]! - b[i]!);
        if (d !== 0) { bytes++; sum += d; if (d > max) max = d; }
      }
      return { bytes, max, mean: sum / a.length };
    };
    const fmtDiff = (d: { bytes: number; max: number; mean: number }): string =>
      `${d.bytes} bytes (max Δ${d.max}${videoFile === null ? '' : `, mean Δ${d.mean.toFixed(3)}`})`;
    const total = session.timeMapper.toMapTime(session.timeMapper.presentationDurationMs);
    const times = mapTimesMs ?? [1, 2, 3, 4, 5].map(i => Math.round(total * i / 6));
    const rows: Record<string, unknown>[] = [];
    for (const t of times) {
      let a = await pixels(live, session.canvas, t);
      // The live <video> seeks asynchronously: give it time to land on the frame, then redraw.
      // Its decoder and WebCodecs may still differ by a little colour rounding (compare mean Δ
      // against a neighbouring frame's if in doubt); a whole different frame is a real mismatch.
      if (videoFile !== null) {
        await new Promise(r => setTimeout(r, 500));
        a = await pixels(live, session.canvas, t);
      }
      const b = await pixels(inThread, canvasOf(inThread), t);
      const c = await pixels(rebuilt, canvasOf(rebuilt), t);
      const ab = diff(a, b), ac = diff(a, c), bc = diff(b, c);
      rows.push({
        mapMs: t, storyboard: live.hasStoryboard && live.options.showStoryboard, video: videoFile !== null,
        'live vs clone': fmtDiff(ab),
        'live vs worker': fmtDiff(ac),
        'clone vs worker': fmtDiff(bc),
      });
    }
    console.table(rows);
    inThread.destroy();
    rebuilt.destroy();
    for (const s of sources) s?.dispose();
  };

// Wire both canvases so touch users can tap either side; the button is too small for touch.
function toggleActivePlayer(): void {
  if (activePlayer === null) return;
  if (activePlayer.isPlaying) doPause();
  else doPlay();
}
canvas.addEventListener('click',  toggleActivePlayer);
canvas2.addEventListener('click', toggleActivePlayer);
for (let i = 2; i < NUM_SLOTS; i++) slotCanvases[i]!.addEventListener('click', toggleActivePlayer);

const canvasColumn = document.querySelector('.canvas-column') as HTMLElement;

// ResizeObserver caps tabs-panel max-height to canvas column so the panel scrolls instead of growing the page.
{
  const tabsPanel = document.querySelector('.tabs-panel') as HTMLElement;
  const syncCanvasHeight = () => {
    const h = canvasColumn.offsetHeight;
    if (h > 0) tabsPanel.style.setProperty('--tabs-max-h', `${h}px`);
    else       tabsPanel.style.removeProperty('--tabs-max-h');
  };
  new ResizeObserver(syncCanvasHeight).observe(canvasColumn);
  syncCanvasHeight();
}

// Viewport width below which the 3-column desktop grid collapses to a stacked
// mobile layout. Above it, effectiveZoom() shrinks the desktop grid to fit.
// Must match the `@media (max-width: …)` collapse breakpoint in style.css
// (which uses this value − 1px).
const STACK_BREAKPOINT_PX = 1280;

// On-screen scale applied to the 1280×720 CSS box. Single source of truth shared
// with the Renderer's backing-store sizing (passed as createReplaySession's pageZoom)
// so the backing store matches what the browser actually paints. Match-view, embed
// and fullscreen — native or the pseudo-fullscreen fallback — clear the zoom
// (canvases are CSS-fit instead).
function effectiveZoom(): number {
  if (document.body.classList.contains('match-view') ||
      document.body.classList.contains('pseudo-fullscreen') ||
      document.body.classList.contains('embed') ||
      document.fullscreenElement !== null) return 1;
  const w = window.innerWidth;
  // Below the 3-column breakpoint the layout stacks (mobile CSS sizes itself) —
  // don't shrink it further. At/above it, scale the fixed-width desktop layout to
  // fit: down to ~0.67 at 1280px (the same effect as a manual browser zoom-out,
  // and exactly fitting the 1872px-wide grid), up to 1.5× on wide monitors.
  if (w < STACK_BREAKPOINT_PX) return 1;
  return Math.min(1.5, w / 1920);
}

// Wide-viewport upscale to 1.5× (1280×720 backing store gets soft past that). JS not CSS:
// Firefox's `zoom` parser rejects calc()/clamp()/viewport units inside the value.
{
  const containerEl = document.querySelector('.container') as HTMLElement;
  const updateZoom = () => {
    if (document.body.classList.contains('match-view')) {
      containerEl.style.zoom = '';
      return;
    }
    // Shared with Renderer's backing-store sizing so the buffer matches the paint.
    containerEl.style.zoom = String(effectiveZoom());
  };
  window.addEventListener('resize', updateZoom);
  // Container zoom multiplies into the fullscreened canvas column (top layer doesn't
  // reset ancestor zoom), so clear it while fullscreen — effectiveZoom() returns 1 then.
  document.addEventListener('fullscreenchange', updateZoom);
  // Observe body classes (match-view, pseudo-fullscreen) so their togglers don't need to call updateZoom.
  new MutationObserver(updateZoom).observe(document.body, {
    attributes: true, attributeFilter: ['class'],
  });
  updateZoom();
}

// Fullscreen (solo replays only; hidden in match view via CSS): fullscreen the whole
// canvas column so the options bar + scrub controls stay usable; CSS letterboxes the
// canvas into the leftover space. Where element fullscreen is unavailable (iPhone
// Safari exposes it for <video> only) or denied (sandboxed iframe), fall back to
// body.pseudo-fullscreen: a fixed-position overlay filling the viewport — browser
// chrome stays visible, which is the best any non-video element can do there.
const setPseudoFullscreen = (on: boolean) => {
  document.body.classList.toggle('pseudo-fullscreen', on);
};
fullscreenBtn.addEventListener('click', () => {
  if (document.fullscreenElement !== null) {
    void document.exitFullscreen();
  } else if (document.body.classList.contains('pseudo-fullscreen')) {
    setPseudoFullscreen(false);
  } else if (document.fullscreenEnabled && typeof canvasColumn.requestFullscreen === 'function') {
    canvasColumn.requestFullscreen().then(() => {
      // 16:9 canvas on a portrait phone: rotate to landscape while fullscreen. Only
      // permitted inside fullscreen and auto-released on exit; rejects wherever
      // unsupported (desktop, iOS — hence the cast: TS lib.dom omits lock()).
      (screen.orientation as { lock?: (o: string) => Promise<void> }).lock?.('landscape').catch(() => {});
    }, () => setPseudoFullscreen(true));
  } else {
    setPseudoFullscreen(true);
  }
});

const VOLUME_WHEEL_STEP = 5;

canvasColumn.addEventListener('wheel', (e) => {
  if (activeSession === null) return;
  // Don't hijack the wheel when it's aimed at a scrollable overlay inside
  // the canvas column (match overview maps list). Otherwise the volume
  // handler's preventDefault kills native scroll on that panel.
  if (e.target instanceof Element && e.target.closest('.match-overview') !== null) return;
  e.preventDefault();
  const dir = e.deltaY > 0 ? -1 : 1;
  const delta = dir * VOLUME_WHEEL_STEP;
  const nextMusic   = Math.max(0, Math.min(100, Number(optMusicVol.value)   + delta));
  const nextEffects = Math.max(0, Math.min(100, Number(optEffectsVol.value) + delta));
  if (String(nextMusic) !== optMusicVol.value) {
    optMusicVol.value = String(nextMusic);
    optMusicVol.dispatchEvent(new Event('input'));
  }
  if (String(nextEffects) !== optEffectsVol.value) {
    optEffectsVol.value = String(nextEffects);
    optEffectsVol.dispatchEvent(new Event('input'));
  }
}, { passive: false });

// ─── Deep-link (#score=…, #match=…[&map=…]) ─────────────────────────────────

/** Sync with src/index.html's <select> and scripts/extract-skins.mjs. */
const DEFAULT_SKINS: ReadonlyArray<string> = ['YUGEN', 'Default', 'Rafis', 'bog', 'Kamui', 'UNTITLED', 'shinchikuskin', '4sbet1', 'R Skin V2.0', 'bojii', 'myuka arrows', 'Skin for CTB'];
function isDefaultSkin(name: string): boolean {
  return DEFAULT_SKINS.includes(name);
}

interface Deeplink {
  kind: 'score' | 'match' | 'auto';
  id:   string;
  mapId?: number;
  /** Auto only — mod acronyms (every approved one is exactly 2 chars). */
  mods?: string[];
  /** Presentation-time seek offset in ms. */
  tMs?: number;
  /** One of DEFAULT_SKINS; non-defaults are filtered out at parse time. */
  skin?: string;
}

/** Chunk an #auto= `mods=` value ("HDDT") into known 2-char acronyms (every approved one is 2 chars). */
function parseAutoModsParam(raw: string | null): string[] {
  if (raw === null) return [];
  const up = raw.toUpperCase();
  const out: string[] = [];
  for (let i = 0; i + 2 <= up.length; i += 2) {
    const a = up.slice(i, i + 2);
    if (KNOWN_AUTO_ACRONYMS.has(a) && !out.includes(a)) out.push(a);
  }
  return out;
}

/** Validate an embed-supplied mods array: keep known acronyms (uppercased), deduped, drop the rest. */
function sanitizeAutoAcronyms(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const out: string[] = [];
  for (const v of input) {
    if (typeof v !== 'string') continue;
    const a = v.toUpperCase();
    if (KNOWN_AUTO_ACRONYMS.has(a) && !out.includes(a)) out.push(a);
  }
  return out;
}

function parseDeeplink(hash: string): Deeplink | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (raw.length === 0) return null;
  const params = new URLSearchParams(raw);

  const rawT  = params.get('t');
  const tMs   = rawT !== null ? Number.parseInt(rawT, 10) : NaN;
  const tOpt  = Number.isFinite(tMs) && tMs >= 0 ? { tMs } : {};

  const rawSkin = params.get('skin');
  const skinOpt = rawSkin !== null && isDefaultSkin(rawSkin) ? { skin: rawSkin } : {};

  const score = params.get('score');
  if (score !== null && score.length > 0) {
    return { kind: 'score', id: score, ...tOpt, ...skinOpt };
  }
  const auto = params.get('auto');
  if (auto !== null && auto.length > 0) {
    return { kind: 'auto', id: auto, mods: parseAutoModsParam(params.get('mods')), ...tOpt, ...skinOpt };
  }
  const match = params.get('match');
  if (match !== null && match.length > 0) {
    const rawMap = params.get('map');
    const mapId  = rawMap !== null ? Number.parseInt(rawMap, 10) : NaN;
    const mapOpt = Number.isFinite(mapId) ? { mapId } : {};
    return { kind: 'match', id: match, ...mapOpt, ...tOpt, ...skinOpt };
  }
  return null;
}

async function consumeDeeplink(link: Deeplink, opts: { autoplay?: boolean } = {}): Promise<void> {
  // Suppress autoplay on boot dispatches to avoid suspended-ctx zombie playback; hashchange callers pass true.
  const autoplay = opts.autoplay ?? false;

  // replaceState doesn't fire hashchange; share buttons reconstruct from session state, not location.hash.
  if (location.hash) window.history.replaceState(null, '', '/');

  if (link.skin !== undefined) {
    const sel = link.kind === 'score' ? fetchDefaultSkinSelect
              : link.kind === 'auto'  ? autoDefaultSkinSelect
              :                         matchDefaultSkinSelect;
    sel.value = link.skin;
    refreshSkinDropdown(sel);
  }

  if (link.kind === 'score') {
    activateTab('online');
    scoreInput.value = link.id;
    await loadScoreById(link.id, { autoplay });
  } else if (link.kind === 'auto') {
    activateTab('auto');
    autoInput.value = link.id;
    const acronyms = link.mods ?? [];
    applyAutoModsToSelector(acronyms);            // reflect the shared mods in the UI checkboxes
    const ref = parseBeatmapRef(link.id);
    // loadAutoSession doesn't self-handle errors (the button/embed callers do); wrap like the button.
    if (ref !== null) {
      setStatus('');
      setLoadProgress(true);
      try {
        await loadAutoSession(ref, acronyms, autoDefaultSkinSelect.value, { autoplay });
      } catch (err) {
        setStatus(loadErrorMessage(err), true);
        console.error(err);
      } finally {
        setLoadProgress(false);
      }
    }
  } else {
    activateTab('match');
    roomInput.value = link.id;
    await loadMatch(link.id, link.mapId, { autoplay });
  }

  if (link.tMs !== undefined && activePlayer !== null) {
    await doSeek(link.tMs);
  }
}

// ─── Embed mode (?embed=1): postMessage-driven load for third-party iframes ──
// e.g. osuplus frames /?embed=1 and postMessages a replay (raw .osr bytes) or an autoplay
// request (a beatmap link/ID + optional mods) in. No login: loadAndAddSession / loadAutoSession
// resolve the beatmap from public mirrors. Both protocols are documented in
// docs/ui-and-session.md → "Embed mode + postMessage".
if (new URLSearchParams(location.search).has('embed')) {
  document.body.classList.add('embed');

  const handleEmbedMessage = async (e: MessageEvent): Promise<void> => {
    const msg = e.data;
    if (msg === null || typeof msg !== 'object') return;
    if (msg.type !== 'replayviewer:load' && msg.type !== 'replayviewer:loadAuto') return;

    const reply = (payload: object): void => {
      (e.source as Window | null)?.postMessage(payload, e.origin);
    };

    try {
      const skin = typeof msg.skin === 'string' && isDefaultSkin(msg.skin) ? msg.skin : 'YUGEN';

      if (msg.type === 'replayviewer:loadAuto') {
        // Autoplay: synthesize a perfect replay for a beatmap (link/ID — no .osr) + optional mods.
        if (typeof msg.beatmap !== 'string') throw new Error('beatmap must be a beatmap link or ID string');
        const ref = parseBeatmapRef(msg.beatmap);
        if (ref === null) throw new Error('unrecognized beatmap link or ID');
        await loadAutoSession(ref, sanitizeAutoAcronyms(msg.mods), skin);
      } else {
        if (!(msg.osr instanceof ArrayBuffer)) throw new Error('osr must be an ArrayBuffer');
        const osz = msg.osz instanceof ArrayBuffer ? msg.osz : undefined;
        await loadAndAddSession(msg.osr, osz, skin);
      }

      // Replace semantics: keep only the just-activated session.
      for (const s of sessions.slice()) if (s !== activeSession) removeSession(s);

      reply({ type: 'replayviewer:loaded' });
    } catch (err) {
      reply({ type: 'replayviewer:error', message: err instanceof Error ? err.message : String(err) });
    }
  };

  window.addEventListener('message', (e) => { void handleEmbedMessage(e); });

  // Announce readiness so the embedder knows it can postMessage.
  if (window.parent !== window) window.parent.postMessage({ type: 'replayviewer:ready' }, '*');
}

// ─── Init: finish pending callback, render auth UI, dispatch deep link ────

(async () => {
  // Warm the OAuth config cache; non-fatal — startLogin / token exchange / refresh
  // each call loadConfig() themselves and surface their own error on failure, so a
  // flaky fetch here must not brick a session that already holds a valid token.
  void loadOAuthConfig().catch(() => {});

  try {
    const didExchange = await handlePendingCallback();
    if (didExchange) {
      // Restore pre-login deep-link; always clear the stash to prevent OAuth loop on failed consume.
      const pending = sessionStorage.getItem('deeplink_pending');
      sessionStorage.removeItem('deeplink_pending');
      window.history.replaceState(null, '', '/' + (pending ? '#' + pending : ''));
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    setStatus(msg, true);
  }

  await refreshAuthUI();

  // Hashchange dispatches allow autoplay (user already interacted). Dedup makes re-dispatch cheap.
  async function dispatchDeeplink(autoplay: boolean): Promise<void> {
    const link = parseDeeplink(location.hash);
    if (link === null) return;
    // Deep links bypass the tab UI; reject match links on mobile so they can't be opened by URL.
    if (link.kind === 'match' && isMatchUnsupported()) {
      if (location.hash) window.history.replaceState(null, '', '/');
      setStatus('Match replay viewing isn’t supported on mobile yet — open this link on desktop.', true);
      return;
    }
    // Score/match need OAuth; Auto is fully login-free (canonical /osu + mirror cascade), so it skips the gate.
    if (link.kind !== 'auto' && !isLoggedIn()) {
      await startLogin();
      return;
    }
    await consumeDeeplink(link, { autoplay });
  }
  window.addEventListener('hashchange', () => { void dispatchDeeplink(true); });
  await dispatchDeeplink(false);
})();
